import { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import MailboxesPanel from '../components/settings/MailboxesPanel';
import SignatureSettings from '../components/settings/SignatureSettings';
import { settingsApi, rolePermissionsApi, usersApi, authApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProductivity } from '../contexts/ProductivityContext';
import { usePageVisitLogger } from '../hooks/useActivityLogger';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { RoleBadge } from '../components/common/Badges';
import DesktopAlertsSettings from '../components/notify/DesktopAlertsSettings';
import {
  Loader2,
  Save,
  Key,
  Mail,
  Bell,
  Shield,
  Users,
  User,
  Eye,
  Phone,
  FileText,
  AlertTriangle,
  ChevronRight,
  Settings,
  Database,
  CheckCircle,
  ExternalLink,
  RefreshCw,
  Trash,
  Calendar,
  BarChart3,
  BarChart2,
  MousePointerClick,
  HardDrive,
  Clock,
  Download,
  Copy,
  Plus,
  X,
  UserCheck,
  Smartphone,
  MessageSquare,
  Zap,
  TrendingUp,
  Building2,
  Trophy,
  Briefcase,
  GraduationCap,
  Folder,
  Edit,
  Trash2,
  LogIn,
  KeyRound,
  Search,
  Globe,
  Sparkles,
  Play,
  XCircle,
  Lock,
  DollarSign,
  Target, PenLine } from 'lucide-react';
import { toast } from 'sonner';

const LLM_MODELS = {
  anthropic: [
    { value: 'claude-fable-5', label: 'Claude Fable 5 (Latest)' },
    { value: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (Legacy)' },
  ],
  openai: [
    { value: 'gpt-5.5', label: 'GPT-5.5 (Latest)' },
    { value: 'gpt-5.5-pro', label: 'GPT-5.5 Pro' },
    { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini' },
    { value: 'gpt-4o', label: 'GPT-4o (Legacy)' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Legacy)' },
  ],
  gemini: [
    { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash (Latest)' },
    { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro' },
    { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (Preview)' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (Legacy)' },
  ],
};

// Grouped settings sections for better navigation
const SETTINGS_CATEGORIES = [
  {
    id: 'ai_automation',
    label: 'AI & Automation',
    icon: Sparkles,
    sections: [
      { id: 'ai', label: 'AI & LLM', icon: Key, description: 'Configure AI providers and models' },
      { id: 'ai_parsing', label: 'AI Parsing', icon: Zap, description: 'AI-powered CV & Company parsing' },
      { id: 'ai_job_search', label: 'AI Job Search', icon: Search, description: 'External job search engine' },
      { id: 'engagement', label: 'Engagement', icon: Target, description: 'Unlock system, spoof emails, inactive alerts' },
      { id: 'scheduled_tasks', label: 'Scheduled Tasks', icon: Clock, description: 'Automated background jobs' },
      { id: 'api_usage', label: 'API Usage', icon: BarChart3, description: 'Token consumption tracking' },
    ]
  },
  {
    id: 'communications',
    label: 'Communications',
    icon: Mail,
    sections: [
      { id: 'email', label: 'Email', icon: Mail, description: 'Email provider settings' },
          { id: 'mailboxes', label: 'Mailboxes', icon: Mail, description: 'care@, hr@ and smita@ - sending and receiving' },
          { id: 'signature', label: 'Email signature', icon: PenLine, description: 'The sign-off added to every mail you send' },
      { id: 'email_templates', label: 'Email Templates', icon: Mail, description: 'Customize email content' },
      { id: 'notification_center', label: 'Notification Center', icon: Bell, description: 'Email & WhatsApp templates' },
      { id: 'hr_retargeting', label: 'HR Retargeting', icon: RefreshCw, description: 'Automated HR follow-ups' },
      { id: 'deal_reminders', label: 'Follow-up Reminders', icon: Bell, description: 'Proposal deal follow-up settings' },
      { id: 'profile_signature', label: 'Profile & Signature', icon: User, description: 'Your email signature details' },
      { id: 'recruiting_discipline', label: 'Recruiting Discipline', icon: Target, description: 'SLA and pipeline rules' },
    ]
  },
  {
    id: 'integrations_webhooks',
    label: 'Integrations',
    icon: ExternalLink,
    sections: [
      { id: 'webhook', label: 'Webhook & ATS', icon: Zap, description: 'External ATS integration' },
      { id: 'integrations', label: 'Calendar', icon: Calendar, description: 'Google Calendar integration' },
      { id: 'verification', label: 'Verification', icon: UserCheck, description: 'Background verification' },
    ]
  },
  {
    id: 'users_access',
    label: 'Users & Access',
    icon: Users,
    sections: [
      { id: 'system_users', label: 'System Users', icon: Users, description: 'Manage user accounts' },
      { id: 'permissions', label: 'Permissions', icon: Shield, description: 'Role-based access control' },
      { id: 'authentication', label: 'Authentication', icon: Key, description: 'OTP & login settings' },
    ]
  },
  {
    id: 'branding_seo',
    label: 'Branding & SEO',
    icon: Globe,
    sections: [
      { id: 'page_settings', label: 'Page Settings', icon: Globe, description: 'SEO, titles & branding' },
      { id: 'page_analytics', label: 'Page Analytics', icon: BarChart2, description: 'Page views & visitors' },
    ]
  },
  {
    id: 'system_tools',
    label: 'System',
    icon: Settings,
    sections: [
      { id: 'jm_import', label: 'Import from JM', icon: Download, description: 'One-time data migration' },
      { id: 'logs', label: 'System Logs', icon: FileText, description: 'Error logs & notifications' },
      { id: 'system', label: 'Cache & Storage', icon: HardDrive, description: 'Performance & maintenance' },
      { id: 'productivity', label: 'Productivity', icon: BarChart3, description: 'Idle tracking & reports' },
      { id: 'documentation', label: 'Documentation', icon: FileText, description: 'User guide' },
      { id: 'danger', label: 'Danger Zone', icon: AlertTriangle, description: 'Reset and delete data' },
    ]
  },
];

// Flat list for mobile dropdown
const SETTINGS_SECTIONS = SETTINGS_CATEGORIES.flatMap(cat => cat.sections);

const PERMISSION_LABELS = {
  view_candidate_phone: { label: 'View Phone', icon: Phone, category: 'Candidate Access' },
  view_candidate_email: { label: 'View Email', icon: Mail, category: 'Candidate Access' },
  view_candidate_resume: { label: 'View Resume', icon: FileText, category: 'Candidate Access' },
  upload_candidates: { label: 'Upload Candidates', icon: Users, category: 'Actions' },
  delete_candidates: { label: 'Delete Candidates', icon: AlertTriangle, category: 'Actions' },
  edit_candidates: { label: 'Edit Candidates', icon: FileText, category: 'Actions' },
  send_emails: { label: 'Send Emails', icon: Mail, category: 'Actions' },
  manage_users: { label: 'Manage Users', icon: Users, category: 'Admin' },
  view_activity_logs: { label: 'View Logs', icon: Eye, category: 'Admin' },
  manage_settings: { label: 'Manage Settings', icon: Settings, category: 'Admin' },
  require_click_to_reveal: { label: 'Click to Reveal', icon: Eye, category: 'Security' },
  require_access_notes: { label: 'Access Notes', icon: FileText, category: 'Security' },
};

const ROLES = [
  { id: 'super_admin', label: 'Super Admin', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { id: 'admin', label: 'Admin', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'hr_manager', label: 'HR Manager', color: 'bg-green-100 text-green-700 border-green-200' },
  { id: 'recruiter', label: 'Recruiter', color: 'bg-amber-100 text-amber-700 border-amber-200' },
];

export default function SettingsPage() {
  // Activity logging
  usePageVisitLogger('Settings');
  
  const { user: currentUser, login } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const routerLocation = useLocation();
  // "/" command palette deep-links: /ats/settings?section=<id>
  const [activeSection, setActiveSection] = useState(() => {
    try { return new URLSearchParams(routerLocation.search).get('section') || 'ai'; } catch (e) { return 'ai'; }
  });
  useEffect(() => {
    const s = new URLSearchParams(routerLocation.search).get('section');
    if (s) setActiveSection(s);
  }, [routerLocation.search]);
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetOptions, setResetOptions] = useState({
    reset_candidates: false,
    reset_jobs: false,
    reset_matches: false,
    reset_users: false,
    reset_activity: false,
  });
  const [rolePermissions, setRolePermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({ connected: false, email: null, has_credentials: false });
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  
  // System Users State
  const [systemUsers, setSystemUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'recruiter',
    phone: '',
    is_active: true,
  });
  const [impersonating, setImpersonating] = useState(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(null);
  const [resetMethod, setResetMethod] = useState('email');
  const [sendingReset, setSendingReset] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  
  // System Logs
  const [systemLogs, setSystemLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsSummary, setLogsSummary] = useState({ critical: 0, error: 0, warning: 0, unresolved: 0 });
  const [logSettings, setLogSettings] = useState({ enabled: false, email: '', interval_days: 3 });
  
  // Notification Status
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  
  // Documentation Pack State (Admin only)
  const [docsStatus, setDocsStatus] = useState(null);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsRegenerating, setDocsRegenerating] = useState(false);
  const [docsDownloading, setDocsDownloading] = useState(false);
  
  // Email Templates
  const [emailTemplates, setEmailTemplates] = useState({
    hr_share_subject: '🎯 New Candidate Match: {{candidate_name}} for {{job_title}} ({{match_score}}%)',
    hr_share_intro: 'We found a great candidate match for your open position!',
    interview_invite_subject: '🎉 Interview Invitation: {{job_title}} at {{company_name}}',
    interview_invite_intro: 'Great news! The hiring team would like to schedule an interview with you.',
    interview_confirm_subject: '✅ Interview Confirmed: {{job_title}}',
    interview_confirm_intro: 'Your interview has been scheduled successfully.',
  });
  const [savingTemplates, setSavingTemplates] = useState(false);

  useEffect(() => {
    loadSettings();
    loadRolePermissions();
    loadGoogleCalendarStatus();
    loadSystemLogs();
    loadLogSettings();
    loadNotificationStatus();
    loadSystemUsers();
    loadDocsStatus();
    
    // Check for Google OAuth callback params
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_success') === 'true') {
      toast.success('Google Calendar connected successfully!');
      loadGoogleCalendarStatus();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('google_error')) {
      toast.error(`Google Calendar error: ${params.get('google_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Documentation Pack Functions (Admin only)
  const loadDocsStatus = async () => {
    if (!isAdmin) {
      setDocsLoading(false);
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/docs/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocsStatus(data);
      }
    } catch (error) {
      console.error('Failed to load docs status:', error);
    } finally {
      setDocsLoading(false);
    }
  };

  const handleDocsRegenerate = async () => {
    setDocsRegenerating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/docs/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success('Documentation pack regenerated successfully!');
        setDocsStatus({
          has_docs: true,
          has_zip: true,
          zip_file: data.zip_file,
          generated_at: data.generated_at,
          git_commit: data.git_commit,
          counts: data.counts,
          files: data.files
        });
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to regenerate documentation');
      }
    } catch (error) {
      toast.error('Failed to regenerate documentation');
      console.error(error);
    } finally {
      setDocsRegenerating(false);
    }
  };

  const handleDocsDownload = async () => {
    setDocsDownloading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/docs/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const filename = docsStatus?.zip_file || 'docs-pack.zip';
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Documentation pack downloaded!');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to download documentation');
      }
    } catch (error) {
      toast.error('Failed to download documentation');
      console.error(error);
    } finally {
      setDocsDownloading(false);
    }
  };

  // System Users Functions
  const loadSystemUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await usersApi.getAll();
      setSystemUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleOpenCreateUser = () => {
    setEditingUser(null);
    setUserFormData({
      email: '',
      full_name: '',
      password: '',
      role: 'recruiter',
      phone: '',
      is_active: true,
    });
    setUserDialogOpen(true);
  };

  const handleOpenEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      email: user.email,
      full_name: user.full_name,
      password: '',
      role: user.role,
      phone: user.phone || '',
      is_active: user.is_active,
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        const updateData = { ...userFormData };
        if (!updateData.password) delete updateData.password;
        await usersApi.update(editingUser.id, updateData);
        toast.success('User updated');
      } else {
        await usersApi.create(userFormData);
        toast.success('User created');
      }
      setUserDialogOpen(false);
      loadSystemUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(userId);
      toast.success('User deleted');
      loadSystemUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleImpersonateUser = async (user) => {
    if (!isSuperAdmin) return;
    if (user.role === 'super_admin') {
      toast.error('Cannot impersonate another Super Admin');
      return;
    }
    if (!window.confirm(`Login as ${user.full_name}? This action is NOT logged.`)) return;
    
    setImpersonating(user.id);
    try {
      const response = await authApi.impersonate(user.id);
      const { access_token, user: userData } = response.data;
      login(userData, access_token);
      toast.success(`Now logged in as ${userData.full_name}`);
      navigate('/ats/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    } finally {
      setImpersonating(null);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!resetPasswordDialog) return;
    setSendingReset(true);
    try {
      const response = await authApi.sendPasswordReset(resetPasswordDialog.id, resetMethod);
      toast.success(response.data.message || `Password reset sent via ${resetMethod}`);
      setResetPasswordDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send password reset');
    } finally {
      setSendingReset(false);
    }
  };

  const filteredSystemUsers = systemUsers.filter(user =>
    user.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const loadNotificationStatus = async () => {
    try {
      const response = await settingsApi.getNotificationStatus();
      setNotificationStatus(response.data);
    } catch (error) {
      console.error('Failed to load notification status:', error);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    setTestingEmail(true);
    try {
      const response = await settingsApi.testEmail(testEmailAddress);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestWhatsApp = async () => {
    if (!testPhoneNumber) {
      toast.error('Please enter a phone number');
      return;
    }
    setTestingWhatsApp(true);
    try {
      const response = await settingsApi.testWhatsApp(testPhoneNumber);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error('Failed to send test WhatsApp');
    } finally {
      setTestingWhatsApp(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleCalendarStatus = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/google-calendar/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGoogleCalendarStatus(data);
      }
    } catch (error) {
      console.error('Failed to load Google Calendar status:', error);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    // First check if credentials are saved
    if (!settings?.google_calendar_client_id || !settings?.google_calendar_client_secret) {
      toast.error('Please enter and save your Google Client ID and Client Secret first');
      return;
    }
    
    setConnectingGoogle(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/google-calendar/auth-url`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to get authorization URL. Make sure credentials are saved.');
      }
    } catch (error) {
      toast.error('Failed to connect to Google Calendar');
    } finally {
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogleCalendar = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/google-calendar/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Google Calendar disconnected');
        setGoogleCalendarStatus({ connected: false, email: null, has_credentials: googleCalendarStatus.has_credentials });
      }
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  const loadSystemLogs = async () => {
    setLogsLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/system-logs?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSystemLogs(data.logs || []);
        setLogsSummary(data.summary || { critical: 0, error: 0, warning: 0, unresolved: 0 });
      }
    } catch (error) {
      console.error('Failed to load system logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadLogSettings = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/system-logs/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogSettings({
          enabled: data.log_notification_enabled || false,
          email: data.log_notification_email || '',
          interval_days: data.log_notification_interval_days || 3
        });
      }
    } catch (error) {
      console.error('Failed to load log settings:', error);
    }
  };

  const saveLogSettings = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      await fetch(`${API_URL}/api/system-logs/settings?enabled=${logSettings.enabled}&email=${encodeURIComponent(logSettings.email)}&interval_days=${logSettings.interval_days}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Log notification settings saved');
    } catch (error) {
      toast.error('Failed to save log settings');
    }
  };

  const downloadLogs = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/system-logs/download?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `system_logs_${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Logs downloaded');
      }
    } catch (error) {
      toast.error('Failed to download logs');
    }
  };

  const copyLogsToClipboard = async () => {
    try {
      const logsText = JSON.stringify(systemLogs, null, 2);
      await navigator.clipboard.writeText(logsText);
      toast.success('Logs copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy logs');
    }
  };

  const resolveLog = async (logId) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/system-logs/${logId}/resolve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Log marked as resolved');
        loadSystemLogs();
      }
    } catch (error) {
      toast.error('Failed to resolve log');
    }
  };

  const loadRolePermissions = async () => {
    try {
      const response = await rolePermissionsApi.getAll();
      setRolePermissions(response.data);
    } catch (error) {
      console.error('Failed to load role permissions:', error);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionChange = (role, permission, value) => {
    setRolePermissions(prev => ({
      ...prev,
      [role]: { ...prev[role], [permission]: value }
    }));
  };

  const handleSavePermissions = async (role) => {
    setSavingPermissions(true);
    try {
      await rolePermissionsApi.update(role, rolePermissions[role]);
      toast.success(`${role.replace('_', ' ')} permissions updated`);
    } catch (error) {
      toast.error('Failed to save permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleReset = async () => {
    if (resetConfirm !== 'RESET') {
      toast.error("Type 'RESET' to confirm");
      return;
    }
    try {
      await settingsApi.reset({ ...resetOptions, confirmation: 'RESET' });
      toast.success('Data reset successfully');
      setResetConfirm('');
      setResetOptions({
        reset_candidates: false,
        reset_jobs: false,
        reset_matches: false,
        reset_users: false,
        reset_activity: false,
      });
    } catch (error) {
      toast.error('Failed to reset data');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a recipient email address');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      const response = await fetch(`${API_URL}/api/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ recipient_email: testEmail })
      });
      
      // Clone the response before reading to avoid body lock issues
      const responseClone = response.clone();
      
      try {
        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || 'Test email sent successfully!');
        } else {
          toast.error(data.detail || 'Failed to send test email');
        }
      } catch (parseError) {
        // If JSON parsing fails, try to get text
        const text = await responseClone.text();
        if (response.ok) {
          toast.success('Test email sent successfully!');
        } else {
          toast.error(text || 'Failed to send test email');
        }
      }
    } catch (error) {
      console.error('Test email error:', error);
      toast.error('Failed to send test email. Check console for details.');
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  // AI Settings Section
  const AISection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-1">AI Provider Configuration</h3>
        <p className="text-sm text-slate-500">Configure your AI providers for CV parsing and job matching</p>
      </div>

      {/* Default Provider */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Default AI Provider</Label>
          <Select
            value={settings?.default_ai_provider || 'anthropic'}
            onValueChange={(value) => setSettings({ ...settings, default_ai_provider: value })}
          >
            <SelectTrigger data-testid="default-provider-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="anthropic">Anthropic Claude</SelectItem>
              <SelectItem value="openai">OpenAI GPT</SelectItem>
              <SelectItem value="gemini">Google Gemini</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Default Model</Label>
          <Select
            value={settings?.default_model || ''}
            onValueChange={(value) => setSettings({ ...settings, default_model: value })}
          >
            <SelectTrigger data-testid="default-model-select">
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent>
              {LLM_MODELS[settings?.default_ai_provider || 'anthropic']?.map((model) => (
                <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* API Keys */}
      <Accordion type="multiple" className="w-full" defaultValue={['anthropic']}>
        <AccordionItem value="anthropic" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                <span className="text-orange-600 font-bold text-sm">A</span>
              </div>
              <div className="text-left">
                <div className="font-medium">Anthropic</div>
                <div className="text-xs text-slate-500">Claude models</div>
              </div>
              {settings?.anthropic_api_key && (
                <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4 pb-2">
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={settings?.anthropic_api_key || ''}
                  onChange={(e) => setSettings({ ...settings, anthropic_api_key: e.target.value })}
                  placeholder="sk-ant-..."
                  data-testid="anthropic-key-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Select
                  value={settings?.anthropic_model || 'claude-fable-5'}
                  onValueChange={(value) => setSettings({ ...settings, anthropic_model: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LLM_MODELS.anthropic.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="openai" className="border rounded-lg px-4 mb-2">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <span className="text-emerald-600 font-bold text-sm">O</span>
              </div>
              <div className="text-left">
                <div className="font-medium">OpenAI</div>
                <div className="text-xs text-slate-500">GPT models</div>
              </div>
              {settings?.openai_api_key && (
                <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4 pb-2">
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={settings?.openai_api_key || ''}
                  onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                  placeholder="sk-..."
                  data-testid="openai-key-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Select
                  value={settings?.openai_model || 'gpt-5.5'}
                  onValueChange={(value) => setSettings({ ...settings, openai_model: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LLM_MODELS.openai.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="gemini" className="border rounded-lg px-4">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-bold text-sm">G</span>
              </div>
              <div className="text-left">
                <div className="font-medium">Google Gemini</div>
                <div className="text-xs text-slate-500">Gemini models</div>
              </div>
              {settings?.gemini_api_key && (
                <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-4 pb-2">
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={settings?.gemini_api_key || ''}
                  onChange={(e) => setSettings({ ...settings, gemini_api_key: e.target.value })}
                  placeholder="AI..."
                  data-testid="gemini-key-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Select
                  value={settings?.gemini_model || 'gemini-3.5-flash'}
                  onValueChange={(value) => setSettings({ ...settings, gemini_model: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LLM_MODELS.gemini.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save AI Settings
      </Button>
    </div>
  );


  // AI Parsing Section - Combined CV & Company Parsing

  const AIParsingSection = () => {
    const [status, setStatus] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [stopping, setStopping] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState('anthropic');
    const [includeExisting, setIncludeExisting] = useState(false);
    // Backfill state - integrated into AIParsingSection
    const [backfillStatus, setBackfillStatus] = useState(null);
    const [backfillRunning, setBackfillRunning] = useState(false);
    const [includeAllBackfill, setIncludeAllBackfill] = useState(false);

    const loadData = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const [statusRes, statsRes, backfillRes] = await Promise.all([
          fetch(`${API_URL}/api/ai-parsing/status`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/ai-parsing/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/ai-summary/backfill-status`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (statusRes.ok) setStatus(await statusRes.json());
        if (statsRes.ok) setStats(await statsRes.json());
        if (backfillRes.ok) {
          const backfillData = await backfillRes.json();
          setBackfillStatus(backfillData);
          setBackfillRunning(backfillData.is_running);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };

    const startBackfill = async () => {
      setBackfillRunning(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/ai-summary/backfill?include_existing=${includeAllBackfill}`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) toast.success('Summary generation started');
        else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
      } catch { toast.error('Failed to start'); }
    };

    const stopBackfill = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        await fetch(`${API_URL}/api/ai-summary/backfill-stop`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
        toast.success('Stopping...');
      } catch { toast.error('Failed'); }
    };

    useEffect(() => {
      loadData();
      const interval = setInterval(loadData, 2000);
      return () => clearInterval(interval);
    }, []);

    const startParsing = async () => {
      setStarting(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const res = await fetch(
          `${API_URL}/api/ai-parsing/start?ai_provider=${selectedProvider}&include_existing=${includeExisting}`,
          { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (res.ok) { toast.success('Parsing started'); loadData(); }
        else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
      } catch (e) { toast.error('Failed to start'); }
      finally { setStarting(false); }
    };

    const stopParsing = async () => {
      setStopping(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        await fetch(`${API_URL}/api/ai-parsing/stop`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
        toast.success('Stopping...'); loadData();
      } catch (e) { toast.error('Failed'); }
      finally { setStopping(false); }
    };

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

    const unparsed = stats?.cvs?.unparsed || 0;
    const dbList = [
      { key: 'companies', label: 'Companies', icon: Building2, color: 'blue' },
      { key: 'skills', label: 'Skills', icon: Zap, color: 'purple' },
      { key: 'institutions', label: 'Institutions', icon: Building2, color: 'green' },
      { key: 'designations', label: 'Job Titles', icon: Users, color: 'amber' },
      { key: 'certifications', label: 'Certifications', icon: Shield, color: 'indigo' },
      { key: 'awards', label: 'Awards', icon: Trophy, color: 'yellow' },
      { key: 'languages', label: 'Languages', icon: MessageSquare, color: 'pink' },
      { key: 'industries', label: 'Industries', icon: Briefcase, color: 'cyan' },
      { key: 'degrees', label: 'Degrees', icon: GraduationCap, color: 'emerald' },
      { key: 'projects', label: 'Projects', icon: Folder, color: 'orange' }
    ];

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">AI-Powered CV Parsing</h3>
          <p className="text-sm text-slate-500">One-click parsing extracts everything and populates all databases</p>
        </div>

        {/* Unparsed Alert */}
        {unparsed > 0 && !status?.is_running && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">{unparsed} new CVs need parsing</p>
              <p className="text-sm text-amber-600">Click Start to process them</p>
            </div>
          </div>
        )}

        {/* Live Status */}
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${status?.is_running ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
          <span className={status?.is_running ? 'text-green-600 font-medium' : 'text-slate-500'}>
            {status?.is_running ? `Parsing with ${status.ai_provider}...` : 'Ready'}
          </span>
        </div>

        {/* CV Stats */}
        {stats?.cvs && (
          <div className="grid grid-cols-4 gap-3">
            <Card className="p-3 text-center">
              <div className="text-2xl font-bold">{stats.cvs.total}</div>
              <div className="text-xs text-slate-500">Total CVs</div>
            </Card>
            <Card className="p-3 text-center bg-green-50 border-green-200">
              <div className="text-2xl font-bold text-green-600">{stats.cvs.parsed}</div>
              <div className="text-xs text-slate-500">Parsed</div>
            </Card>
            <Card className="p-3 text-center bg-amber-50 border-amber-200">
              <div className="text-2xl font-bold text-amber-600">{stats.cvs.unparsed}</div>
              <div className="text-xs text-slate-500">Unparsed</div>
            </Card>
            <Card className="p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.cvs.parsed_rate}%</div>
              <div className="text-xs text-slate-500">Complete</div>
            </Card>
          </div>
        )}

        {/* All Databases */}
        <Card className="p-4">
          <h4 className="font-medium text-slate-800 mb-3">Databases (Auto-populated from CVs)</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {dbList.map(db => (
              <div key={db.key} className={`p-3 bg-${db.color}-50 rounded-lg text-center border border-${db.color}-100`}>
                <db.icon className={`w-4 h-4 mx-auto text-${db.color}-600 mb-1`} />
                <div className="text-lg font-bold">{stats?.databases?.[db.key] || 0}</div>
                <div className="text-xs text-slate-500">{db.label}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            All databases are auto-populated when CVs are parsed. Access via /api/master/[database_name]
          </p>
        </Card>

        {/* Live Progress */}
        {status?.is_running && (
          <Card className="p-4 border-2 border-blue-200 bg-blue-50/50">
            <div className="flex items-center gap-2 mb-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="font-semibold text-blue-800">Parsing in Progress</span>
            </div>
            
            <div className="p-3 bg-white rounded-lg border mb-3">
              <div className="text-xs text-slate-500">Currently parsing:</div>
              <div className="font-medium truncate">{status.current_item || 'Starting...'}</div>
            </div>

            <div className="mb-3">
              <div className="flex justify-between text-sm mb-1">
                <span>Progress</span>
                <span className="font-bold text-blue-600">{status.percentage}%</span>
              </div>
              <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${status.percentage}%` }} />
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>{status.completed} done, {status.failed} failed</span>
                <span>{status.total - status.completed - status.failed} remaining</span>
              </div>
            </div>

            {/* Extracted counts */}
            {status.extracted && Object.keys(status.extracted).some(k => status.extracted[k] > 0) && (
              <div className="p-3 bg-green-100 rounded text-sm text-green-800">
                <span className="font-medium">Extracted this session: </span>
                {Object.entries(status.extracted).filter(([k,v]) => v > 0).map(([k,v]) => `${v} ${k}`).join(', ')}
              </div>
            )}
          </Card>
        )}

        {/* Completed */}
        {!status?.is_running && status?.completed_at && (
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Last run complete</span>
            </div>
            <p className="text-sm text-green-600 mt-1">
              {status.completed} parsed, {status.failed} failed
            </p>
          </Card>
        )}

        {/* Errors */}
        {status?.errors?.length > 0 && (
          <Card className="p-4 bg-red-50 border-red-200">
            <p className="text-sm font-medium text-red-700 mb-2">Errors:</p>
            <ul className="text-xs text-red-600 space-y-1 max-h-20 overflow-y-auto">
              {status.errors.slice(-5).map((e, i) => <li key={i}>• {e}</li>)}
            </ul>
          </Card>
        )}

        {/* Controls */}
        <Card className="p-4">
          <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-4">Start Parsing</h4>
          
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block text-sm">AI Engine</Label>
              <Select value={selectedProvider} onValueChange={setSelectedProvider} disabled={status?.is_running}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(stats?.providers || ['anthropic', 'openai', 'gemini']).map(p => (
                    <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Parsing Mode Selection */}
            <div className="space-y-3">
              <Label className="block text-sm">Parsing Mode</Label>
              
              {/* Option 1: Only unparsed (default) */}
              <label 
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  !includeExisting 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' 
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                }`}
                onClick={() => setIncludeExisting(false)}
              >
                <input 
                  type="radio" 
                  name="parseMode" 
                  checked={!includeExisting} 
                  onChange={() => setIncludeExisting(false)}
                  disabled={status?.is_running}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800 dark:text-slate-200">Parse new CVs only</span>
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300">
                      Recommended
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Only parse CVs that haven't been processed yet
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-amber-600 border-amber-300 dark:border-amber-700">
                      {stats?.cvs?.unparsed || 0} CVs to parse
                    </Badge>
                    <span className="text-xs text-slate-400">
                      (Skipping {stats?.cvs?.parsed || 0} already parsed)
                    </span>
                  </div>
                </div>
              </label>

              {/* Option 2: Include already parsed (re-parse all) */}
              <label 
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  includeExisting 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' 
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                }`}
                onClick={() => setIncludeExisting(true)}
              >
                <input 
                  type="radio" 
                  name="parseMode" 
                  checked={includeExisting} 
                  onChange={() => setIncludeExisting(true)}
                  disabled={status?.is_running}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800 dark:text-slate-200">Re-parse all CVs</span>
                    <Badge variant="outline" className="text-slate-500">
                      Full refresh
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Re-process all CVs to refresh extracted data (useful after algorithm updates)
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-blue-600 border-blue-300 dark:border-blue-700">
                      {stats?.cvs?.with_text || stats?.cvs?.total || 0} CVs total
                    </Badge>
                    <span className="text-xs text-amber-500">
                      ⚠️ This will use more API credits
                    </span>
                  </div>
                </div>
              </label>
            </div>

            <div className="flex gap-2">
              {!status?.is_running ? (
                <Button onClick={startParsing} disabled={starting} className="flex-1">
                  {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                  Start Parsing {includeExisting ? `(All ${stats?.cvs?.with_text || 0})` : `(${unparsed} new)`}
                </Button>
              ) : (
                <Button onClick={stopParsing} disabled={stopping} variant="destructive" className="flex-1">
                  {stopping ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                  Stop
                </Button>
              )}
              <Button variant="outline" onClick={loadData}><RefreshCw className="w-4 h-4" /></Button>
            </div>
          </div>
        </Card>

        {/* What Gets Extracted */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <h4 className="font-medium text-slate-800 mb-2">What gets extracted & saved?</h4>
          <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
            <div>
              <p className="font-medium text-slate-700">Personal Info</p>
              <p className="text-xs">Name, DOB, Gender, Nationality, Location, Contact</p>
            </div>
            <div>
              <p className="font-medium text-slate-700">Work Experience</p>
              <p className="text-xs">Companies, Job Titles, Duration, Achievements</p>
            </div>
            <div>
              <p className="font-medium text-slate-700">Education</p>
              <p className="text-xs">Institutions, Degrees, Fields, GPA</p>
            </div>
            <div>
              <p className="font-medium text-slate-700">Skills & Tech</p>
              <p className="text-xs">Technical, Soft, Tools, Languages, Frameworks</p>
            </div>
            <div>
              <p className="font-medium text-slate-700">Credentials</p>
              <p className="text-xs">Certifications, Awards, Publications, Patents</p>
            </div>
            <div>
              <p className="font-medium text-slate-700">Other</p>
              <p className="text-xs">Projects, Salary, Notice Period, References</p>
            </div>
          </div>
        </Card>

        {/* Backfill AI Summaries - Inline to avoid hooks issues */}
        <Card className="p-4 border-indigo-200 bg-indigo-50/30">
          <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-600" />
            Backfill AI Summaries & CTC Estimates
          </h4>
          <p className="text-sm text-slate-500 mb-3">Generate AI executive summaries and salary estimates for existing candidates</p>
          
          {backfillRunning && backfillStatus && (
            <div className="mb-3 p-3 bg-white rounded-lg border">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                <span className="text-sm font-medium">Processing: {backfillStatus.current_item || 'Starting...'}</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${backfillStatus.total > 0 ? ((backfillStatus.completed + backfillStatus.failed) / backfillStatus.total * 100) : 0}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-1">{backfillStatus.completed} done, {backfillStatus.failed} failed of {backfillStatus.total}</p>
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={includeAllBackfill} onChange={e => setIncludeAllBackfill(e.target.checked)} disabled={backfillRunning} />
              Include already generated
            </label>
            {!backfillRunning ? (
              <Button size="sm" onClick={startBackfill}>
                <Sparkles className="w-3.5 h-3.5 mr-1" />Generate Summaries
              </Button>
            ) : (
              <Button size="sm" variant="destructive" onClick={stopBackfill}>
                <X className="w-3.5 h-3.5 mr-1" />Stop
              </Button>
            )}
          </div>
        </Card>

        {/* Parsing Queue Management */}
        <ParsingQueueManager />
      </div>
    );
  };

  // Parsing Queue Manager Component
  const ParsingQueueManager = () => {
    const [queue, setQueue] = useState([]);
    const [counts, setCounts] = useState({ pending: 0, failed: 0, completed: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('failed');
    const [clearing, setClearing] = useState(false);

    const loadQueue = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/ai-parsing/queue?status=${filter}&limit=20`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setQueue(data.items || []);
          setCounts(data.counts || { pending: 0, failed: 0, completed: 0 });
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };

    useEffect(() => {
      loadQueue();
    }, [filter]);

    const removeFromQueue = async (candidateId) => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/ai-parsing/queue/${candidateId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          toast.success('Removed from queue');
          loadQueue();
        } else {
          toast.error('Failed to remove');
        }
      } catch { toast.error('Failed'); }
    };

    const retryParsing = async (candidateId) => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/ai-parsing/queue/${candidateId}/retry`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          toast.success('Queued for retry');
          loadQueue();
        } else {
          toast.error('Failed to retry');
        }
      } catch { toast.error('Failed'); }
    };

    const clearAllFailed = async () => {
      if (!window.confirm('Remove all failed items from the queue?')) return;
      setClearing(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/ai-parsing/queue/clear-failed`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          toast.success(data.message);
          loadQueue();
        }
      } catch { toast.error('Failed'); }
      finally { setClearing(false); }
    };

    return (
      <Card className="p-4 border-red-200 bg-red-50/30">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-slate-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            Parsing Queue Management
          </h4>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-amber-600">{counts.pending} pending</Badge>
            <Badge variant="outline" className="text-red-600">{counts.failed} failed</Badge>
            <Badge variant="outline" className="text-green-600">{counts.completed} done</Badge>
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-3">
          Remove failed items to save API credits, or retry individual items
        </p>

        <div className="flex items-center gap-3 mb-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="failed">Failed ({counts.failed})</SelectItem>
              <SelectItem value="pending">Pending ({counts.pending})</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          
          {counts.failed > 0 && (
            <Button 
              size="sm" 
              variant="destructive" 
              onClick={clearAllFailed}
              disabled={clearing}
            >
              {clearing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
              Clear All Failed
            </Button>
          )}
          
          <Button size="sm" variant="outline" onClick={loadQueue}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        {loading ? (
          <div className="py-4 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : queue.length > 0 ? (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {queue.map(item => (
              <div key={item.id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.name || 'Unknown'}</p>
                  <p className="text-xs text-slate-500 truncate">{item.email || item.original_file_name || item.id}</p>
                  {item.parsing_error && (
                    <p className="text-xs text-red-500 truncate mt-1">{item.parsing_error}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <Badge 
                    variant="outline" 
                    className={item.parsing_status === 'failed' ? 'text-red-600' : 'text-amber-600'}
                  >
                    {item.parsing_status || 'pending'}
                  </Badge>
                  {item.parsing_status === 'failed' && (
                    <Button size="sm" variant="ghost" onClick={() => retryParsing(item.id)}>
                      <Play className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeFromQueue(item.id)}>
                    <XCircle className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">
            No items in queue with status: {filter}
          </p>
        )}
      </Card>
    );
  };

  // EmailSection component
  const EmailSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-800">Email Configuration</h3>
        <p className="text-sm text-slate-500">Configure email provider settings for sending notifications</p>
      </div>

      <div className="space-y-2">
        <Label>Email Provider</Label>
        <Select
          value={settings?.email_provider || 'smtp'}
          onValueChange={(value) => setSettings({ ...settings, email_provider: value })}
        >
          <SelectTrigger data-testid="email-provider-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="smtp">SMTP (Gmail, Outlook, etc.)</SelectItem>
            <SelectItem value="sendgrid">SendGrid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {settings?.email_provider === 'smtp' && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" />
              SMTP Settings
            </CardTitle>
            <CardDescription>For Gmail, use App Password (not your regular password)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>SMTP Host</Label>
                <Input
                  value={settings?.smtp_host || ''}
                  onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label>SMTP Port</Label>
                <Input
                  type="number"
                  value={settings?.smtp_port || 587}
                  onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                  data-testid="smtp-port-input"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Username (Email)</Label>
                <Input
                  value={settings?.smtp_username || ''}
                  onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                  placeholder="your@gmail.com"
                  data-testid="smtp-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Password / App Password</Label>
                <Input
                  type="password"
                  value={settings?.smtp_password || ''}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  placeholder="App password (16 chars)"
                  data-testid="smtp-password-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sender Email (From)</Label>
              <Input
                value={settings?.smtp_sender_email || ''}
                onChange={(e) => setSettings({ ...settings, smtp_sender_email: e.target.value })}
                placeholder="noreply@yourcompany.com"
                data-testid="smtp-sender-input"
              />
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Gmail Users:</strong> Create an App Password at{' '}
                  <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">
                    Google App Passwords
                  </a>
                  {' '}(requires 2FA enabled)
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {settings?.email_provider === 'sendgrid' && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">SendGrid Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>SendGrid API Key</Label>
              <Input
                type="password"
                value={settings?.sendgrid_api_key || ''}
                onChange={(e) => setSettings({ ...settings, sendgrid_api_key: e.target.value })}
                placeholder="SG...."
                data-testid="sendgrid-key-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Sender Email</Label>
              <Input
                value={settings?.sendgrid_sender_email || ''}
                onChange={(e) => setSettings({ ...settings, sendgrid_sender_email: e.target.value })}
                placeholder="noreply@yourcompany.com"
                data-testid="sendgrid-sender-input"
              />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col md:flex-row gap-4">
        <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Email Settings
        </Button>
      </div>

      {/* Test Email Section */}
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-green-600" />
            Test Email Delivery
          </CardTitle>
          <CardDescription>Send a test email to verify your configuration is working</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter recipient email address"
                data-testid="test-email-input"
              />
            </div>
            <Button 
              onClick={handleSendTestEmail} 
              disabled={sendingTestEmail || !testEmail}
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-100"
              data-testid="send-test-email-btn"
            >
              {sendingTestEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Send Test Email
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            A test email will be sent to verify your SMTP/SendGrid configuration. Check your inbox (and spam folder) for the test message.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  // Reminder Schedule Settings Component
  const ReminderScheduleSettings = ({ settings, onSave }) => {
    const [reminderDays, setReminderDays] = useState({
      first: settings?.on_hold_reminder_schedule?.first || 3,
      second: settings?.on_hold_reminder_schedule?.second || 6,
      third: settings?.on_hold_reminder_schedule?.third || 12
    });
    const [reminderStats, setReminderStats] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loadingStats, setLoadingStats] = useState(true);

    useEffect(() => {
      loadReminderStats();
      if (settings?.on_hold_reminder_schedule) {
        setReminderDays(settings.on_hold_reminder_schedule);
      }
    }, [settings]);

    const loadReminderStats = async () => {
      try {
        const res = await settingsApi.getReminderEffectiveness();
        setReminderStats(res.data);
      } catch (err) {
        console.error('Failed to load reminder stats');
      } finally {
        setLoadingStats(false);
      }
    };

    const handleSave = async () => {
      setSaving(true);
      try {
        await settingsApi.update({ on_hold_reminder_schedule: reminderDays });
        toast.success('Reminder schedule saved');
        onSave && onSave();
      } catch (err) {
        toast.error('Failed to save reminder schedule');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">On-Hold Reminder Schedule</h3>
          <p className="text-sm text-slate-500">Configure when reminders are sent to HR for candidates on hold</p>
        </div>

        {/* Reminder Schedule Configuration */}
        <Card className="border border-slate-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              Reminder Schedule
            </CardTitle>
            <CardDescription>Set the number of days after "on hold" when each reminder is sent</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl font-bold text-blue-600">1</span>
                </div>
                <Label className="text-sm text-slate-600 block mb-2">First Reminder</Label>
                <Select value={String(reminderDays.first)} onValueChange={(v) => setReminderDays({ ...reminderDays, first: parseInt(v) })}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 7, 10, 14].map(d => (
                      <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl font-bold text-amber-600">2</span>
                </div>
                <Label className="text-sm text-slate-600 block mb-2">Second Reminder</Label>
                <Select value={String(reminderDays.second)} onValueChange={(v) => setReminderDays({ ...reminderDays, second: parseInt(v) })}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 5, 6, 7, 10, 14, 21].map(d => (
                      <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl font-bold text-red-600">3</span>
                </div>
                <Label className="text-sm text-slate-600 block mb-2">Third Reminder</Label>
                <Select value={String(reminderDays.third)} onValueChange={(v) => setReminderDays({ ...reminderDays, third: parseInt(v) })}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[7, 10, 12, 14, 21, 30].map(d => (
                      <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-600">
                <strong>Current Schedule:</strong> Reminders will be sent at <strong>{reminderDays.first}</strong>, <strong>{reminderDays.second}</strong>, and <strong>{reminderDays.third}</strong> days after a candidate is put on hold.
              </p>
              <p className="text-xs text-slate-400 mt-2">
                HR can opt out of reminders for specific candidates at any time.
              </p>
            </div>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Schedule
            </Button>
          </CardContent>
        </Card>

        {/* Reminder Effectiveness Stats */}
        <Card className="border border-slate-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              Reminder Effectiveness
            </CardTitle>
            <CardDescription>Track how effective each reminder is at getting HR to take action</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : reminderStats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-2xl font-bold text-slate-800">{reminderStats.total_reminders_sent || 0}</p>
                    <p className="text-xs text-slate-500">Total Sent</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4">
                    <p className="text-2xl font-bold text-green-600">{reminderStats.actions_after_reminder || 0}</p>
                    <p className="text-xs text-slate-500">Actions Taken</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4">
                    <p className="text-2xl font-bold text-blue-600">{reminderStats.effectiveness_rate || 0}%</p>
                    <p className="text-xs text-slate-500">Effectiveness</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4">
                    <p className="text-2xl font-bold text-amber-600">{reminderStats.opt_outs || 0}</p>
                    <p className="text-xs text-slate-500">Opt-outs</p>
                  </div>
                </div>

                {/* Breakdown by Reminder */}
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-3">Actions by Reminder Number</p>
                  <div className="space-y-3">
                    {[
                      { label: '1st Reminder', count: reminderStats.actions_after_first || 0, color: 'bg-blue-500' },
                      { label: '2nd Reminder', count: reminderStats.actions_after_second || 0, color: 'bg-amber-500' },
                      { label: '3rd Reminder', count: reminderStats.actions_after_third || 0, color: 'bg-red-500' }
                    ].map((item, i) => {
                      const maxCount = Math.max(reminderStats.actions_after_first || 0, reminderStats.actions_after_second || 0, reminderStats.actions_after_third || 0, 1);
                      const width = (item.count / maxCount) * 100;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-sm text-slate-600 w-28">{item.label}</span>
                          <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full ${item.color} rounded-full transition-all duration-500`} style={{ width: `${width}%` }} />
                          </div>
                          <span className="text-sm font-medium text-slate-700 w-10 text-right">{item.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <TrendingUp className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p>No reminder data yet</p>
                <p className="text-sm mt-1">Stats will appear as reminders are sent and HR takes action</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Unified Notification Center Section
  const NotificationCenterSection = () => {
    const [activeTab, setActiveTab] = useState('events');
    const [notificationSettings, setNotificationSettings] = useState({
      // HR Pipeline Events
      hr_candidate_shared: { email: true, whatsapp: false, wa_template: '' },
      hr_bulk_share: { email: true, whatsapp: false, wa_template: '' },
      hr_shortlisted: { email: true, whatsapp: false, wa_template: '' },
      hr_rejected: { email: true, whatsapp: false, wa_template: '' },
      hr_on_hold: { email: true, whatsapp: false, wa_template: '' },
      // Interview Events
      interview_slots_created: { email: true, whatsapp: false, wa_template: '' },
      interview_confirmed: { email: true, whatsapp: false, wa_template: '' },
      interview_reminder: { email: true, whatsapp: false, wa_template: '' },
      // Matching Events
      high_score_match: { email: true, whatsapp: false, wa_template: '' },
      daily_match_digest: { email: true, whatsapp: false, wa_template: '' },
      // Verification Events
      verification_request: { email: true, whatsapp: false, wa_template: '' },
      verification_completed: { email: true, whatsapp: false, wa_template: '' },
      verification_reminder: { email: true, whatsapp: false, wa_template: '' },
      // System Events
      cv_uploaded: { email: false, whatsapp: false, wa_template: '' },
      cv_upload_candidate_ack: { email: false, whatsapp: false, wa_template: 'cv_upload_candidate_ack' },
      daily_report: { email: true, whatsapp: false, wa_template: '' },
    });
    const [savingNotifications, setSavingNotifications] = useState(false);

    // Load notification settings from server
    useEffect(() => {
      if (settings?.notification_settings) {
        setNotificationSettings(prev => ({
          ...prev,
          ...settings.notification_settings
        }));
      }
    }, [settings]);

    const NOTIFICATION_EVENTS = [
      {
        category: 'HR Pipeline',
        icon: Users,
        color: 'text-blue-600 bg-blue-50',
        events: [
          { 
            key: 'hr_candidate_shared', 
            label: 'Candidate Shared for Review', 
            desc: 'When a single candidate is shared with HR',
            variables: '{{candidate_name}}, {{job_title}}, {{match_score}}, {{review_link}}',
            recipient: 'HR Contact'
          },
          { 
            key: 'hr_bulk_share', 
            label: 'Multiple Candidates Shared', 
            desc: 'When multiple candidates are bulk shared',
            variables: '{{count}}, {{job_title}}, {{review_link}}',
            recipient: 'HR Contact'
          },
          { 
            key: 'hr_shortlisted', 
            label: 'Candidate Shortlisted', 
            desc: 'When HR shortlists a candidate',
            variables: '{{candidate_name}}, {{job_title}}, {{hr_name}}',
            recipient: 'Recruiter'
          },
          { 
            key: 'hr_rejected', 
            label: 'Candidate Rejected', 
            desc: 'When HR rejects a candidate',
            variables: '{{candidate_name}}, {{job_title}}, {{hr_name}}, {{reason}}',
            recipient: 'Recruiter'
          },
          { 
            key: 'hr_on_hold', 
            label: 'Candidate On Hold', 
            desc: 'When HR puts candidate on hold',
            variables: '{{candidate_name}}, {{job_title}}, {{hr_name}}',
            recipient: 'Recruiter'
          },
        ]
      },
      {
        category: 'Interviews',
        icon: Calendar,
        color: 'text-purple-600 bg-purple-50',
        events: [
          { 
            key: 'interview_slots_created', 
            label: 'Interview Invitation', 
            desc: 'Sent to candidate to select time slot',
            variables: '{{candidate_name}}, {{job_title}}, {{company_name}}, {{select_link}}',
            recipient: 'Candidate'
          },
          { 
            key: 'interview_confirmed', 
            label: 'Interview Confirmed', 
            desc: 'When candidate confirms interview slot',
            variables: '{{candidate_name}}, {{job_title}}, {{company_name}}, {{date}}, {{time}}',
            recipient: 'Candidate + Recruiter'
          },
          { 
            key: 'interview_reminder', 
            label: 'Interview Reminder', 
            desc: '24 hours before interview',
            variables: '{{candidate_name}}, {{job_title}}, {{date}}, {{time}}',
            recipient: 'Candidate'
          },
        ]
      },
      {
        category: 'Matching',
        icon: Zap,
        color: 'text-amber-600 bg-amber-50',
        events: [
          { 
            key: 'high_score_match', 
            label: 'High Score Match', 
            desc: 'When a candidate matches above threshold',
            variables: '{{candidate_name}}, {{job_title}}, {{match_score}}, {{view_link}}',
            recipient: 'Recruiter'
          },
          { 
            key: 'daily_match_digest', 
            label: 'Daily Match Digest', 
            desc: 'Summary of new matches each day',
            variables: '{{date}}, {{new_matches_count}}, {{top_matches}}',
            recipient: 'Recruiter'
          },
        ]
      },
      {
        category: 'Background Verification',
        icon: UserCheck,
        color: 'text-green-600 bg-green-50',
        events: [
          { 
            key: 'verification_request', 
            label: 'Verification Request', 
            desc: 'Sent to previous employer',
            variables: '{{candidate_name}}, {{company_name}}, {{verifier_link}}',
            recipient: 'Previous Employer'
          },
          { 
            key: 'verification_completed', 
            label: 'Verification Completed', 
            desc: 'When employer responds',
            variables: '{{candidate_name}}, {{company_name}}, {{status}}',
            recipient: 'Recruiter'
          },
          { 
            key: 'verification_reminder', 
            label: 'Verification Reminder', 
            desc: 'Follow-up to employer',
            variables: '{{candidate_name}}, {{company_name}}, {{days_pending}}',
            recipient: 'Previous Employer'
          },
        ]
      },
      {
        category: 'System',
        icon: Bell,
        color: 'text-slate-600 bg-slate-100',
        events: [
          { 
            key: 'cv_uploaded', 
            label: 'New CV Uploaded', 
            desc: 'When a new CV is parsed',
            variables: '{{candidate_name}}, {{uploader_name}}',
            recipient: 'Admin'
          },
          { 
            key: 'daily_report', 
            label: 'Daily Activity Report', 
            desc: 'End of day summary',
            variables: '{{date}}, {{candidates_added}}, {{interviews_scheduled}}',
            recipient: 'Admin'
          },
        ]
      },
      {
        category: 'Candidate Notifications',
        icon: User,
        color: 'text-green-600 bg-green-50',
        events: [
          { 
            key: 'cv_upload_candidate_ack', 
            label: 'CV Upload Acknowledgement', 
            desc: 'Thank you & profile link sent to candidate after CV upload',
            variables: '{{profile_link}}',
            recipient: 'Candidate'
          },
        ]
      },
    ];

    const handleNotificationChange = (eventKey, channel, value) => {
      setNotificationSettings(prev => ({
        ...prev,
        [eventKey]: {
          ...prev[eventKey],
          [channel]: value
        }
      }));
    };

    const handleSaveNotifications = async () => {
      setSavingNotifications(true);
      try {
        await settingsApi.update({ notification_settings: notificationSettings });
        toast.success('Notification settings saved');
      } catch (err) {
        toast.error('Failed to save notification settings');
      } finally {
        setSavingNotifications(false);
      }
    };

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Notification Center</h3>
          <p className="text-sm text-slate-500">Configure email and WhatsApp notifications for all events</p>
        </div>

        {/* Browser notifications for the people using the ATS (notify-center) */}
        <DesktopAlertsSettings />

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
          {[
            { id: 'events', label: 'Notification Events', icon: Bell },
            { id: 'reminder_schedule', label: 'Reminder Schedule', icon: Clock },
            { id: 'email_templates', label: 'Email Templates', icon: Mail },
            { id: 'whatsapp_config', label: 'WhatsApp Config', icon: Smartphone },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-6">
            <div className="border border-slate-200 rounded-2xl p-6 bg-slate-50/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white border border-slate-200">
                    <Bell className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Channel Legend</p>
                    <p className="text-xs text-slate-500">Enable channels per notification event</p>
                  </div>
                </div>
                <div className="flex gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-500"></div>
                    <span className="text-slate-600">Email</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-green-500"></div>
                    <span className="text-slate-600">WhatsApp</span>
                  </div>
                </div>
              </div>
            </div>

            {NOTIFICATION_EVENTS.map((category) => (
              <div key={category.category} className="border border-slate-200 rounded-2xl overflow-hidden">
                <div className={`flex items-center gap-3 px-6 py-4 border-b border-slate-200 ${category.color.split(' ')[1]}`}>
                  <category.icon className={`w-5 h-5 ${category.color.split(' ')[0]}`} />
                  <h4 className="font-semibold text-slate-800">{category.category}</h4>
                  <span className="text-xs text-slate-500 ml-auto">{category.events.length} events</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {category.events.map((event) => (
                    <div key={event.key} className="px-6 py-4 hover:bg-slate-50/50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-slate-800">{event.label}</p>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                              → {event.recipient}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 mb-2">{event.desc}</p>
                          <p className="text-xs text-slate-400 font-mono">
                            Variables: {event.variables}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          {/* Email Toggle */}
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-blue-500" />
                            <Switch
                              checked={notificationSettings[event.key]?.email ?? false}
                              onCheckedChange={(checked) => handleNotificationChange(event.key, 'email', checked)}
                            />
                          </div>
                          {/* WhatsApp Toggle */}
                          <div className="flex items-center gap-2">
                            <Smartphone className="w-4 h-4 text-green-500" />
                            <Switch
                              checked={notificationSettings[event.key]?.whatsapp ?? false}
                              onCheckedChange={(checked) => handleNotificationChange(event.key, 'whatsapp', checked)}
                            />
                          </div>
                        </div>
                      </div>
                      {/* WhatsApp Template Name (shown when WhatsApp enabled) */}
                      {notificationSettings[event.key]?.whatsapp && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <div className="flex items-center gap-3">
                            <Label className="text-xs text-slate-500 whitespace-nowrap">WhatsApp Template:</Label>
                            <Input
                              value={notificationSettings[event.key]?.wa_template || ''}
                              onChange={(e) => handleNotificationChange(event.key, 'wa_template', e.target.value)}
                              placeholder="e.g., candidate_review_v1"
                              className="h-8 text-sm max-w-xs"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <Button onClick={handleSaveNotifications} disabled={savingNotifications}>
              {savingNotifications ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Notification Settings
            </Button>
          </div>
        )}

        {/* Reminder Schedule Tab */}
        {activeTab === 'reminder_schedule' && (
          <ReminderScheduleSettings settings={settings} onSave={loadSettings} />
        )}

        {/* Email Templates Tab */}
        {activeTab === 'email_templates' && (
          <div className="space-y-6">
            <Card className="border border-slate-200 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  HR Share Email
                </CardTitle>
                <CardDescription>Sent when sharing a candidate with company HR</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Subject Line</Label>
                  <Input
                    value={emailTemplates.hr_share_subject}
                    onChange={(e) => setEmailTemplates({ ...emailTemplates, hr_share_subject: e.target.value })}
                    placeholder="Email subject..."
                  />
                  <p className="text-xs text-slate-500 mt-1">Variables: {'{{candidate_name}}, {{job_title}}, {{match_score}}, {{company_name}}'}</p>
                </div>
                <div>
                  <Label>Introduction Text</Label>
                  <Textarea
                    value={emailTemplates.hr_share_intro}
                    onChange={(e) => setEmailTemplates({ ...emailTemplates, hr_share_intro: e.target.value })}
                    rows={2}
                    placeholder="Opening paragraph..."
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-500" />
                  Interview Invitation Email
                </CardTitle>
                <CardDescription>Sent to candidates when HR schedules interview slots</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Subject Line</Label>
                  <Input
                    value={emailTemplates.interview_invite_subject}
                    onChange={(e) => setEmailTemplates({ ...emailTemplates, interview_invite_subject: e.target.value })}
                    placeholder="Email subject..."
                  />
                  <p className="text-xs text-slate-500 mt-1">Variables: {'{{candidate_name}}, {{job_title}}, {{company_name}}'}</p>
                </div>
                <div>
                  <Label>Introduction Text</Label>
                  <Textarea
                    value={emailTemplates.interview_invite_intro}
                    onChange={(e) => setEmailTemplates({ ...emailTemplates, interview_invite_intro: e.target.value })}
                    rows={2}
                    placeholder="Opening paragraph..."
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Interview Confirmation Email
                </CardTitle>
                <CardDescription>Sent when candidate confirms their interview slot</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Subject Line</Label>
                  <Input
                    value={emailTemplates.interview_confirm_subject}
                    onChange={(e) => setEmailTemplates({ ...emailTemplates, interview_confirm_subject: e.target.value })}
                    placeholder="Email subject..."
                  />
                </div>
                <div>
                  <Label>Introduction Text</Label>
                  <Textarea
                    value={emailTemplates.interview_confirm_intro}
                    onChange={(e) => setEmailTemplates({ ...emailTemplates, interview_confirm_intro: e.target.value })}
                    rows={2}
                    placeholder="Opening paragraph..."
                  />
                </div>
              </CardContent>
            </Card>

            <Button onClick={() => handleSaveSettings()} disabled={savingTemplates}>
              {savingTemplates ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Email Templates
            </Button>
          </div>
        )}

        {/* WhatsApp Config Tab */}
        {activeTab === 'whatsapp_config' && (
          <div className="space-y-6">
            <div className="border border-slate-200 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 rounded-xl bg-green-50">
                  <Smartphone className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">Interakt WhatsApp API</h4>
                  <p className="text-sm text-slate-500 mt-1">
                    Configure your Interakt credentials to send WhatsApp notifications
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Interakt API Key</Label>
                  <Input
                    type="password"
                    value={settings?.interakt_api_key || ''}
                    onChange={(e) => setSettings({ ...settings, interakt_api_key: e.target.value })}
                    placeholder="Enter your API key"
                  />
                  <p className="text-xs text-slate-500">
                    From <a href="https://app.interakt.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Interakt Dashboard</a> → Settings → Developer API
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Default Country Code</Label>
                  <Input
                    value={settings?.default_country_code || '91'}
                    onChange={(e) => setSettings({ ...settings, default_country_code: e.target.value })}
                    placeholder="91"
                    className="w-24"
                  />
                  <p className="text-xs text-slate-500">Used when phone number doesn't include country code</p>
                </div>
              </div>
            </div>

            {/* WhatsApp Template Reference Guide */}
            <div className="border border-green-200 rounded-2xl p-6 bg-green-50/30">
              <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                WhatsApp Template Reference (All Templates)
              </h4>
              <p className="text-sm text-slate-600 mb-4">
                Below is the complete list of WhatsApp templates used by the system. 
                <strong className="text-red-600"> Only {'"{{1}}"'} variable is supported per template.</strong>
                <br/>All links must use <code className="bg-white px-2 py-0.5 rounded text-xs">daruwallas.com</code> domain.
              </p>
              
              <div className="space-y-3">
                {/* HR Review Templates */}
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-slate-800">HR Review Templates</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Template Name</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Action/Event</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Variable (1)</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Link</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">hr_candidate_review</code></td>
                          <td className="px-3 py-2 text-slate-600">Single candidate shared for HR review</td>
                          <td className="px-3 py-2 text-slate-500">review_link</td>
                          <td className="px-3 py-2"><code className="text-xs text-green-700">daruwallas.com/hr-review/{"{{1}}"}</code></td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">hr_bulk_review</code></td>
                          <td className="px-3 py-2 text-slate-600">Multiple candidates shared</td>
                          <td className="px-3 py-2 text-slate-500">review_link</td>
                          <td className="px-3 py-2"><code className="text-xs text-green-700">daruwallas.com/hr-review/{"{{1}}"}</code></td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">hr_retargeting_reminder</code></td>
                          <td className="px-3 py-2 text-slate-600">Follow-up reminder to HR</td>
                          <td className="px-3 py-2 text-slate-500">review_link</td>
                          <td className="px-3 py-2"><code className="text-xs text-green-700">daruwallas.com/hr-review/{"{{1}}"}</code></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Interview Templates */}
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-slate-800">Interview Templates</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Template Name</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Action/Event</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Variable (1)</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Link</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">interview_slot_selection</code></td>
                          <td className="px-3 py-2 text-slate-600">Ask candidate to select slot</td>
                          <td className="px-3 py-2 text-slate-500">slot_select_link</td>
                          <td className="px-3 py-2"><code className="text-xs text-green-700">daruwallas.com/interview/select/{"{{1}}"}</code></td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">interview_confirmed</code></td>
                          <td className="px-3 py-2 text-slate-600">Interview slot confirmed</td>
                          <td className="px-3 py-2 text-slate-500">interview_link</td>
                          <td className="px-3 py-2"><code className="text-xs text-green-700">daruwallas.com/interview/{"{{1}}"}</code></td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">interview_reminder</code></td>
                          <td className="px-3 py-2 text-slate-600">24hr reminder before interview</td>
                          <td className="px-3 py-2 text-slate-500">interview_link</td>
                          <td className="px-3 py-2"><code className="text-xs text-green-700">daruwallas.com/interview/{"{{1}}"}</code></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* OTP & Verification Templates */}
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Lock className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-slate-800">OTP & Verification Templates</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Template Name</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Action/Event</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Variable (1)</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Link</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">candidate_otp</code></td>
                          <td className="px-3 py-2 text-slate-600">Candidate portal login OTP</td>
                          <td className="px-3 py-2 text-slate-500">otp_code</td>
                          <td className="px-3 py-2 text-slate-400">No link</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">verification_request</code></td>
                          <td className="px-3 py-2 text-slate-600">Request employment verification</td>
                          <td className="px-3 py-2 text-slate-500">verify_link</td>
                          <td className="px-3 py-2"><code className="text-xs text-green-700">daruwallas.com/verify/{"{{1}}"}</code></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Candidate Notifications */}
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-teal-600" />
                    <span className="font-medium text-slate-800">Candidate Notification Templates</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Template Name</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Action/Event</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Variable (1)</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Link</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">cv_upload_candidate_ack</code></td>
                          <td className="px-3 py-2 text-slate-600">CV uploaded - thank you & profile link sent to candidate</td>
                          <td className="px-3 py-2 text-slate-500">profile_link</td>
                          <td className="px-3 py-2"><code className="text-xs text-green-700">daruwallas.com/{'{'}portal_slug{'}'}</code></td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">cv_uploaded</code></td>
                          <td className="px-3 py-2 text-slate-600">Admin notified of new CV upload</td>
                          <td className="px-3 py-2 text-slate-500">candidate_name, uploader_name</td>
                          <td className="px-3 py-2"><code className="text-xs text-slate-400">No link</code></td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">job_match_alert</code></td>
                          <td className="px-3 py-2 text-slate-600">High match job found</td>
                          <td className="px-3 py-2 text-slate-500">job_link</td>
                          <td className="px-3 py-2"><code className="text-xs text-green-700">daruwallas.com/my/jobs</code></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Important:</strong> When creating templates in Interakt, use exact template names above.
                  Base URL for all buttons: <code className="bg-white px-2 py-0.5 rounded text-xs">https://daruwallas.com</code>
                </p>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save WhatsApp Config
            </Button>
          </div>
        )}
      </div>
    );
  };

  // AI Job Search Engine Section
  const AIJobSearchSection = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    const copyToClipboard = (text) => {
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-1">AI Job Search Engine</h3>
            <p className="text-sm text-slate-500">
              Search external job boards (Google Jobs, LinkedIn, Indeed) and match candidates automatically
            </p>
          </div>
          <a 
            href="/ai-job-search-setup.html" 
            target="_blank"
            className="flex items-center gap-2 text-violet-600 hover:text-violet-700 text-sm font-medium"
          >
            <ExternalLink className="w-4 h-4" />
            View Setup Guide
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Enable AI Job Search */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Enable AI Job Search</CardTitle>
                  <CardDescription>Auto-search external jobs when CV is uploaded</CardDescription>
                </div>
                <Switch
                  checked={settings?.ai_job_search_enabled || false}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ai_job_search_enabled: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Auto-Trigger */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Auto-Trigger on CV Upload</CardTitle>
                  <CardDescription>Automatically start job search when new CV is parsed</CardDescription>
                </div>
                <Switch
                  checked={settings?.job_search_auto_trigger ?? true}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, job_search_auto_trigger: checked }))}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* LLM Provider Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="w-4 h-4" />
              AI Provider for Job Search
            </CardTitle>
            <CardDescription>Choose which LLM to use for query generation and job matching</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div 
                onClick={() => setSettings(prev => ({ ...prev, ai_job_search_provider: 'openai' }))}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  (settings?.ai_job_search_provider || 'openai') === 'openai' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-600 flex items-center justify-center text-white font-bold">O</div>
                  <div>
                    <p className="font-medium text-slate-800">OpenAI GPT-4o-mini</p>
                    <p className="text-xs text-slate-500">Fast & reliable</p>
                  </div>
                </div>
                {(settings?.ai_job_search_provider || 'openai') === 'openai' && (
                  <Badge className="mt-2 bg-green-100 text-green-700">Active</Badge>
                )}
              </div>
              
              <div 
                onClick={() => setSettings(prev => ({ ...prev, ai_job_search_provider: 'gemini' }))}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  settings?.ai_job_search_provider === 'gemini' 
                    ? 'border-violet-500 bg-violet-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center text-white font-bold">G</div>
                  <div>
                    <p className="font-medium text-slate-800">Gemini Flash</p>
                    <p className="text-xs text-slate-500">Google AI</p>
                  </div>
                </div>
                {settings?.ai_job_search_provider === 'gemini' && (
                  <Badge className="mt-2 bg-violet-100 text-violet-700">Active</Badge>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Note: Make sure the selected provider's API key is configured in AI & LLM settings
            </p>
          </CardContent>
        </Card>

        {/* API Keys */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="w-4 h-4" />
              Search API Keys
            </CardTitle>
            <CardDescription>Configure external search providers (at least one required)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* SerpAPI */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white font-bold text-sm">S</div>
                <div>
                  <p className="font-medium text-slate-800">SerpAPI (Google Jobs)</p>
                  <a href="https://serpapi.com/manage-api-key" target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-600 hover:underline flex items-center gap-1">
                    Get API Key <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <Input
                type="password"
                placeholder="Enter SerpAPI Key"
                value={settings?.serpapi_api_key || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, serpapi_api_key: e.target.value }))}
              />
            </div>

            {/* Perplexity */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center text-white font-bold text-sm">P</div>
                <div>
                  <p className="font-medium text-slate-800">Perplexity Sonar (AI Search)</p>
                  <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                    Get API Key <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <Input
                type="password"
                placeholder="Enter Perplexity API Key"
                value={settings?.perplexity_api_key || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, perplexity_api_key: e.target.value }))}
              />
            </div>

            {/* Firecrawl */}
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center text-white font-bold text-sm">F</div>
                <div>
                  <p className="font-medium text-slate-800">Firecrawl (Job Page Scraping)</p>
                  <a href="https://firecrawl.dev" target="_blank" rel="noopener noreferrer" className="text-xs text-orange-600 hover:underline flex items-center gap-1">
                    Get API Key <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <Input
                type="password"
                placeholder="Enter Firecrawl API Key (optional)"
                value={settings?.firecrawl_api_key || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, firecrawl_api_key: e.target.value }))}
              />
              <p className="text-xs text-slate-500 mt-1">Optional: Enriches job details by scraping job pages</p>
            </div>

            {/* Apollo.io */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center text-white font-bold text-sm">A</div>
                <div>
                  <p className="font-medium text-slate-800">Apollo.io (HR Contact Finder)</p>
                  <a href="https://app.apollo.io/#/settings/integrations/api" target="_blank" rel="noopener noreferrer" className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                    Get API Key <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <Input
                type="password"
                placeholder="Enter Apollo.io API Key"
                value={settings?.apollo_api_key || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, apollo_api_key: e.target.value }))}
              />
              <p className="text-xs text-slate-500 mt-1">Finds HR email & phone for each company automatically</p>
            </div>

            {/* Lemlist */}
            <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center text-white font-bold text-sm">L</div>
                <div>
                  <p className="font-medium text-slate-800">Lemlist (Cold Email Sending)</p>
                  <a href="https://app.lemlist.com/settings/integrations" target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 hover:underline flex items-center gap-1">
                    Get API Key <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <Input
                type="password"
                placeholder="Enter Lemlist API Key"
                value={settings?.lemlist_api_key || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, lemlist_api_key: e.target.value }))}
              />
              <p className="text-xs text-slate-500 mt-1">Lets BDE send emails through Lemlist and read back opens, bounces, spam flags and replies</p>
            </div>
          </CardContent>
        </Card>

        {/* Match Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Match Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Minimum Match Score for Candidates (%)</Label>
              <p className="text-xs text-slate-500 mb-2">Jobs below this score won't be shown to candidates (admin can force-reveal)</p>
              <Input
                type="number"
                min="0"
                max="100"
                value={settings?.job_search_min_match_score ?? 50}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  job_search_min_match_score: parseInt(e.target.value) || 50 
                }))}
                className="w-32"
              />
            </div>
          </CardContent>
        </Card>

        {/* Max Jobs Per Search */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="w-4 h-4" />
              Search Limits
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Maximum Jobs Per Search</Label>
              <p className="text-xs text-slate-500 mb-2">Set to 0 to disable limit, or 1-50 for specific limits (higher uses more API credits)</p>
              <Input
                type="number"
                min="0"
                max="50"
                value={settings?.job_search_max_results ?? 20}
                onChange={(e) => setSettings(prev => ({ 
                  ...prev, 
                  job_search_max_results: Math.min(50, Math.max(0, parseInt(e.target.value) || 0))
                }))}
                className="w-32"
              />
            </div>
          </CardContent>
        </Card>

        {/* Auto-Share Settings */}
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-blue-700">
              <Zap className="w-4 h-4" />
              Auto-Share with HR
            </CardTitle>
            <CardDescription>Automatically share high-scoring job matches with HR contacts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Auto-Share</Label>
                <p className="text-xs text-slate-500">Automatically share candidates with HR when match score is high</p>
              </div>
              <Switch
                checked={settings?.ai_job_search_auto_share_enabled || false}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ai_job_search_auto_share_enabled: checked }))}
              />
            </div>
            {settings?.ai_job_search_auto_share_enabled && (
              <div>
                <Label>Auto-Share Score Threshold (%)</Label>
                <p className="text-xs text-slate-500 mb-2">Set to 0 to share all matches, or 1-100 for minimum score</p>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings?.ai_job_search_auto_share_threshold ?? 80}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    ai_job_search_auto_share_threshold: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                  }))}
                  className="w-32"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Production URL Setting */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              Production URL
            </CardTitle>
            <CardDescription>Base URL used in shared links (e.g., HR review links)</CardDescription>
          </CardHeader>
          <CardContent>
            <Input
              type="url"
              placeholder="https://daruwallas.com"
              value={settings?.production_frontend_url || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, production_frontend_url: e.target.value }))}
            />
            <p className="text-xs text-slate-500 mt-2">
              Leave empty to use: daruwallas.com
            </p>
          </CardContent>
        </Card>

        {/* Save Button for AI Job Search Settings */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="px-8">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save AI Job Search Settings
          </Button>
        </div>

        {/* WhatsApp Templates Info */}
        <Card className="border-violet-200 bg-violet-50/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-violet-700">
              <MessageSquare className="w-4 h-4" />
              WhatsApp Templates Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-3">
              Submit these templates in Interakt for job search notifications:
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="font-mono text-xs">ai_job_match_found</Badge>
                <span className="text-slate-600">→ Notify candidate of job matches</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="font-mono text-xs">candidate_applied_job</Badge>
                <span className="text-slate-600">→ Notify admin when candidate applies</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="font-mono text-xs">job_search_started</Badge>
                <span className="text-slate-600">→ Confirm search started to candidate</span>
              </div>
            </div>
            <a 
              href="/ai-job-search-setup.html" 
              target="_blank"
              className="inline-flex items-center gap-1 mt-4 text-violet-600 hover:text-violet-700 text-sm font-medium"
            >
              View template formats <ExternalLink className="w-3 h-3" />
            </a>
          </CardContent>
        </Card>

        {/* Cost Estimate */}
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-green-700">
              <TrendingUp className="w-4 h-4" />
              Estimated Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-slate-500">SerpAPI</p>
                <p className="font-semibold text-slate-800">~$0.03</p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-slate-500">Perplexity</p>
                <p className="font-semibold text-slate-800">~$0.02</p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-slate-500">Firecrawl</p>
                <p className="font-semibold text-slate-800">~$0.02</p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-slate-500">Gemini</p>
                <p className="font-semibold text-slate-800">~$0.01</p>
              </div>
            </div>
            <p className="text-center mt-3 text-green-700 font-medium">
              Total: ~$0.08 - $0.15 per CV
            </p>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Email Templates Section with HTML Editor and Preview
  const EmailTemplatesSection = () => {
    const [activeTemplate, setActiveTemplate] = useState('hr_share');
    const [showPreview, setShowPreview] = useState(false);
    const [savingTemplate, setSavingTemplate] = useState(false);
    
    // Default HR share template
    const defaultHrTemplate = `<html>
<body style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: linear-gradient(135deg, #1e40af, #3b82f6); padding: 20px; border-radius: 10px 10px 0 0;">
        <h2 style="color: white; margin: 0;">Candidate Referral for {{job_title}}</h2>
    </div>
    <div style="border: 1px solid #e2e8f0; border-top: none; padding: 20px; border-radius: 0 0 10px 10px;">
        <p>Dear {{hr_name}},</p>
        <p>Daruwallas would like to present this candidate to you for your reference for the <strong>{{job_title}}</strong> position at <strong>{{company_name}}</strong>.</p>
        
        <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #3b82f6;">
            <p style="margin: 5px 0;"><strong>Candidate Name:</strong> {{candidate_name}}</p>
            <p style="margin: 5px 0;"><strong>Experience:</strong> {{experience_years}} years</p>
            <p style="margin: 5px 0;"><strong>Current Role:</strong> {{current_role}}</p>
            <p style="margin: 5px 0;"><strong>Current Location:</strong> {{location}}</p>
            <p style="margin: 5px 0;"><strong>CTC:</strong> {{ctc}}</p>
            <p style="margin: 5px 0;"><strong>Notice Period:</strong> {{notice_period}}</p>
        </div>
        
        <p><strong>Key Skills:</strong> {{skills}}</p>
        
        <div style="text-align: center; margin: 25px 0;">
            <a href="{{review_link}}" style="background: #3b82f6; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block;">Review Candidate Profile</a>
        </div>
        
        <p style="color: #64748b; font-size: 12px;">This link is valid for 7 days.</p>
        
        <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;">
        <p style="color: #64748b; font-size: 11px;">
            Best regards,<br>
            Daruwalla Consultancy Services
        </p>
    </div>
</body>
</html>`;

    // Default candidate template
    const defaultCandidateTemplate = `<html>
<body style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: linear-gradient(135deg, #059669, #10b981); padding: 20px; border-radius: 10px 10px 0 0;">
        <h2 style="color: white; margin: 0;">Exciting Opportunity at {{company_name}}</h2>
    </div>
    <div style="border: 1px solid #e2e8f0; border-top: none; padding: 20px; border-radius: 0 0 10px 10px;">
        <p>Dear {{candidate_name}},</p>
        <p>We would like to present you an exciting opportunity to work as <strong>{{job_title}}</strong> at <strong>{{company_name}}</strong>.</p>
        
        <div style="background: #f0fdf4; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #10b981;">
            <p style="margin: 5px 0;"><strong>Position:</strong> {{job_title}}</p>
            <p style="margin: 5px 0;"><strong>Company:</strong> {{company_name}}</p>
            <p style="margin: 5px 0;"><strong>Location:</strong> {{job_location}}</p>
            <p style="margin: 5px 0;"><strong>Salary Range:</strong> {{salary_range}}</p>
        </div>
        
        <p><strong>Job Description:</strong></p>
        <p>{{job_description}}</p>
        
        <div style="text-align: center; margin: 25px 0;">
            <a href="{{apply_link}}" style="background: #10b981; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block;">View Job Details</a>
        </div>
        
        <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;">
        <p style="color: #64748b; font-size: 11px;">
            Best regards,<br>
            Daruwalla Consultancy Services<br>
            <em>Your Career Partner</em>
        </p>
    </div>
</body>
</html>`;

    // Initialize with default templates if no saved templates exist
    const [hrTemplate, setHrTemplate] = useState(settings?.hr_share_email_template || defaultHrTemplate);
    const [candidateTemplate, setCandidateTemplate] = useState(settings?.candidate_opportunity_email_template || defaultCandidateTemplate);

    const getCurrentTemplate = () => activeTemplate === 'hr_share' ? hrTemplate : candidateTemplate;
    const setCurrentTemplate = (val) => activeTemplate === 'hr_share' ? setHrTemplate(val) : setCandidateTemplate(val);
    const getDefaultTemplate = () => activeTemplate === 'hr_share' ? defaultHrTemplate : defaultCandidateTemplate;
    
    const getPreviewHtml = () => {
      const template = getCurrentTemplate() || getDefaultTemplate();
      // Replace placeholders with sample data for preview
      return template
        .replace(/\{\{hr_name\}\}/g, 'John Smith')
        .replace(/\{\{candidate_name\}\}/g, 'Rahul Sharma')
        .replace(/\{\{job_title\}\}/g, 'Senior Software Engineer')
        .replace(/\{\{company_name\}\}/g, 'TechCorp India')
        .replace(/\{\{experience_years\}\}/g, '5')
        .replace(/\{\{current_role\}\}/g, 'Software Engineer')
        .replace(/\{\{location\}\}/g, 'Mumbai, India')
        .replace(/\{\{ctc\}\}/g, '₹15,00,000')
        .replace(/\{\{notice_period\}\}/g, '30 days')
        .replace(/\{\{skills\}\}/g, 'React, Node.js, Python, AWS')
        .replace(/\{\{review_link\}\}/g, '#preview')
        .replace(/\{\{match_score\}\}/g, '85')
        .replace(/\{\{job_location\}\}/g, 'Mumbai, India')
        .replace(/\{\{salary_range\}\}/g, '₹18-25 LPA')
        .replace(/\{\{job_description\}\}/g, 'We are looking for an experienced developer to join our team...')
        .replace(/\{\{apply_link\}\}/g, '#preview');
    };
    
    const saveTemplates = async () => {
      try {
        setSavingTemplate(true);
        const token = localStorage.getItem('ats_token');
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        
        const response = await fetch(`${API_URL}/api/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            hr_share_email_template: hrTemplate || null,
            candidate_opportunity_email_template: candidateTemplate || null
          })
        });
        
        if (response.ok) {
          toast.success('Email templates saved successfully');
          setSettings(prev => ({
            ...prev,
            hr_share_email_template: hrTemplate || null,
            candidate_opportunity_email_template: candidateTemplate || null
          }));
        } else {
          toast.error('Failed to save templates');
        }
      } catch (error) {
        console.error('Error saving templates:', error);
        toast.error('Error saving templates');
      } finally {
        setSavingTemplate(false);
      }
    };
    
    const resetToDefault = () => {
      setCurrentTemplate(getDefaultTemplate());
      toast.info('Reset to default template');
    };
    
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Template Editor
            </CardTitle>
            <CardDescription>
              Customize the HTML emails sent to HR contacts and candidates. Use placeholders like {`{{candidate_name}}`} for dynamic content.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Template Tabs */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={activeTemplate === 'hr_share' ? 'default' : 'outline'}
                onClick={() => setActiveTemplate('hr_share')}
                className="flex items-center gap-2"
              >
                <Building2 className="w-4 h-4" />
                HR Share Email
              </Button>
              <Button
                variant={activeTemplate === 'candidate' ? 'default' : 'outline'}
                onClick={() => setActiveTemplate('candidate')}
                className="flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                Candidate Opportunity Email
              </Button>
            </div>
            
            {/* Available Placeholders */}
            <Card className="mb-4 bg-slate-50 border-slate-200">
              <CardContent className="py-3">
                <p className="text-sm font-medium text-slate-700 mb-2">Available Placeholders:</p>
                <div className="flex flex-wrap gap-2">
                  {activeTemplate === 'hr_share' ? (
                    <>
                      <Badge variant="outline" className="font-mono text-xs">{`{{hr_name}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{candidate_name}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{job_title}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{company_name}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{experience_years}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{current_role}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{location}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{ctc}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{notice_period}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{skills}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{review_link}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{match_score}}`}</Badge>
                    </>
                  ) : (
                    <>
                      <Badge variant="outline" className="font-mono text-xs">{`{{candidate_name}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{job_title}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{company_name}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{job_location}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{salary_range}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{job_description}}`}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">{`{{apply_link}}`}</Badge>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Editor/Preview Toggle */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Button
                  variant={!showPreview ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setShowPreview(false)}
                >
                  <Edit className="w-4 h-4 mr-1" /> Editor
                </Button>
                <Button
                  variant={showPreview ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setShowPreview(true)}
                >
                  <Eye className="w-4 h-4 mr-1" /> Preview
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={resetToDefault}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Reset to Default
                </Button>
              </div>
            </div>
            
            {/* Editor or Preview */}
            {!showPreview ? (
              <Textarea
                value={getCurrentTemplate() || getDefaultTemplate()}
                onChange={(e) => setCurrentTemplate(e.target.value)}
                className="font-mono text-sm min-h-[400px] resize-y"
                placeholder="Enter your HTML email template..."
              />
            ) : (
              <div className="border rounded-lg p-4 bg-white min-h-[400px] overflow-auto">
                <div dangerouslySetInnerHTML={{ __html: getPreviewHtml() }} />
              </div>
            )}
            
            {/* Save Button */}
            <div className="flex justify-end mt-4">
              <Button onClick={saveTemplates} disabled={savingTemplate}>
                {savingTemplate ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Templates
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Instructions */}
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-amber-700">
              <FileText className="w-4 h-4" />
              Template Tips
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-600">
            <ul className="space-y-2">
              <li>• Use inline CSS styles for best email client compatibility</li>
              <li>• Test your emails by using the "Share with HR" feature</li>
              <li>• Leave template empty to use the default template</li>
              <li>• Placeholders like {`{{candidate_name}}`} will be replaced with actual values</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Webhook & ATS Integration Section
  const WebhookSection = () => {
    const [webhookStats, setWebhookStats] = useState(null);
    const [webhookNotifications, setWebhookNotifications] = useState([]);
    const [loadingStats, setLoadingStats] = useState(true);
    const [generatingSecret, setGeneratingSecret] = useState(false);
    const [activeTab, setActiveTab] = useState('settings');
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    const loadWebhookData = async () => {
      try {
        setLoadingStats(true);
        const token = localStorage.getItem('ats_token');
        const [statsRes, notifRes] = await Promise.all([
          fetch(`${API_URL}/api/webhook/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/webhook/notifications?limit=20`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        if (statsRes.ok) {
          const data = await statsRes.json();
          setWebhookStats(data.stats);
        }
        if (notifRes.ok) {
          const data = await notifRes.json();
          setWebhookNotifications(data.notifications || []);
        }
      } catch (error) {
        console.error('Failed to load webhook data:', error);
      }
      setLoadingStats(false);
    };
    
    useEffect(() => {
      loadWebhookData();
    }, []);
    
    const generateNewSecret = async () => {
      try {
        setGeneratingSecret(true);
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${API_URL}/api/webhook/generate-secret`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, webhook_secret: data.secret }));
          toast.success('New webhook secret generated');
        } else {
          toast.error('Failed to generate secret');
        }
      } catch (error) {
        toast.error('Error generating secret');
      }
      setGeneratingSecret(false);
    };
    
    const copyToClipboard = (text) => {
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    };
    
    const resendNotification = async (id) => {
      try {
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${API_URL}/api/webhook/notifications/${id}/resend`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          toast.success('Notification queued for resend');
          loadWebhookData();
        } else {
          toast.error('Failed to resend notification');
        }
      } catch (error) {
        toast.error('Error resending notification');
      }
    };
    
    const cancelNotification = async (id) => {
      try {
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${API_URL}/api/webhook/notifications/${id}/cancel`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          toast.success('Notification cancelled');
          loadWebhookData();
        } else {
          toast.error('Failed to cancel notification');
        }
      } catch (error) {
        toast.error('Error cancelling notification');
      }
    };
    
    const getStatusBadge = (status) => {
      const styles = {
        pending: 'bg-amber-100 text-amber-700',
        sent: 'bg-green-100 text-green-700',
        failed: 'bg-red-100 text-red-700',
        cancelled: 'bg-slate-100 text-slate-500'
      };
      return (
        <Badge className={styles[status] || 'bg-slate-100'}>
          {status}
        </Badge>
      );
    };
    
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Webhook & ATS Integration</h3>
          <p className="text-sm text-slate-500">Receive candidate data from external ATS and send automated WhatsApp notifications</p>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          {['settings', 'notifications', 'stats'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                activeTab === tab 
                  ? 'bg-violet-100 text-violet-700 border-b-2 border-violet-500' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Enable Webhook */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Enable Webhook</CardTitle>
                    <CardDescription>Allow external ATS to send candidate data</CardDescription>
                  </div>
                  <Switch
                    checked={settings?.webhook_enabled || false}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, webhook_enabled: checked }))}
                  />
                </div>
              </CardHeader>
            </Card>
            
            {/* Webhook Secret */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Webhook Secret
                </CardTitle>
                <CardDescription>Use this secret in your ATS webhook configuration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={settings?.webhook_secret || ''}
                    readOnly
                    className="font-mono text-sm bg-slate-50"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(settings?.webhook_secret || '')}
                    disabled={!settings?.webhook_secret}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={generateNewSecret}
                  disabled={generatingSecret}
                  className="text-sm"
                >
                  {generatingSecret ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Generate New Secret
                </Button>
                <p className="text-xs text-slate-500">
                  Your ATS should include this in the header: <code className="bg-slate-100 px-1 rounded">X-Webhook-Secret: {settings?.webhook_secret ? '••••••••' : '[not set]'}</code>
                </p>
              </CardContent>
            </Card>
            
            {/* Webhook Endpoint */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Webhook Endpoint
                </CardTitle>
                <CardDescription>Configure your external ATS to POST data to this URL</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={`${settings?.production_frontend_url || 'https://daruwallas.com'}/api/webhook/candidate`}
                    readOnly
                    className="font-mono text-sm bg-slate-50"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(`${settings?.production_frontend_url || 'https://daruwallas.com'}/api/webhook/candidate`)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  <strong>Note:</strong> This URL uses your production domain. You can change it in Page Settings → Production Frontend URL.
                </p>
                <div className="p-3 bg-slate-50 rounded-lg border text-xs font-mono">
                  <p className="text-slate-500 mb-2">Expected payload:</p>
                  <pre className="text-slate-700 whitespace-pre-wrap">{`{
  "candidate_name": "John Doe",
  "phone": "+919876543210",
  "email": "john@example.com",
  "job_title": "Senior Developer",
  "match_score": 85,
  "company_name": "Tech Corp",
  "parsed_resume": { ... }
}`}</pre>
                </div>
              </CardContent>
            </Card>
            
            {/* Notification Settings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Notification Settings
                </CardTitle>
                <CardDescription>Configure when and how notifications are sent</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Delay Before Sending (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="1440"
                      value={settings?.webhook_notification_delay_minutes ?? 5}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        webhook_notification_delay_minutes: parseInt(e.target.value) || 0 
                      }))}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Wait time before sending WhatsApp (0 = immediate)</p>
                  </div>
                  <div>
                    <Label className="text-sm">Minimum Score Threshold (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={settings?.webhook_min_score_threshold ?? 0}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        webhook_min_score_threshold: parseInt(e.target.value) || 0 
                      }))}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Only notify if score is above this (0 = all)</p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm">WhatsApp Template Name</Label>
                  <Input
                    type="text"
                    placeholder="candidate_match_notification"
                    value={settings?.webhook_whatsapp_template || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      webhook_whatsapp_template: e.target.value 
                    }))}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Template variables: <code className="bg-slate-100 px-1 rounded">{'{{candidate_name}}, {{job_title}}, {{match_score}}, {{profile_link}}'}</code>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {activeTab === 'notifications' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-slate-700">Recent Notifications</h4>
              <Button variant="outline" size="sm" onClick={loadWebhookData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            {loadingStats ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : webhookNotifications.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No notifications yet</p>
                <p className="text-sm">Notifications will appear here when candidates are received via webhook</p>
              </div>
            ) : (
              <div className="space-y-2">
                {webhookNotifications.map(notif => (
                  <div key={notif.id} className="p-4 bg-white border rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-800">{notif.candidate_name}</span>
                          {getStatusBadge(notif.status)}
                        </div>
                        <p className="text-sm text-slate-500 truncate">
                          {notif.job_title} • {notif.match_score}% match
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {notif.candidate_phone} • Scheduled: {new Date(notif.scheduled_at).toLocaleString()}
                        </p>
                        {notif.error_message && (
                          <p className="text-xs text-red-500 mt-1">{notif.error_message}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {notif.status === 'failed' && (
                          <Button variant="ghost" size="sm" onClick={() => resendNotification(notif.id)}>
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        {notif.status === 'pending' && (
                          <Button variant="ghost" size="sm" onClick={() => cancelNotification(notif.id)}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'stats' && (
          <div className="space-y-4">
            {loadingStats ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : webhookStats ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-slate-800">{webhookStats.total}</div>
                    <p className="text-sm text-slate-500">Total Received</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-green-600">{webhookStats.sent}</div>
                    <p className="text-sm text-slate-500">Sent</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-amber-600">{webhookStats.pending}</div>
                    <p className="text-sm text-slate-500">Pending</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-red-600">{webhookStats.failed}</div>
                    <p className="text-sm text-slate-500">Failed</p>
                  </CardContent>
                </Card>
                <Card className="col-span-2">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-violet-600">{webhookStats.today_received}</div>
                    <p className="text-sm text-slate-500">Received Today</p>
                  </CardContent>
                </Card>
                <Card className="col-span-2">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-violet-600">{webhookStats.today_sent}</div>
                    <p className="text-sm text-slate-500">Sent Today</p>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">Failed to load statistics</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // System Users Section
  const SystemUsersSection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">System Users</h3>
          <p className="text-sm text-slate-500">Manage admin, recruiter, and HR accounts</p>
        </div>
        <Button onClick={handleOpenCreateUser} data-testid="add-system-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search users..."
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Users Table */}
      {usersLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-slate-600">User</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Role</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-right p-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSystemUsers.map((user) => (
                <tr key={user.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="p-3">
                    <div>
                      <p className="font-medium text-slate-800">{user.full_name}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                      {user.phone && <p className="text-xs text-slate-400">{user.phone}</p>}
                    </div>
                  </td>
                  <td className="p-3">
                    <RoleBadge role={user.role} />
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* Impersonate - only for super_admin and not for other super_admins */}
                      {isSuperAdmin && user.role !== 'super_admin' && (
                        <button
                          onClick={() => handleImpersonateUser(user)}
                          disabled={impersonating === user.id}
                          className="p-2 text-slate-400 hover:text-violet-600 rounded"
                          title="Login as this user (untraced)"
                          data-testid={`impersonate-${user.id}`}
                        >
                          {impersonating === user.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <LogIn className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      {/* Password Reset */}
                      <button
                        onClick={() => setResetPasswordDialog(user)}
                        className="p-2 text-slate-400 hover:text-amber-600 rounded"
                        title="Send password reset"
                        data-testid={`reset-password-${user.id}`}
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      {/* Edit */}
                      <button
                        onClick={() => handleOpenEditUser(user)}
                        className="p-2 text-slate-400 hover:text-slate-600 rounded"
                        title="Edit user"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {/* Delete */}
                      {isSuperAdmin && user.id !== currentUser?.id && (
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 text-slate-400 hover:text-red-600 rounded"
                          title="Delete user"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSystemUsers.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-slate-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={userFormData.full_name}
                onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                placeholder="john@company.com"
              />
            </div>
            <div>
              <Label>Phone (for WhatsApp OTP)</Label>
              <Input
                value={userFormData.phone}
                onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                placeholder="+91 9876543210"
              />
            </div>
            <div>
              <Label>Password {editingUser && '(leave blank to keep current)'}</Label>
              <Input
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                placeholder={editingUser ? '••••••••' : 'Enter password'}
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={userFormData.role}
                onValueChange={(value) => setUserFormData({ ...userFormData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.id} value={role.id}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={userFormData.is_active}
                onCheckedChange={(checked) => setUserFormData({ ...userFormData, is_active: checked })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveUser}>
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={!!resetPasswordDialog} onOpenChange={() => setResetPasswordDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Password Reset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Send a password reset link to <strong>{resetPasswordDialog?.full_name}</strong>
            </p>
            <div>
              <Label>Send via</Label>
              <Select value={resetMethod} onValueChange={setResetMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email ({resetPasswordDialog?.email || 'not set'})
                    </div>
                  </SelectItem>
                  <SelectItem value="whatsapp">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      WhatsApp ({resetPasswordDialog?.phone || 'not set'})
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialog(null)}>Cancel</Button>
            <Button onClick={handleSendPasswordReset} disabled={sendingReset}>
              {sendingReset ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Send Reset Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Permissions Section
  const PermissionsSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-1">Role-Based Access Control</h3>
        <p className="text-sm text-slate-500">Configure what each role can see and do</p>
      </div>

      {permissionsLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <Accordion type="single" collapsible className="w-full">
          {ROLES.map((role) => (
            <AccordionItem key={role.id} value={role.id} className="border rounded-lg px-4 mb-2">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${role.color}`}>
                    {role.label}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4 pb-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(PERMISSION_LABELS).map(([key, { label, icon: Icon }]) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={rolePermissions[role.id]?.[key] ?? false}
                        onCheckedChange={(checked) => handlePermissionChange(role.id, key, checked)}
                      />
                      <Icon className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
                <Button
                  size="sm"
                  onClick={() => handleSavePermissions(role.id)}
                  disabled={savingPermissions}
                  className="mt-4"
                >
                  {savingPermissions ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save {role.label} Permissions
                </Button>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );

  // Integrations Section (Google Calendar)
  const IntegrationsSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-1">Third-Party Integrations</h3>
        <p className="text-sm text-slate-500">Connect external services for enhanced functionality</p>
      </div>

      {/* Organization Timezone */}
      <Card className="border-amber-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Organization Timezone
          </CardTitle>
          <CardDescription>Set the timezone for calendar events and scheduled tasks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select
              value={settings?.organization_timezone || 'Asia/Kolkata'}
              onValueChange={(value) => setSettings({ ...settings, organization_timezone: value })}
            >
              <SelectTrigger data-testid="organization-timezone">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Asia/Kolkata">Asia/Kolkata (IST, UTC+5:30)</SelectItem>
                <SelectItem value="Asia/Dubai">Asia/Dubai (GST, UTC+4)</SelectItem>
                <SelectItem value="Asia/Singapore">Asia/Singapore (SGT, UTC+8)</SelectItem>
                <SelectItem value="Europe/London">Europe/London (GMT/BST)</SelectItem>
                <SelectItem value="Europe/Paris">Europe/Paris (CET/CEST)</SelectItem>
                <SelectItem value="America/New_York">America/New_York (EST/EDT)</SelectItem>
                <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</SelectItem>
                <SelectItem value="America/Chicago">America/Chicago (CST/CDT)</SelectItem>
                <SelectItem value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</SelectItem>
                <SelectItem value="Pacific/Auckland">Pacific/Auckland (NZST/NZDT)</SelectItem>
                <SelectItem value="UTC">UTC (Coordinated Universal Time)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">This timezone is used for Google Calendar events and scheduled task times</p>
          </div>
          <Button onClick={handleSave} disabled={saving} size="sm">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Timezone
          </Button>
        </CardContent>
      </Card>

      {/* Google Calendar Integration */}
      <Card className="border-blue-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Google Calendar
            {googleCalendarStatus.connected && (
              <Badge className="bg-green-100 text-green-700 ml-2">Connected</Badge>
            )}
          </CardTitle>
          <CardDescription>Schedule interviews directly from candidate profiles</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Connection Status */}
          {googleCalendarStatus.connected ? (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Connected to Google Calendar</p>
                    <p className="text-sm text-green-600">{googleCalendarStatus.email}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleDisconnectGoogleCalendar} className="text-red-600 border-red-200 hover:bg-red-50">
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">Setup Instructions:</h4>
                <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
                  <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                  <li>Enable the <strong>Google Calendar API</strong></li>
                  <li>Create OAuth 2.0 credentials (Web Application)</li>
                  <li>Add redirect URI: <code className="bg-blue-100 px-1 rounded text-xs">{process.env.REACT_APP_BACKEND_URL}/api/google-calendar/callback</code></li>
                  <li>Enter your credentials below and save</li>
                  <li>Click "Connect Google Calendar" button</li>
                </ol>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Google Client ID</Label>
                  <Input
                    value={settings?.google_calendar_client_id || ''}
                    onChange={(e) => setSettings({ ...settings, google_calendar_client_id: e.target.value })}
                    placeholder="xxxxx.apps.googleusercontent.com"
                    data-testid="google-calendar-client-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Google Client Secret</Label>
                  <Input
                    type="password"
                    value={settings?.google_calendar_client_secret || ''}
                    onChange={(e) => setSettings({ ...settings, google_calendar_client_secret: e.target.value })}
                    placeholder="GOCSPX-..."
                    data-testid="google-calendar-client-secret"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Credentials
                </Button>
                <Button 
                  onClick={handleConnectGoogleCalendar} 
                  disabled={connectingGoogle || !settings?.google_calendar_client_id || !settings?.google_calendar_client_secret}
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  {connectingGoogle ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                  Connect Google Calendar
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Email Tracking */}
      <Card className="border-purple-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            Email Analytics
          </CardTitle>
          <CardDescription>Track email opens and link clicks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
              <div>
                <div className="font-medium flex items-center gap-2">
                  <Eye className="w-4 h-4 text-slate-400" />
                  Track Email Opens
                </div>
                <div className="text-sm text-slate-500">Know when candidates open your emails</div>
              </div>
              <Switch
                checked={settings?.track_email_opens ?? true}
                onCheckedChange={(checked) => setSettings({ ...settings, track_email_opens: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
              <div>
                <div className="font-medium flex items-center gap-2">
                  <MousePointerClick className="w-4 h-4 text-slate-400" />
                  Track Link Clicks
                </div>
                <div className="text-sm text-slate-500">Track when candidates click links in emails</div>
              </div>
              <Switch
                checked={settings?.track_email_clicks ?? true}
                onCheckedChange={(checked) => setSettings({ ...settings, track_email_clicks: checked })}
              />
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700">
              <strong>Note:</strong> Email tracking requires a tracking pixel in emails. 
              Analytics will appear in the Email section once enabled.
            </p>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save Integration Settings
      </Button>
    </div>
  );


  // Verification Settings Section
  const VerificationSection = () => {
    const [requiredDocs, setRequiredDocs] = useState([]);
    const [newDoc, setNewDoc] = useState('');
    const [emailTemplate, setEmailTemplate] = useState({ subject: '', body: '' });
    const [loadingVerif, setLoadingVerif] = useState(true);
    const [savingDocs, setSavingDocs] = useState(false);
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [requireApproval, setRequireApproval] = useState(false);
    const [sendImmediately, setSendImmediately] = useState(true);
    const [scheduledTime, setScheduledTime] = useState('10:00');
    const [pendingEmails, setPendingEmails] = useState([]);
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [bulkProcessing, setBulkProcessing] = useState(false);

    useEffect(() => {
      loadVerificationSettings();
      loadPendingEmails();
    }, []);

    const loadVerificationSettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        
        const [docsRes, templateRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/settings/required-documents`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/settings/verification-email-template`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/settings/verification`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        if (docsRes.ok) {
          const data = await docsRes.json();
          setRequiredDocs(data.documents || []);
        }
        if (templateRes.ok) {
          const data = await templateRes.json();
          setEmailTemplate({ subject: data.subject || '', body: data.body || '' });
        }
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setRequireApproval(data.require_approval || false);
          setSendImmediately(data.send_immediately !== false);
          setScheduledTime(data.scheduled_send_time || '10:00');
        }
      } catch (error) {
        console.error('Failed to load verification settings:', error);
      } finally {
        setLoadingVerif(false);
      }
    };

    const loadPendingEmails = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${API_URL}/api/email-queue?status=pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPendingEmails(data.emails || []);
        }
      } catch (error) {
        console.error('Failed to load pending emails:', error);
      }
    };

    const updateVerificationSetting = async (key, value) => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const params = new URLSearchParams();
        params.append(key, value);
        
        await fetch(`${API_URL}/api/settings/verification?${params}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Setting updated');
      } catch (error) {
        toast.error('Failed to update setting');
      }
    };

    const approveEmail = async (emailId) => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${API_URL}/api/email-queue/${emailId}/approve`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          toast.success(data.message || 'Email approved');
          loadPendingEmails();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to approve email');
        }
      } catch (error) {
        toast.error('Failed to approve email');
      }
    };

    const rejectEmail = async (emailId) => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${API_URL}/api/email-queue/${emailId}/reject`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          toast.success('Email rejected');
          loadPendingEmails();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to reject email');
        }
      } catch (error) {
        toast.error('Failed to reject email');
      }
    };

    const toggleSelectEmail = (emailId) => {
      setSelectedEmails(prev => 
        prev.includes(emailId) 
          ? prev.filter(id => id !== emailId)
          : [...prev, emailId]
      );
    };

    const toggleSelectAll = () => {
      if (selectedEmails.length === pendingEmails.length) {
        setSelectedEmails([]);
      } else {
        setSelectedEmails(pendingEmails.map(e => e.id));
      }
    };

    const bulkApprove = async () => {
      if (selectedEmails.length === 0) return;
      setBulkProcessing(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      let success = 0;
      
      for (const emailId of selectedEmails) {
        try {
          const response = await fetch(`${API_URL}/api/email-queue/${emailId}/approve`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) success++;
        } catch (e) {
          console.error(`Failed to approve email ${emailId}:`, e);
        }
      }
      
      toast.success(`${success} email(s) approved`);
      setSelectedEmails([]);
      loadPendingEmails();
      setBulkProcessing(false);
    };

    const bulkReject = async () => {
      if (selectedEmails.length === 0) return;
      setBulkProcessing(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      let success = 0;
      
      for (const emailId of selectedEmails) {
        try {
          const response = await fetch(`${API_URL}/api/email-queue/${emailId}/reject`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) success++;
        } catch (e) {
          console.error(`Failed to reject email ${emailId}:`, e);
        }
      }
      
      toast.success(`${success} email(s) rejected`);
      setSelectedEmails([]);
      loadPendingEmails();
      setBulkProcessing(false);
    };

    const saveRequiredDocs = async () => {
      setSavingDocs(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${API_URL}/api/settings/required-documents`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requiredDocs)
        });
        if (response.ok) {
          toast.success('Required documents list saved');
        } else {
          toast.error('Failed to save required documents');
        }
      } catch (error) {
        toast.error('Failed to save required documents');
      } finally {
        setSavingDocs(false);
      }
    };

    const saveEmailTemplate = async () => {
      setSavingTemplate(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const params = new URLSearchParams();
        params.append('subject', emailTemplate.subject);
        params.append('body', emailTemplate.body);
        
        const response = await fetch(`${API_URL}/api/settings/verification-email-template?${params}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          toast.success('Verification email template saved');
        } else {
          toast.error('Failed to save email template');
        }
      } catch (error) {
        toast.error('Failed to save email template');
      } finally {
        setSavingTemplate(false);
      }
    };

    const addDocument = () => {
      if (newDoc.trim() && !requiredDocs.includes(newDoc.trim())) {
        setRequiredDocs([...requiredDocs, newDoc.trim()]);
        setNewDoc('');
      }
    };

    const removeDocument = (doc) => {
      setRequiredDocs(requiredDocs.filter(d => d !== doc));
    };

    if (loadingVerif) {
      return (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Background Verification Settings</h3>
          <p className="text-sm text-slate-500">Configure required documents, email approval, and verification templates</p>
        </div>

        {/* Email Sending Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-600" />
              Email Sending Options
            </CardTitle>
            <CardDescription>Control how verification emails are sent</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Require Admin Approval</p>
                <p className="text-sm text-slate-500">Emails go to queue until approved by admin</p>
              </div>
              <Switch
                checked={requireApproval}
                onCheckedChange={(checked) => {
                  setRequireApproval(checked);
                  updateVerificationSetting('require_approval', checked);
                }}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Send Immediately</p>
                <p className="text-sm text-slate-500">Send emails as soon as they're created/approved</p>
              </div>
              <Switch
                checked={sendImmediately}
                onCheckedChange={(checked) => {
                  setSendImmediately(checked);
                  updateVerificationSetting('send_immediately', checked);
                }}
              />
            </div>
            
            {!sendImmediately && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label>Scheduled Send Time</Label>
                <Select value={scheduledTime} onValueChange={(v) => {
                  setScheduledTime(v);
                  updateVerificationSetting('scheduled_send_time', v);
                }}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="09:00">9:00 AM</SelectItem>
                    <SelectItem value="10:00">10:00 AM</SelectItem>
                    <SelectItem value="14:00">2:00 PM</SelectItem>
                    <SelectItem value="17:00">5:00 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Email Queue */}
        {pendingEmails.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4" />
                  Pending Email Approvals ({pendingEmails.length})
                </CardTitle>
                {selectedEmails.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">{selectedEmails.length} selected</span>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={bulkReject} disabled={bulkProcessing}>
                      {bulkProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reject Selected'}
                    </Button>
                    <Button size="sm" onClick={bulkApprove} disabled={bulkProcessing}>
                      {bulkProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Approve Selected'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Checkbox 
                  checked={selectedEmails.length === pendingEmails.length && pendingEmails.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
                <span className="text-sm text-slate-600">Select All</span>
              </div>
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {pendingEmails.map((email) => (
                  <div key={email.id} className={`p-3 bg-white rounded-lg border ${selectedEmails.includes(email.id) ? 'ring-2 ring-blue-500' : ''}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        checked={selectedEmails.includes(email.id)}
                        onCheckedChange={() => toggleSelectEmail(email.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{email.subject}</p>
                        <p className="text-xs text-slate-500">To: {email.to_email}</p>
                        <p className="text-xs text-slate-400">
                          {email.candidate_name} • {email.created_by_name}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="text-red-600 h-7 px-2" onClick={() => rejectEmail(email.id)}>
                          Reject
                        </Button>
                        <Button size="sm" className="h-7 px-2" onClick={() => approveEmail(email.id)}>
                          Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Required Documents */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Required Documents Checklist
            </CardTitle>
            <CardDescription>Documents to collect from candidates during verification</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newDoc}
                onChange={(e) => setNewDoc(e.target.value)}
                placeholder="Add a document type (e.g., 'PAN Card', 'Address Proof')"
                onKeyDown={(e) => e.key === 'Enter' && addDocument()}
              />
              <Button onClick={addDocument}>
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>

            <div className="space-y-2">
              {requiredDocs.map((doc, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 border rounded-lg bg-slate-50"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-500">{idx + 1}.</span>
                    <span>{doc}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => removeDocument(doc)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button onClick={saveRequiredDocs} disabled={savingDocs}>
              {savingDocs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Document List
            </Button>
          </CardContent>
        </Card>

        {/* Email Template */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-green-600" />
              Verification Email Template
            </CardTitle>
            <CardDescription>Email sent to previous employers for background verification</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm">
              <p className="font-medium text-blue-800 mb-1">Available Variables:</p>
              <div className="flex flex-wrap gap-2">
                {['{{contact_name}}', '{{company_name}}', '{{designation}}', '{{tenure_from}}', '{{tenure_to}}', '{{sender_name}}'].map(v => (
                  <code key={v} className="px-2 py-0.5 bg-blue-100 rounded text-blue-700 text-xs">{v}</code>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Subject</Label>
              <Input
                value={emailTemplate.subject}
                onChange={(e) => setEmailTemplate({ ...emailTemplate, subject: e.target.value })}
                placeholder="Employment Verification Request - {{company_name}}"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Body</Label>
              <Textarea
                value={emailTemplate.body}
                onChange={(e) => setEmailTemplate({ ...emailTemplate, body: e.target.value })}
                rows={15}
                className="font-mono text-sm"
                placeholder="Dear {{contact_name}},

We are reaching out regarding an employment verification..."
              />
            </div>

            <Button onClick={saveEmailTemplate} disabled={savingTemplate}>
              {savingTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Email Template
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Authentication Settings Section (WhatsApp OTP)
  const AuthenticationSection = () => {
    return (
      <div className="space-y-6">
        {/* WhatsApp Status Banner */}
        {notificationStatus && (
          <Card className={`border ${notificationStatus.whatsapp.ready ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {notificationStatus.whatsapp.ready ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  )}
                  <div>
                    <p className={`font-medium ${notificationStatus.whatsapp.ready ? 'text-green-800' : 'text-amber-800'}`}>
                      {notificationStatus.whatsapp.ready ? 'WhatsApp Configured' : 'WhatsApp Not Configured'}
                    </p>
                    <p className="text-sm text-slate-600">
                      {notificationStatus.whatsapp.ready 
                        ? `Interakt API key set | Country code: +${notificationStatus.whatsapp.default_country_code}`
                        : 'Configure Interakt API key below to enable WhatsApp notifications'}
                    </p>
                  </div>
                </div>
                {notificationStatus.whatsapp.ready && (
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="9876543210"
                      value={testPhoneNumber}
                      onChange={(e) => setTestPhoneNumber(e.target.value)}
                      className="w-36"
                    />
                    <Button size="sm" variant="outline" onClick={handleTestWhatsApp} disabled={testingWhatsApp}>
                      {testingWhatsApp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-green-600" />
              WhatsApp OTP Login
            </CardTitle>
            <CardDescription>
              Configure phone-based OTP authentication using Interakt WhatsApp API
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${settings?.whatsapp_otp_enabled ? 'bg-green-100' : 'bg-slate-200'}`}>
                  <MessageSquare className={`w-5 h-5 ${settings?.whatsapp_otp_enabled ? 'text-green-600' : 'text-slate-500'}`} />
                </div>
                <div>
                  <p className="font-medium text-slate-800">Enable WhatsApp OTP</p>
                  <p className="text-sm text-slate-500">Allow users to login via OTP sent to WhatsApp</p>
                </div>
              </div>
              <Switch
                checked={settings?.whatsapp_otp_enabled || false}
                onCheckedChange={(checked) => setSettings({ ...settings, whatsapp_otp_enabled: checked })}
              />
            </div>

            {/* BGV send gate - the two switches that decide whether background */}
            {/* verification actually transmits. They used to be reachable only by */}
            {/* editing a file on the server. */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-4">
              <div>
                <p className="font-semibold text-slate-800">Background verification messages</p>
                <p className="text-sm text-slate-600">
                  On means BGV sends real emails and WhatsApps to candidates and clients.
                  Off means every message is still recorded, but nothing leaves the building.
                </p>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-800">Send BGV notifications</p>
                  <p className="text-sm text-slate-500">The master switch. Off means nothing goes out, by any channel.</p>
                </div>
                <Switch
                  checked={settings?.bgv_send_enabled || false}
                  onCheckedChange={(checked) => setSettings({ ...settings, bgv_send_enabled: checked })}
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-800">Send BGV WhatsApps</p>
                  <p className="text-sm text-slate-500">Needs the master switch above and an Interakt key. Email is unaffected.</p>
                </div>
                <Switch
                  checked={settings?.bgv_whatsapp_enabled || false}
                  onCheckedChange={(checked) => setSettings({ ...settings, bgv_whatsapp_enabled: checked })}
                />
              </div>
            </div>

            {/* Interakt Configuration */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Interakt API Configuration
              </h3>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="interakt_api_key">Interakt API Key</Label>
                  <Input
                    id="interakt_api_key"
                    type="password"
                    value={settings?.interakt_api_key || ''}
                    onChange={(e) => setSettings({ ...settings, interakt_api_key: e.target.value })}
                    placeholder="Enter your Interakt API key"
                  />
                  <p className="text-xs text-slate-500">
                    Get your API key from <a href="https://app.interakt.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Interakt Dashboard</a>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interakt_template">WhatsApp Template Name</Label>
                  <Input
                    id="interakt_template"
                    value={settings?.interakt_template_name || ''}
                    onChange={(e) => setSettings({ ...settings, interakt_template_name: e.target.value })}
                    placeholder="e.g., otp_login_template"
                  />
                  <p className="text-xs text-slate-500">
                    The approved template name for OTP messages
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interakt_thankyou_template">Portal Thank You Template</Label>
                  <Input
                    id="interakt_thankyou_template"
                    value={settings?.interakt_portal_thankyou_template || ''}
                    onChange={(e) => setSettings({ ...settings, interakt_portal_thankyou_template: e.target.value })}
                    placeholder="e.g., portal_verification_thankyou"
                  />
                  <p className="text-xs text-slate-500">
                    Sent after a candidate verifies their phone on the portal. Use <code className="bg-slate-100 px-1 rounded">{'"{{1}}"'}</code> for candidate name.
                  </p>
                </div>
              </div>
            </div>

            {/* OTP Settings */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Key className="w-4 h-4" />
                OTP Settings
              </h3>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="otp_validity">OTP Validity (minutes)</Label>
                  <Input
                    id="otp_validity"
                    type="number"
                    min={1}
                    max={30}
                    value={settings?.otp_validity_minutes || 5}
                    onChange={(e) => setSettings({ ...settings, otp_validity_minutes: parseInt(e.target.value) || 5 })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div>
                    <p className="font-medium text-sm text-slate-800">Allow Self Registration</p>
                    <p className="text-xs text-slate-500">New phone numbers can create accounts</p>
                  </div>
                  <Switch
                    checked={settings?.allow_otp_self_registration || false}
                    onCheckedChange={(checked) => setSettings({ ...settings, allow_otp_self_registration: checked })}
                  />
                </div>
              </div>
            </div>

            {/* Test Mode Notice */}
            {!settings?.whatsapp_otp_enabled && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Test Mode Active</p>
                    <p className="text-sm text-amber-700">
                      WhatsApp OTP is disabled. OTP will be displayed as a toast notification on the login page for testing purposes.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {settings?.whatsapp_otp_enabled && (!settings?.interakt_api_key || !settings?.interakt_template_name) && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Configuration Required</p>
                    <p className="text-sm text-red-700">
                      Please enter your Interakt API key and template name to enable WhatsApp OTP.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* How to Setup Guide */}
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="setup-guide">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    How to Setup Interakt WhatsApp OTP
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 space-y-3">
                  <ol className="list-decimal list-inside space-y-2">
                    <li>Sign up at <a href="https://www.interakt.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Interakt.ai</a></li>
                    <li>Connect your WhatsApp Business account</li>
                    <li>Create a message template for OTP with these variables:
                      <ul className="list-disc list-inside ml-4 mt-1 text-slate-500">
                        <li><code className="bg-slate-100 px-1 rounded">{'"{{1}}"'}</code> - The 6-digit OTP code</li>
                        <li><code className="bg-slate-100 px-1 rounded">{'{{2}}'}</code> - Validity in minutes</li>
                      </ul>
                    </li>
                    <li>Wait for template approval (usually 24-48 hours)</li>
                    <li>Copy your API key from Settings → Developer API</li>
                    <li>Enter the API key and template name above</li>
                    <li>Enable WhatsApp OTP toggle</li>
                  </ol>
                  <p className="text-xs text-slate-500 mt-3">
                    Example template: "Your OTP for DCS ATS login is {'"{{1}}"'}. Valid for {'{{2}}'} minutes."
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Save Button */}
            <div className="pt-4 border-t">
              <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Authentication Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Productivity Settings Section
  const ProductivitySection = () => {
    const { refreshSettings: refreshProductivityContext } = useProductivity();
    const [prodSettings, setProdSettings] = useState({
      idle_timeout: 55,
      alarm_sound: 'classic',
      alarm_volume: 0.8,
      idle_tracking_enabled: true,
      daily_report_enabled: true,
      daily_report_emails: '',
      daily_report_time: '18:00'
    });
    const [loadingProd, setLoadingProd] = useState(true);
    const [savingProd, setSavingProd] = useState(false);

    useEffect(() => {
      loadProductivitySettings();
    }, []);

    const loadProductivitySettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${API_URL}/api/productivity/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setProdSettings({
            idle_timeout: data.idle_timeout || 55,
            alarm_sound: data.alarm_sound || 'classic',
            alarm_volume: data.alarm_volume || 0.8,
            idle_tracking_enabled: data.idle_tracking_enabled !== false,
            daily_report_enabled: data.daily_report_enabled !== false,
            daily_report_emails: (data.daily_report_emails || []).join(', '),
            daily_report_time: data.daily_report_time || '18:00'
          });
        }
      } catch (error) {
        console.error('Failed to load productivity settings:', error);
      } finally {
        setLoadingProd(false);
      }
    };

    const saveProductivitySettings = async () => {
      setSavingProd(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const params = new URLSearchParams();
        params.append('idle_timeout', prodSettings.idle_timeout);
        params.append('alarm_sound', prodSettings.alarm_sound);
        params.append('alarm_volume', prodSettings.alarm_volume);
        params.append('idle_tracking_enabled', prodSettings.idle_tracking_enabled);
        params.append('daily_report_enabled', prodSettings.daily_report_enabled);
        params.append('daily_report_emails', prodSettings.daily_report_emails);
        params.append('daily_report_time', prodSettings.daily_report_time);
        
        const response = await fetch(`${API_URL}/api/productivity/settings?${params}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          toast.success('Productivity settings saved');
          // Refresh the ProductivityContext so idle tracking picks up new settings
          if (refreshProductivityContext) {
            await refreshProductivityContext();
          }
        } else {
          toast.error('Failed to save productivity settings');
        }
      } catch (error) {
        toast.error('Failed to save productivity settings');
      } finally {
        setSavingProd(false);
      }
    };

    const playTestAlarm = () => {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const patterns = {
        classic: { frequency: 800, pattern: [200, 100, 200, 100, 200, 100, 200] },
        urgent: { frequency: 1000, pattern: [100, 50, 100, 50, 100, 200, 100, 50, 100, 50, 100] },
        gentle: { frequency: 600, pattern: [300, 200, 300, 200, 300] },
      };
      const config = patterns[prodSettings.alarm_sound] || patterns.classic;
      let currentTime = ctx.currentTime;
      
      config.pattern.forEach((duration, index) => {
        if (index % 2 === 0) {
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);
          oscillator.frequency.value = config.frequency;
          oscillator.type = 'square';
          gainNode.gain.value = prodSettings.alarm_volume;
          oscillator.start(currentTime);
          oscillator.stop(currentTime + duration / 1000);
        }
        currentTime += duration / 1000;
      });
    };

    if (loadingProd) {
      return (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Productivity & Idle Tracking</h3>
          <p className="text-sm text-slate-500">Configure idle detection and daily productivity reports</p>
        </div>

        {/* Idle Tracking Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MousePointerClick className="w-4 h-4 text-amber-600" />
              Idle Detection Settings
            </CardTitle>
            <CardDescription>Configure when and how to alert idle recruiters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <div className="font-medium">Enable Idle Tracking</div>
                <div className="text-sm text-slate-500">Track when recruiters are inactive</div>
              </div>
              <Switch
                checked={prodSettings.idle_tracking_enabled}
                onCheckedChange={(checked) => setProdSettings({ ...prodSettings, idle_tracking_enabled: checked })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Idle Timeout</Label>
                <Select 
                  value={String(prodSettings.idle_timeout)} 
                  onValueChange={(v) => setProdSettings({ ...prodSettings, idle_timeout: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 seconds</SelectItem>
                    <SelectItem value="30">30 seconds</SelectItem>
                    <SelectItem value="45">45 seconds</SelectItem>
                    <SelectItem value="55">55 seconds (default)</SelectItem>
                    <SelectItem value="60">1 minute</SelectItem>
                    <SelectItem value="90">1.5 minutes</SelectItem>
                    <SelectItem value="120">2 minutes</SelectItem>
                    <SelectItem value="180">3 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Alarm Sound</Label>
                <Select 
                  value={prodSettings.alarm_sound} 
                  onValueChange={(v) => setProdSettings({ ...prodSettings, alarm_sound: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="classic">Classic Beep</SelectItem>
                    <SelectItem value="urgent">Urgent Alert</SelectItem>
                    <SelectItem value="gentle">Gentle Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Alarm Volume: {Math.round(prodSettings.alarm_volume * 100)}%</Label>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1"
                value={prodSettings.alarm_volume}
                onChange={(e) => setProdSettings({ ...prodSettings, alarm_volume: parseFloat(e.target.value) })}
                className="w-full"
              />
            </div>

            <Button variant="outline" onClick={playTestAlarm}>
              <Bell className="w-4 h-4 mr-2" />
              Test Alarm Sound
            </Button>
          </CardContent>
        </Card>

        {/* Daily Report Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" />
              Daily Productivity Report
            </CardTitle>
            <CardDescription>Automatic email reports at end of day</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <div className="font-medium">Enable Daily Reports</div>
                <div className="text-sm text-slate-500">Send productivity summary emails</div>
              </div>
              <Switch
                checked={prodSettings.daily_report_enabled}
                onCheckedChange={(checked) => setProdSettings({ ...prodSettings, daily_report_enabled: checked })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Report Email Recipients</Label>
                <Input
                  value={prodSettings.daily_report_emails}
                  onChange={(e) => setProdSettings({ ...prodSettings, daily_report_emails: e.target.value })}
                  placeholder="email1@company.com, email2@company.com"
                />
                <p className="text-xs text-slate-500">Comma-separated email addresses</p>
              </div>
              <div className="space-y-2">
                <Label>Report Time</Label>
                <Select 
                  value={prodSettings.daily_report_time} 
                  onValueChange={(v) => setProdSettings({ ...prodSettings, daily_report_time: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="17:00">5:00 PM</SelectItem>
                    <SelectItem value="18:00">6:00 PM</SelectItem>
                    <SelectItem value="19:00">7:00 PM</SelectItem>
                    <SelectItem value="20:00">8:00 PM</SelectItem>
                    <SelectItem value="21:00">9:00 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button onClick={saveProductivitySettings} disabled={savingProd}>
          {savingProd ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Productivity Settings
        </Button>
      </div>
    );
  };


  // Documentation Section - uses page-level state for hooks compliance
  const DocumentationSection = () => {
    const formatDate = (isoString) => {
      if (!isoString) return 'Never';
      const date = new Date(isoString);
      return date.toLocaleString('en-IN', { 
        dateStyle: 'medium', 
        timeStyle: 'short',
        timeZone: 'Asia/Kolkata'
      });
    };

    const formatBytes = (bytes) => {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    return (
      <div className="space-y-6">
        {/* User Guide Section */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Documentation & User Guide</h3>
                <p className="text-sm text-slate-500">Learn how to use DCS ATS effectively</p>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-800">Complete User Guide</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Comprehensive documentation covering all features, step-by-step tutorials, and best practices.
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        HTML Format
                      </span>
                      <span className="flex items-center gap-1">
                        <Download className="w-4 h-4" />
                        Downloadable
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button 
                      onClick={() => window.open('/docs/DCS_ATS_User_Guide.html', '_blank')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Online
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = '/docs/DCS_ATS_User_Guide.html';
                        link.download = 'DCS_ATS_User_Guide.html';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        toast.success('Documentation downloaded!');
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-3">Quick Links</h4>
                <div className="grid grid-cols-2 gap-3">
                  <a href="/docs/DCS_ATS_User_Guide.html#candidates" target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Managing Candidates</span>
                  </a>
                  <a href="/docs/DCS_ATS_User_Guide.html#jobs" target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                    <Briefcase className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Job Management</span>
                  </a>
                  <a href="/docs/DCS_ATS_User_Guide.html#matching" target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                    <Zap className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">AI Matching</span>
                  </a>
                  <a href="/docs/DCS_ATS_User_Guide.html#hr-pipeline" target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">HR Pipeline</span>
                  </a>
                  <a href="/docs/DCS_ATS_User_Guide.html#settings" target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                    <Settings className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">Settings Guide</span>
                  </a>
                  <a href="/docs/DCS_ATS_User_Guide.html#faq" target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                    <AlertTriangle className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-700">FAQ & Troubleshooting</span>
                  </a>
                </div>
              </div>

              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-emerald-800">Documentation Includes</h4>
                    <ul className="text-sm text-emerald-700 mt-2 space-y-1">
                      <li>• Step-by-step tutorials for all features</li>
                      <li>• AI CV parsing and matching guide</li>
                      <li>• HR pipeline and sharing workflow</li>
                      <li>• Candidate portal setup</li>
                      <li>• Keyboard shortcuts reference</li>
                      <li>• Troubleshooting tips</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Documentation Pack - Admin Only */}
        {isAdmin && (
          <Card className="border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Download className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">System Documentation Pack</h3>
                  <p className="text-sm text-slate-500">Code-derived technical documentation for architects</p>
                </div>
                <span className="ml-auto px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded">Admin Only</span>
              </div>

              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 mb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800">Technical Documentation Pack</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Complete A-to-Z specification of the DCS ATS system. Auto-generated from codebase including API endpoints, database schemas, templates, and more.
                    </p>
                    
                    {docsLoading ? (
                      <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading status...
                      </div>
                    ) : docsStatus?.has_docs ? (
                      <div className="mt-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Generated: {formatDate(docsStatus.generated_at)}
                          </span>
                          {docsStatus.git_commit && (
                            <span className="flex items-center gap-1 font-mono text-xs bg-slate-200 px-2 py-0.5 rounded">
                              {docsStatus.git_commit}
                            </span>
                          )}
                          {docsStatus.zip_size_bytes && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              {formatBytes(docsStatus.zip_size_bytes)}
                            </span>
                          )}
                        </div>
                        
                        {docsStatus.counts && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                              {docsStatus.counts.api_endpoints} API Endpoints
                            </span>
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                              {docsStatus.counts.frontend_routes} Routes
                            </span>
                            <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                              {docsStatus.counts.collections} Collections
                            </span>
                            <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                              {docsStatus.counts.scheduled_jobs} Cron Jobs
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-3 text-sm text-amber-600">
                        No documentation pack generated yet. Click "Regenerate" to create one.
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button 
                      onClick={handleDocsRegenerate}
                      disabled={docsRegenerating}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {docsRegenerating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Regenerate
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleDocsDownload}
                      disabled={docsDownloading || !docsStatus?.has_zip}
                    >
                      {docsDownloading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download ZIP
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-3">Pack Contents ({docsStatus?.files?.length || 17} files)</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 00-INDEX.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 01-overview.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 02-sitemap.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 03-roles-permissions.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 04-data-model.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 05-api-reference.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 06-candidate-portal.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 07-recruiter-modules.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 08-ai-features.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 09-communications.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 10-schedulers-cron.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 11-integrations.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 12-settings-admin.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 13-files-storage.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 14-jm-modules.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> 15-known-gaps.md
                  </div>
                  <div className="flex items-center gap-1 text-slate-600">
                    <FileText className="w-3 h-3" /> manifest.json
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };


  // Scheduled Tasks Section (with hooks - needs to be a component)
  const ScheduledTasksSection = () => {
    const [taskStatus, setTaskStatus] = useState(null);
    const [taskLogs, setTaskLogs] = useState([]);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [runningTask, setRunningTask] = useState(null);

    useEffect(() => {
      loadTaskStatus();
      loadTaskLogs();
    }, []);

    const loadTaskStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scheduled-tasks/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTaskStatus(data);
        }
      } catch (error) {
        console.error('Failed to load task status:', error);
      } finally {
        setLoadingStatus(false);
      }
    };

    const loadTaskLogs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scheduled-tasks/logs?limit=10`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTaskLogs(data.logs || []);
        }
      } catch (error) {
        console.error('Failed to load task logs:', error);
      }
    };

    const runTaskNow = async (taskName) => {
      setRunningTask(taskName);
      try {
        const formData = new FormData();
        formData.append('task_name', taskName);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scheduled-tasks/run-now`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
          body: formData
        });
        
        if (response.ok) {
          toast.success('Task started! Check logs for progress.');
          setTimeout(() => {
            loadTaskStatus();
            loadTaskLogs();
          }, 2000);
        } else {
          const error = await response.json().catch(() => ({}));
          toast.error(error.detail || 'Failed to start task');
        }
      } catch (error) {
        toast.error('Failed to start task');
      } finally {
        setRunningTask(null);
      }
    };

    const DAYS_OF_WEEK = [
      { value: 'monday', label: 'Mon' },
      { value: 'tuesday', label: 'Tue' },
      { value: 'wednesday', label: 'Wed' },
      { value: 'thursday', label: 'Thu' },
      { value: 'friday', label: 'Fri' },
      { value: 'saturday', label: 'Sat' },
      { value: 'sunday', label: 'Sun' }
    ];

    const toggleDay = (day) => {
      const currentDays = settings?.hr_retargeting_schedule_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const newDays = currentDays.includes(day) 
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];
      setSettings({ ...settings, hr_retargeting_schedule_days: newDays });
    };

    if (loadingStatus) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Master Toggle */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Scheduled Tasks</h3>
                  <p className="text-sm text-slate-500">Enable automated background jobs</p>
                </div>
              </div>
              <Switch
                checked={settings?.scheduled_tasks_enabled || false}
                onCheckedChange={(checked) => setSettings({ ...settings, scheduled_tasks_enabled: checked })}
              />
            </div>
          </CardContent>
        </Card>

        {/* AI Insights Schedule - NEW */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-amber-100">
                <Sparkles className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-800">AI Insights Generation</h3>
                <p className="text-sm text-slate-500">Schedule daily AI-powered analytics (saves API costs)</p>
              </div>
              <Switch
                checked={settings?.ai_insights_enabled !== false}
                onCheckedChange={(checked) => setSettings({ ...settings, ai_insights_enabled: checked })}
                disabled={!settings?.scheduled_tasks_enabled}
              />
            </div>

            {settings?.ai_insights_enabled !== false && (
              <div className="space-y-4 mt-4 pt-4 border-t">
                {/* Auto-refresh Toggle */}
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <p className="font-medium text-amber-800">Auto-Refresh Mode</p>
                    <p className="text-sm text-amber-600">
                      {settings?.ai_insights_auto_refresh 
                        ? "Regenerates insights on every page load (higher API costs)" 
                        : "Only runs at scheduled time (recommended for cost control)"}
                    </p>
                  </div>
                  <Switch
                    checked={settings?.ai_insights_auto_refresh || false}
                    onCheckedChange={(checked) => setSettings({ ...settings, ai_insights_auto_refresh: checked })}
                  />
                </div>

                {/* Schedule Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700">Daily Run Time</Label>
                    <Input
                      type="time"
                      value={settings?.ai_insights_schedule_time || "06:00"}
                      onChange={(e) => setSettings({ ...settings, ai_insights_schedule_time: e.target.value })}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Server timezone (UTC)</p>
                  </div>
                  <div className="flex items-end pb-1">
                    <div className="p-3 bg-slate-100 rounded-lg text-sm">
                      <p className="text-slate-600">Next scheduled run:</p>
                      <p className="font-medium text-slate-800">
                        {taskStatus?.ai_insights?.next_run 
                          ? new Date(taskStatus.ai_insights.next_run).toLocaleString()
                          : 'Not scheduled'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Schedule Days */}
                <div>
                  <Label className="text-slate-700 mb-2 block">Run on Days</Label>
                  <div className="flex gap-2">
                    {DAYS_OF_WEEK.map(day => {
                      const aiDays = settings?.ai_insights_schedule_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
                      const isSelected = aiDays.includes(day.value);
                      return (
                        <button
                          key={day.value}
                          onClick={() => {
                            const newDays = isSelected 
                              ? aiDays.filter(d => d !== day.value)
                              : [...aiDays, day.value];
                            setSettings({ ...settings, ai_insights_schedule_days: newDays });
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-amber-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Run Now Button */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-600">
                      Last run: {taskStatus?.ai_insights?.last_run 
                        ? new Date(taskStatus.ai_insights.last_run).toLocaleString() 
                        : 'Never'}
                    </p>
                  </div>
                  <Button
                    onClick={() => runTaskNow('ai_insights')}
                    disabled={runningTask === 'ai_insights'}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  >
                    {runningTask === 'ai_insights' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Generate Now
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* HR Retargeting Schedule */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-purple-100">
                <RefreshCw className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-800">HR Retargeting Automation</h3>
                <p className="text-sm text-slate-500">Automatically send follow-up reminders</p>
              </div>
              <Switch
                checked={settings?.hr_retargeting_schedule_enabled || false}
                onCheckedChange={(checked) => setSettings({ ...settings, hr_retargeting_schedule_enabled: checked })}
                disabled={!settings?.scheduled_tasks_enabled}
              />
            </div>

            {settings?.hr_retargeting_schedule_enabled && (
              <div className="space-y-4 mt-4 pt-4 border-t">
                {/* Schedule Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700">Run Time (Server Timezone)</Label>
                    <Input
                      type="time"
                      value={settings?.hr_retargeting_schedule_time || "09:00"}
                      onChange={(e) => setSettings({ ...settings, hr_retargeting_schedule_time: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Batch Size (per run)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={settings?.hr_retargeting_batch_size || 10}
                      onChange={(e) => setSettings({ ...settings, hr_retargeting_batch_size: parseInt(e.target.value) || 10 })}
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Max emails to send per run</p>
                  </div>
                </div>

                {/* Schedule Days */}
                <div>
                  <Label className="text-slate-700 mb-2 block">Run on Days</Label>
                  <div className="flex gap-2">
                    {DAYS_OF_WEEK.map(day => {
                      const isSelected = (settings?.hr_retargeting_schedule_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']).includes(day.value);
                      return (
                        <button
                          key={day.value}
                          onClick={() => toggleDay(day.value)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-purple-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Advanced Options */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Advanced Options
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Delay Between Emails (seconds)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="60"
                        value={settings?.hr_retargeting_delay_between_emails || 5}
                        onChange={(e) => setSettings({ ...settings, hr_retargeting_delay_between_emails: parseInt(e.target.value) || 5 })}
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-400 mt-1">Prevents rate limiting</p>
                    </div>
                  </div>
                </div>

                {/* Status & Run Now */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-800">
                        {taskStatus?.hr_retargeting?.pending_count || 0} reminders pending
                      </p>
                      <p className="text-sm text-blue-600">
                        {taskStatus?.hr_retargeting?.total_active || 0} total active retargeting entries
                      </p>
                    </div>
                    <Button
                      onClick={() => runTaskNow('hr_retargeting')}
                      disabled={runningTask === 'hr_retargeting'}
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      {runningTask === 'hr_retargeting' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Run Now
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Task Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Recent Task Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {taskLogs.length > 0 ? (
              <div className="space-y-2">
                {taskLogs.map((log, idx) => (
                  <div 
                    key={log.id || idx}
                    className={`p-3 rounded-lg border ${
                      log.status === 'completed' ? 'bg-green-50 border-green-200' :
                      log.status === 'failed' ? 'bg-red-50 border-red-200' :
                      'bg-amber-50 border-amber-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {log.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-600" />}
                        {log.status === 'failed' && <XCircle className="w-4 h-4 text-red-600" />}
                        {log.status === 'running' && <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />}
                        <span className="font-medium text-slate-800">{log.task_name}</span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {log.started_at ? new Date(log.started_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      Processed: {log.items_processed || 0} | Success: {log.items_succeeded || 0} | Failed: {log.items_failed || 0}
                    </div>
                    {log.errors?.length > 0 && (
                      <p className="text-xs text-red-600 mt-1">Errors: {log.errors.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-400 py-8">No task logs yet</p>
            )}
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="w-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Save Scheduled Task Settings
        </Button>
      </div>
    );
  };


  // HR Retargeting Section
  const HRRetargetingSection = () => (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-100">
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">HR Email Retargeting</h3>
              <p className="text-sm text-slate-500">Automatically follow up with HR when they haven't responded</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-slate-700 font-medium">Enable HR Retargeting</Label>
                <p className="text-sm text-slate-500">Automatically send reminder emails to HR contacts</p>
              </div>
              <Switch
                checked={settings?.hr_retargeting_enabled || false}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, hr_retargeting_enabled: checked }))}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-700">1st Reminder (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={settings?.hr_retargeting_intervals?.[0] || 3}
                  onChange={(e) => {
                    const intervals = [...(settings?.hr_retargeting_intervals || [3, 6, 15])];
                    intervals[0] = parseInt(e.target.value) || 3;
                    setSettings(prev => ({ ...prev, hr_retargeting_intervals: intervals }));
                  }}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">After initial share</p>
              </div>
              <div>
                <Label className="text-slate-700">2nd Reminder (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={settings?.hr_retargeting_intervals?.[1] || 6}
                  onChange={(e) => {
                    const intervals = [...(settings?.hr_retargeting_intervals || [3, 6, 15])];
                    intervals[1] = parseInt(e.target.value) || 6;
                    setSettings(prev => ({ ...prev, hr_retargeting_intervals: intervals }));
                  }}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">After 1st reminder</p>
              </div>
              <div>
                <Label className="text-slate-700">3rd Reminder (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={settings?.hr_retargeting_intervals?.[2] || 15}
                  onChange={(e) => {
                    const intervals = [...(settings?.hr_retargeting_intervals || [3, 6, 15])];
                    intervals[2] = parseInt(e.target.value) || 15;
                    setSettings(prev => ({ ...prev, hr_retargeting_intervals: intervals }));
                  }}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">After 2nd reminder</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-slate-700">Max Reminder Cycles</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={settings?.hr_retargeting_max_cycles || 1}
                  onChange={(e) => setSettings(prev => ({ ...prev, hr_retargeting_max_cycles: parseInt(e.target.value) || 1 }))}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Number of complete 3-reminder cycles before asking admin</p>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-700 font-medium">Auto-Continue</Label>
                  <p className="text-sm text-slate-500">Auto-start new cycle without admin approval</p>
                </div>
                <Switch
                  checked={settings?.hr_retargeting_auto_continue || false}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, hr_retargeting_auto_continue: checked }))}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">How it works:</p>
                <ul className="text-sm text-amber-700 mt-1 space-y-1">
                  <li>• When you share a candidate profile with HR, retargeting is automatically enabled</li>
                  <li>• Reminders are sent at the configured intervals (e.g., 3, 6, 15 days)</li>
                  <li>• After 3 reminders, the system asks you to continue or cancel</li>
                  <li>• If HR responds (views/accepts/rejects), reminders stop automatically</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
        Save HR Retargeting Settings
      </Button>
    </div>
  );


  // Deal Follow-up Reminders Section (Admin only)
  const DealRemindersSection = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const [dealSettings, setDealSettings] = useState({
      enabled: true,
      interval_hours: 2,
      quiet_hours_start: 21,
      quiet_hours_end: 8,
      max_reminders: 0
    });
    const [loadingDealSettings, setLoadingDealSettings] = useState(true);
    const [savingDealSettings, setSavingDealSettings] = useState(false);
    
    useEffect(() => {
      const loadDealSettings = async () => {
        try {
          const token = localStorage.getItem('ats_token');
          const res = await fetch(`${API_URL}/api/settings/deal-reminders`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setDealSettings(data);
          }
        } catch (e) {
          console.error('Failed to load deal settings:', e);
        }
        setLoadingDealSettings(false);
      };
      loadDealSettings();
    }, []);
    
    const saveDealSettings = async () => {
      setSavingDealSettings(true);
      try {
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/settings/deal-reminders`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dealSettings)
        });
        if (res.ok) {
          toast.success('Deal reminder settings saved');
        } else {
          toast.error('Failed to save settings');
        }
      } catch (e) {
        toast.error('Failed to save settings');
      }
      setSavingDealSettings(false);
    };
    
    if (!isAdmin) {
      return (
        <Card>
          <CardContent className="p-6 text-center text-slate-500">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Admin access required to view this section</p>
          </CardContent>
        </Card>
      );
    }
    
    if (loadingDealSettings) {
      return (
        <Card>
          <CardContent className="p-6 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </CardContent>
        </Card>
      );
    }
    
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-amber-100">
                <Bell className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Proposal Follow-up Reminders</h3>
                <p className="text-sm text-slate-500">Configure automatic follow-up reminders for proposal deals</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-700 font-medium">Send Follow-up Reminders</Label>
                  <p className="text-sm text-slate-500">Automatically remind senders to follow up on open deals</p>
                </div>
                <Switch
                  checked={dealSettings.enabled}
                  onCheckedChange={(checked) => setDealSettings({ ...dealSettings, enabled: checked })}
                />
              </div>
              
              {dealSettings.enabled && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Reminder Interval (hours)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="72"
                        value={dealSettings.interval_hours}
                        onChange={(e) => setDealSettings({ ...dealSettings, interval_hours: parseInt(e.target.value) || 2 })}
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">Time between reminders (1-72)</p>
                    </div>
                    <div>
                      <Label className="text-slate-700">Max Reminders per Deal</Label>
                      <Input
                        type="number"
                        min="0"
                        value={dealSettings.max_reminders}
                        onChange={(e) => setDealSettings({ ...dealSettings, max_reminders: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">0 = unlimited</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Quiet Hours Start (UTC)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        value={dealSettings.quiet_hours_start}
                        onChange={(e) => setDealSettings({ ...dealSettings, quiet_hours_start: parseInt(e.target.value) || 21 })}
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">No reminders after this hour</p>
                    </div>
                    <div>
                      <Label className="text-slate-700">Quiet Hours End (UTC)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        value={dealSettings.quiet_hours_end}
                        onChange={(e) => setDealSettings({ ...dealSettings, quiet_hours_end: parseInt(e.target.value) || 8 })}
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">Reminders resume after this hour</p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> Set quiet hours start = end to disable quiet hours completely.
                    </p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Button onClick={saveDealSettings} disabled={savingDealSettings} className="w-full">
          {savingDealSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Reminder Settings
        </Button>
      </div>
    );
  };


  // Profile & Email Signature Section (All users can edit their own)
  const ProfileSignatureSection = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const [profileData, setProfileData] = useState({
      name: '',
      designation: '',
      mobile: '',
      email: ''
    });
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [savingProfile, setSavingProfile] = useState(false);
    
    useEffect(() => {
      const loadProfile = async () => {
        try {
          const token = localStorage.getItem('ats_token');
          const res = await fetch(`${API_URL}/api/users/me/profile-signature`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setProfileData(data);
          }
        } catch (e) {
          console.error('Failed to load profile:', e);
        }
        setLoadingProfile(false);
      };
      loadProfile();
    }, []);
    
    const saveProfile = async () => {
      setSavingProfile(true);
      try {
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/users/me/profile-signature`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            designation: profileData.designation,
            mobile: profileData.mobile
          })
        });
        if (res.ok) {
          toast.success('Profile signature saved');
        } else {
          toast.error('Failed to save profile');
        }
      } catch (e) {
        toast.error('Failed to save profile');
      }
      setSavingProfile(false);
    };
    
    if (loadingProfile) {
      return (
        <Card>
          <CardContent className="p-6 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </CardContent>
        </Card>
      );
    }
    
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-emerald-100">
                <User className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Profile & Email Signature</h3>
                <p className="text-sm text-slate-500">Your details for proposal emails and client communications</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700">Full Name</Label>
                <Input
                  value={profileData.name}
                  disabled
                  className="mt-1 bg-slate-100"
                />
                <p className="text-xs text-slate-500 mt-1">Contact admin to change your name</p>
              </div>
              
              <div>
                <Label className="text-slate-700">Email</Label>
                <Input
                  value={profileData.email}
                  disabled
                  className="mt-1 bg-slate-100"
                />
              </div>
              
              <div>
                <Label className="text-slate-700">Designation</Label>
                <Input
                  value={profileData.designation}
                  onChange={(e) => setProfileData({ ...profileData, designation: e.target.value })}
                  placeholder="e.g., Managing Partner, Senior Recruiter"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Appears in your email signature</p>
              </div>
              
              <div>
                <Label className="text-slate-700">Mobile</Label>
                <Input
                  value={profileData.mobile}
                  onChange={(e) => setProfileData({ ...profileData, mobile: e.target.value })}
                  placeholder="e.g., +91 9167896969"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Appears in your email signature</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg border">
                <Label className="text-slate-700 mb-2 block">Signature Preview</Label>
                <div className="text-sm text-slate-600 whitespace-pre-line font-mono bg-white p-3 rounded border">
{`Thanks & Regards,
${profileData.name || '[Your Name]'}
${profileData.designation || '[Designation]'}
Daruwalla Consultancy Services
Email: care@daruwallas.com
Mobile: ${profileData.mobile || '[Mobile]'}`}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Button onClick={saveProfile} disabled={savingProfile} className="w-full">
          {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Profile Signature
        </Button>
      </div>
    );
  };


  // Recruiting Discipline Section (Admin only)
  const RecruitingDisciplineSection = () => {
    const [slaSettings, setSlaSettings] = useState({
      new_to_screened_hours: 24,
      screened_to_submitted_hours: 48,
      client_followup_hours: 24,
      interview_outcome_hours: 4,
      submission_target_per_requirement: 3,
      escalation_enabled: true,
      escalation_digest_hour: 8,
      daily_target_cvs: 10,
      daily_target_submissions: 5,
      daily_target_followups: 8,
      hard_gates_enabled: true,
    });
    const [loadingSla, setLoadingSla] = useState(true);
    const [savingSla, setSavingSla] = useState(false);
    
    useEffect(() => {
      const loadSlaSettings = async () => {
        try {
          const token = localStorage.getItem('ats_token');
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const res = await fetch(`${API_URL}/api/settings/recruiting-discipline`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setSlaSettings(data);
          }
        } catch (e) {
          console.error('Failed to load SLA settings:', e);
        }
        setLoadingSla(false);
      };
      loadSlaSettings();
    }, []);
    
    const saveSlaSettings = async () => {
      setSavingSla(true);
      try {
        const token = localStorage.getItem('ats_token');
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API_URL}/api/settings/recruiting-discipline`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(slaSettings)
        });
        if (res.ok) {
          toast.success('Recruiting discipline settings saved');
        } else {
          toast.error('Failed to save settings');
        }
      } catch (e) {
        toast.error('Failed to save settings');
      }
      setSavingSla(false);
    };
    
    if (!isAdmin) {
      return (
        <Card>
          <CardContent className="p-6 text-center text-slate-500">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Admin access required to view this section</p>
          </CardContent>
        </Card>
      );
    }
    
    if (loadingSla) {
      return (
        <Card>
          <CardContent className="p-6 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </CardContent>
        </Card>
      );
    }
    
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-indigo-100">
                <Target className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Recruiting Discipline</h3>
                <p className="text-sm text-slate-500">SLA thresholds and pipeline rules for recruiters</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium text-slate-700 border-b pb-2">SLA Thresholds</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">New → Screened (hours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={slaSettings.new_to_screened_hours}
                    onChange={(e) => setSlaSettings({ ...slaSettings, new_to_screened_hours: parseInt(e.target.value) || 24 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Time to screen new candidates</p>
                </div>
                <div>
                  <Label className="text-slate-700">Screened → Submitted (hours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={slaSettings.screened_to_submitted_hours}
                    onChange={(e) => setSlaSettings({ ...slaSettings, screened_to_submitted_hours: parseInt(e.target.value) || 48 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Time to submit after screening</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">Client Follow-up (hours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={slaSettings.client_followup_hours}
                    onChange={(e) => setSlaSettings({ ...slaSettings, client_followup_hours: parseInt(e.target.value) || 24 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Follow up with client every N hours</p>
                </div>
                <div>
                  <Label className="text-slate-700">Interview Outcome (hours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={slaSettings.interview_outcome_hours}
                    onChange={(e) => setSlaSettings({ ...slaSettings, interview_outcome_hours: parseInt(e.target.value) || 4 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Time to record outcome after interview</p>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700">Submission Target per Requirement</Label>
                <Input
                  type="number"
                  min="1"
                  value={slaSettings.submission_target_per_requirement}
                  onChange={(e) => setSlaSettings({ ...slaSettings, submission_target_per_requirement: parseInt(e.target.value) || 3 })}
                  className="mt-1 w-32"
                />
                <p className="text-xs text-slate-500 mt-1">Minimum candidates to submit before deadline</p>
              </div>
              
              <h4 className="font-medium text-slate-700 border-b pb-2 mt-6">Escalation</h4>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-700 font-medium">Enable Escalation Digest</Label>
                  <p className="text-sm text-slate-500">Send daily digest of items overdue &gt; 48h to admin</p>
                </div>
                <Switch
                  checked={slaSettings.escalation_enabled}
                  onCheckedChange={(checked) => setSlaSettings({ ...slaSettings, escalation_enabled: checked })}
                />
              </div>
              
              {slaSettings.escalation_enabled && (
                <div>
                  <Label className="text-slate-700">Digest Hour (UTC)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={slaSettings.escalation_digest_hour}
                    onChange={(e) => setSlaSettings({ ...slaSettings, escalation_digest_hour: parseInt(e.target.value) || 8 })}
                    className="mt-1 w-24"
                  />
                  <p className="text-xs text-slate-500 mt-1">Hour to send escalation digest (0-23 UTC, 8 UTC = 1:30 PM IST)</p>
                </div>
              )}
              
              <h4 className="font-medium text-slate-700 border-b pb-2 mt-6">Daily Recruiter Targets</h4>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-700">CVs Uploaded</Label>
                  <Input
                    type="number"
                    min="1"
                    value={slaSettings.daily_target_cvs}
                    onChange={(e) => setSlaSettings({ ...slaSettings, daily_target_cvs: parseInt(e.target.value) || 10 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Per day target</p>
                </div>
                <div>
                  <Label className="text-slate-700">Submissions</Label>
                  <Input
                    type="number"
                    min="1"
                    value={slaSettings.daily_target_submissions}
                    onChange={(e) => setSlaSettings({ ...slaSettings, daily_target_submissions: parseInt(e.target.value) || 5 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Per day target</p>
                </div>
                <div>
                  <Label className="text-slate-700">Client Follow-ups</Label>
                  <Input
                    type="number"
                    min="1"
                    value={slaSettings.daily_target_followups}
                    onChange={(e) => setSlaSettings({ ...slaSettings, daily_target_followups: parseInt(e.target.value) || 8 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Per day target</p>
                </div>
              </div>
              
              <h4 className="font-medium text-slate-700 border-b pb-2 mt-6">Hard Gates</h4>
              
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <Label className="text-slate-700 font-medium">Enable Hard Gates</Label>
                  <p className="text-sm text-slate-500">Block stage transitions until prerequisites are met</p>
                  <p className="text-xs text-amber-700 mt-1">
                    When enabled: CV + Dossier required for Shortlist, Submission email required for Interview scheduling
                  </p>
                </div>
                <Switch
                  checked={slaSettings.hard_gates_enabled}
                  onCheckedChange={(checked) => setSlaSettings({ ...slaSettings, hard_gates_enabled: checked })}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Button onClick={saveSlaSettings} disabled={savingSla} className="w-full">
          {savingSla ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Recruiting Discipline Settings
        </Button>
      </div>
    );
  };


  // System Logs Section
  const LogsSection = () => {
    const getLevelColor = (level) => {
      switch (level) {
        case 'critical': return 'bg-red-100 text-red-700 border-red-200';
        case 'error': return 'bg-orange-100 text-orange-700 border-orange-200';
        case 'warning': return 'bg-amber-100 text-amber-700 border-amber-200';
        default: return 'bg-blue-100 text-blue-700 border-blue-200';
      }
    };

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">System Logs & Error Monitoring</h3>
          <p className="text-sm text-slate-500">Monitor errors, warnings, and receive periodic notifications</p>
        </div>

        {/* Log Notification Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4 text-blue-600" />
              Log Notification Settings
            </CardTitle>
            <CardDescription>Get notified about system errors periodically</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <div className="font-medium">Enable Log Notifications</div>
                <div className="text-sm text-slate-500">Receive email alerts about system errors</div>
              </div>
              <Switch
                checked={logSettings.enabled}
                onCheckedChange={(checked) => setLogSettings({ ...logSettings, enabled: checked })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Notification Email</Label>
                <Input
                  type="email"
                  value={logSettings.email}
                  onChange={(e) => setLogSettings({ ...logSettings, email: e.target.value })}
                  placeholder="admin@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Notification Interval (Days)</Label>
                <Select 
                  value={String(logSettings.interval_days)} 
                  onValueChange={(v) => setLogSettings({ ...logSettings, interval_days: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Daily</SelectItem>
                    <SelectItem value="3">Every 3 Days</SelectItem>
                    <SelectItem value="7">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={saveLogSettings}>
              <Save className="w-4 h-4 mr-2" />
              Save Notification Settings
            </Button>
          </CardContent>
        </Card>

        {/* Log Summary */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-700">{logsSummary.critical}</p>
              <p className="text-sm text-red-600">Critical</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-700">{logsSummary.error}</p>
              <p className="text-sm text-orange-600">Errors</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{logsSummary.warning}</p>
              <p className="text-sm text-amber-600">Warnings</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{logsSummary.unresolved}</p>
              <p className="text-sm text-blue-600">Unresolved</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button onClick={downloadLogs} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Download Log Dump
          </Button>
          <Button onClick={copyLogsToClipboard} variant="outline">
            <Copy className="w-4 h-4 mr-2" />
            Copy to Clipboard
          </Button>
          <Button onClick={loadSystemLogs} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Logs List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent System Logs</CardTitle>
          </CardHeader>
          <CardContent>
            {logsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : systemLogs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p>No system logs found. Everything is running smoothly!</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {systemLogs.map((log) => (
                  <div 
                    key={log.id} 
                    className={`p-3 rounded-lg border ${log.resolved ? 'opacity-50' : ''} ${getLevelColor(log.level)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase">{log.level}</span>
                          <span className="text-xs opacity-70">{log.category}</span>
                          <span className="text-xs opacity-50">
                            {new Date(log.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="font-medium mt-1">{log.message}</p>
                        {log.details && (
                          <p className="text-sm opacity-80 mt-1">{log.details}</p>
                        )}
                        {log.endpoint && (
                          <p className="text-xs opacity-60 mt-1">Endpoint: {log.endpoint}</p>
                        )}
                      </div>
                      {!log.resolved && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => resolveLog(log.id)}
                          className="text-green-700 hover:bg-green-100"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {log.resolved && (
                      <p className="text-xs mt-2 opacity-60">
                        Resolved by {log.resolved_by} on {new Date(log.resolved_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Junk Data Cleanup Component
  const JunkDataCleanupSection = () => {
    const [junkStats, setJunkStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cleaning, setCleaning] = useState(false);

    const loadJunkStats = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/data/junk-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setJunkStats(data);
        }
      } catch (e) {
        console.error('Error loading junk stats:', e);
      }
      setLoading(false);
    };

    const handleCleanup = async () => {
      if (!window.confirm('Are you sure you want to remove all junk data? This action cannot be undone.')) return;
      
      setCleaning(true);
      try {
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/data/cleanup-junk`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          toast.success(data.message);
          loadJunkStats(); // Reload stats
        } else {
          toast.error('Failed to cleanup junk data');
        }
      } catch (e) {
        toast.error('Error during cleanup');
      }
      setCleaning(false);
    };

    useEffect(() => {
      loadJunkStats();
    }, []);

    return (
      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <h4 className="font-medium text-slate-800">Junk Data Cleanup</h4>
            <p className="text-sm text-slate-500 mt-1">
              Remove blank companies, short/invalid skills, titles, and other junk data to keep your system clean.
            </p>
            {junkStats && (
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white p-2 rounded text-center">
                  <div className="font-bold text-red-600">{junkStats.blank_companies}</div>
                  <div className="text-slate-500">Blank Companies</div>
                </div>
                <div className="bg-white p-2 rounded text-center">
                  <div className="font-bold text-red-600">{junkStats.short_skills}</div>
                  <div className="text-slate-500">Invalid Skills</div>
                </div>
                <div className="bg-white p-2 rounded text-center">
                  <div className="font-bold text-red-600">{junkStats.candidates_without_name}</div>
                  <div className="text-slate-500">No-Name Candidates</div>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button 
              onClick={loadJunkStats}
              disabled={loading}
              variant="outline"
              size="sm"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check Stats'}
            </Button>
            <Button 
              onClick={handleCleanup}
              disabled={cleaning || !junkStats || junkStats.total_potential_junk === 0}
              variant="destructive"
              size="sm"
            >
              {cleaning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Clean {junkStats?.total_potential_junk || 0} Items
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // System Section (Cache, Maintenance)
  const SystemSection = () => {
    const [clearing, setClearing] = useState(false);
    const [assigningCodes, setAssigningCodes] = useState(false);

    const handleClearCache = async () => {
      setClearing(true);
      try {
        // Clear localStorage
        const token = localStorage.getItem('ats_token');
        const user = localStorage.getItem('user');
        localStorage.clear();
        // Restore auth
        if (token) localStorage.setItem('token', token);
        if (user) localStorage.setItem('user', user);
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        // Clear caches if available
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }

        toast.success('Cache cleared successfully!');
        
        // Reload after short delay
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        toast.error('Failed to clear cache');
        setClearing(false);
      }
    };

    const handleAutoAssignCompanyCodes = async () => {
      setAssigningCodes(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${API_URL}/api/companies/auto-assign-codes`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          toast.success(data.message || 'Company codes assigned successfully');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to assign company codes');
        }
      } catch (error) {
        toast.error('Failed to assign company codes');
      } finally {
        setAssigningCodes(false);
      }
    };

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">System Maintenance</h3>
          <p className="text-sm text-slate-500">Cache management and application maintenance</p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-slate-600" />
              Cache Management
            </CardTitle>
            <CardDescription>Clear cached data to free up space or fix display issues</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-slate-800">Clear Cache & Reload</h4>
                  <p className="text-sm text-slate-500 mt-1">
                    Clears browser cache, local storage (except login), and reloads the application.
                    Use this if you're experiencing display issues or stale data.
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleClearCache} 
                disabled={clearing}
                variant="outline"
                className="mt-4"
              >
                {clearing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {clearing ? 'Clearing & Reloading...' : 'Clear Cache & Reload'}
              </Button>
            </div>

            <div className="p-4 bg-slate-50 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-slate-800">Force Hard Reload</h4>
                  <p className="text-sm text-slate-500 mt-1">
                    Bypass browser cache and reload all resources from server.
                  </p>
                </div>
                <Button 
                  onClick={() => window.location.reload(true)}
                  variant="outline"
                  size="sm"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Hard Reload
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              Data Maintenance
            </CardTitle>
            <CardDescription>One-time operations to fix or migrate data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h4 className="font-medium text-slate-800">Auto-Assign Company Codes</h4>
                  <p className="text-sm text-slate-500 mt-1">
                    Generate unique codes for any companies that are missing them. 
                    This is a one-time operation and won't affect companies that already have codes.
                  </p>
                </div>
                <Button 
                  onClick={handleAutoAssignCompanyCodes}
                  disabled={assigningCodes}
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0"
                >
                  {assigningCodes ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Building2 className="w-4 h-4 mr-2" />
                  )}
                  {assigningCodes ? 'Assigning...' : 'Assign Codes'}
                </Button>
              </div>
            </div>
            
            {/* Junk Data Cleanup Section */}
            <JunkDataCleanupSection />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="w-5 h-5 text-slate-600" />
              Storage Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-slate-800">
                  {(JSON.stringify(localStorage).length / 1024).toFixed(1)} KB
                </div>
                <div className="text-sm text-slate-500">Local Storage</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-slate-800">
                  {(JSON.stringify(sessionStorage).length / 1024).toFixed(1)} KB
                </div>
                <div className="text-sm text-slate-500">Session Storage</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-slate-800">
                  {navigator.cookieEnabled ? 'Enabled' : 'Disabled'}
                </div>
                <div className="text-sm text-slate-500">Cookies</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Page Settings Section - SEO and Branding
  const PageSettingsSection = () => {
    // WhatsApp Preview Component
    const WhatsAppPreview = ({ title, description, image, url }) => (
      <div className="mt-3 max-w-sm">
        <p className="text-xs font-medium text-slate-500 mb-1.5">WhatsApp / Social Media Preview</p>
        <div className="border rounded-lg overflow-hidden bg-[#e9e6df] shadow-sm">
          {image && (
            <div className="w-full h-32 bg-slate-200 flex items-center justify-center overflow-hidden">
              <img src={image} alt="OG Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; }} />
            </div>
          )}
          <div className="p-3 bg-[#f0ede6]">
            <p className="text-xs text-[#8c8c8c] uppercase tracking-wide">{url || 'daruwallas.com'}</p>
            <p className="text-sm font-semibold text-[#1d1d1d] mt-0.5 line-clamp-2">{title || 'Page Title'}</p>
            <p className="text-xs text-[#6b6b6b] mt-0.5 line-clamp-2">{description || 'Page description will appear here'}</p>
          </div>
        </div>
      </div>
    );

    return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-800">Page Settings</h3>
        <p className="text-sm text-slate-500">Configure SEO metadata, titles, and social media previews for all pages</p>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-600" />
            Home Page (daruwallas.com)
          </CardTitle>
          <CardDescription>Main landing page - this is what shows when you share your domain link on WhatsApp</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Page Title <span className="text-xs text-slate-400">(browser tab)</span></Label>
                <Input
                  value={settings?.home_page_title || ''}
                  onChange={(e) => setSettings({ ...settings, home_page_title: e.target.value })}
                  placeholder="DCS ATS - AI-Powered Talent Management"
                  data-testid="home-page-title"
                />
              </div>
              <div className="space-y-2">
                <Label>OG Title <span className="text-xs text-slate-400">(WhatsApp / social preview title)</span></Label>
                <Input
                  value={settings?.home_og_title || ''}
                  onChange={(e) => setSettings({ ...settings, home_og_title: e.target.value })}
                  placeholder="DCS ATS | AI-Powered Applicant Tracking"
                  data-testid="home-og-title"
                />
              </div>
              <div className="space-y-2">
                <Label>OG Description <span className="text-xs text-slate-400">(WhatsApp / social preview text)</span></Label>
                <Textarea
                  value={settings?.home_og_description || ''}
                  onChange={(e) => setSettings({ ...settings, home_og_description: e.target.value })}
                  placeholder="AI-Powered Talent Management & Applicant Tracking by Daruwalla Consultancy Services"
                  rows={2}
                  data-testid="home-og-description"
                />
              </div>
              <div className="space-y-2">
                <Label>OG Image URL <span className="text-xs text-slate-400">(1200x630px recommended)</span></Label>
                <Input
                  value={settings?.home_og_image || ''}
                  onChange={(e) => setSettings({ ...settings, home_og_image: e.target.value })}
                  placeholder="https://daruwallas.com/og-banner.png"
                  data-testid="home-og-image"
                />
              </div>
              <div className="space-y-2">
                <Label>Meta Description <span className="text-xs text-slate-400">(Google search result)</span></Label>
                <Textarea
                  value={settings?.home_page_description || ''}
                  onChange={(e) => setSettings({ ...settings, home_page_description: e.target.value })}
                  placeholder="Upload your CV and get matched with the best opportunities."
                  rows={2}
                  data-testid="home-page-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Meta Keywords</Label>
                <Input
                  value={settings?.home_page_keywords || ''}
                  onChange={(e) => setSettings({ ...settings, home_page_keywords: e.target.value })}
                  placeholder="jobs, recruitment, CV, resume, career, hiring"
                  data-testid="home-page-keywords"
                />
              </div>
            </div>
            <div>
              <WhatsAppPreview
                title={settings?.home_og_title || settings?.home_page_title || 'DCS ATS | AI-Powered Applicant Tracking'}
                description={settings?.home_og_description || settings?.home_page_description || 'AI-Powered Talent Management'}
                image={settings?.home_og_image}
                url="daruwallas.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4 text-green-600" />
            Candidate Portal (/my)
          </CardTitle>
          <CardDescription>Candidate login & dashboard - shown when portal link is shared</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Page Title</Label>
                <Input
                  value={settings?.portal_page_title || ''}
                  onChange={(e) => setSettings({ ...settings, portal_page_title: e.target.value })}
                  placeholder="Candidate Portal - DCS ATS"
                  data-testid="portal-page-title"
                />
              </div>
              <div className="space-y-2">
                <Label>OG Title <span className="text-xs text-slate-400">(WhatsApp preview)</span></Label>
                <Input
                  value={settings?.portal_og_title || ''}
                  onChange={(e) => setSettings({ ...settings, portal_og_title: e.target.value })}
                  placeholder="Candidate Portal - DCS ATS"
                  data-testid="portal-og-title"
                />
              </div>
              <div className="space-y-2">
                <Label>OG Description <span className="text-xs text-slate-400">(WhatsApp preview)</span></Label>
                <Textarea
                  value={settings?.portal_og_description || ''}
                  onChange={(e) => setSettings({ ...settings, portal_og_description: e.target.value })}
                  placeholder="Access your profile, view job matches, and manage your career journey."
                  rows={2}
                  data-testid="portal-og-description"
                />
              </div>
              <div className="space-y-2">
                <Label>OG Image URL</Label>
                <Input
                  value={settings?.portal_og_image || ''}
                  onChange={(e) => setSettings({ ...settings, portal_og_image: e.target.value })}
                  placeholder="https://daruwallas.com/portal-banner.png"
                  data-testid="portal-og-image"
                />
              </div>
              <div className="space-y-2">
                <Label>Meta Description</Label>
                <Textarea
                  value={settings?.portal_page_description || ''}
                  onChange={(e) => setSettings({ ...settings, portal_page_description: e.target.value })}
                  placeholder="Access your profile, view job matches, and manage your career journey."
                  rows={2}
                  data-testid="portal-page-description"
                />
              </div>
            </div>
            <div>
              <WhatsAppPreview
                title={settings?.portal_og_title || settings?.portal_page_title || 'Candidate Portal - DCS ATS'}
                description={settings?.portal_og_description || settings?.portal_page_description || 'Access your profile and job matches'}
                image={settings?.portal_og_image || settings?.home_og_image}
                url="daruwallas.com/my"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="w-4 h-4 text-purple-600" />
            Public Profiles (/{'{'}username{'}'})
          </CardTitle>
          <CardDescription>Default settings for candidate public profiles shared with HR</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title Format</Label>
                <Input
                  value={settings?.public_profile_title_format || ''}
                  onChange={(e) => setSettings({ ...settings, public_profile_title_format: e.target.value })}
                  placeholder="{name} - {designation} | DCS ATS"
                  data-testid="public-profile-title-format"
                />
                <p className="text-xs text-slate-500">Available: {'{'}name{'}'}, {'{'}designation{'}'}, {'{'}company{'}'}, {'{'}location{'}'}</p>
              </div>
              <div className="space-y-2">
                <Label>Default OG Image URL</Label>
                <Input
                  value={settings?.default_og_image || ''}
                  onChange={(e) => setSettings({ ...settings, default_og_image: e.target.value })}
                  placeholder="https://daruwallas.com/og-profile.png"
                  data-testid="default-og-image"
                />
                <p className="text-xs text-slate-500">Used when candidate has no profile picture</p>
              </div>
            </div>
            <div>
              <WhatsAppPreview
                title="John Doe - Software Engineer | DCS ATS"
                description="View candidate profile and skills"
                image={settings?.default_og_image || settings?.home_og_image}
                url="daruwallas.com/john-doe-abc123"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-orange-600" />
            HR Review Pages
          </CardTitle>
          <CardDescription>Settings for HR review links shared with company contacts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>OG Title</Label>
                <Input
                  value={settings?.hr_review_og_title || ''}
                  onChange={(e) => setSettings({ ...settings, hr_review_og_title: e.target.value })}
                  placeholder="Candidate Profile for Review - DCS ATS"
                  data-testid="hr-review-og-title"
                />
              </div>
              <div className="space-y-2">
                <Label>OG Description</Label>
                <Textarea
                  value={settings?.hr_review_og_description || ''}
                  onChange={(e) => setSettings({ ...settings, hr_review_og_description: e.target.value })}
                  placeholder="Review this candidate's profile, skills, and experience. Take action to schedule an interview."
                  rows={2}
                  data-testid="hr-review-og-description"
                />
              </div>
              <div className="space-y-2">
                <Label>OG Image URL</Label>
                <Input
                  value={settings?.hr_review_og_image || ''}
                  onChange={(e) => setSettings({ ...settings, hr_review_og_image: e.target.value })}
                  placeholder="https://daruwallas.com/hr-review-banner.png"
                  data-testid="hr-review-og-image"
                />
              </div>
            </div>
            <div>
              <WhatsAppPreview
                title={settings?.hr_review_og_title || 'Candidate Profile for Review - DCS ATS'}
                description={settings?.hr_review_og_description || 'Review this candidate\'s profile, skills, and experience.'}
                image={settings?.hr_review_og_image || settings?.home_og_image}
                url="daruwallas.com/hr/review/abc123"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-amber-600" />
            Branding
          </CardTitle>
          <CardDescription>Logo, company name, and favicon</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Company Name</Label>
            <Input
              value={settings?.company_name || 'Daruwallas Consultancy Services'}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              placeholder="Your Company Name"
              data-testid="company-name"
            />
          </div>
          <div className="space-y-2">
            <Label>Site Name <span className="text-xs text-slate-400">(og:site_name - shows on social media)</span></Label>
            <Input
              value={settings?.og_site_name || ''}
              onChange={(e) => setSettings({ ...settings, og_site_name: e.target.value })}
              placeholder="DCS ATS"
              data-testid="og-site-name"
            />
          </div>
          <div className="space-y-2">
            <Label>Production Domain URL</Label>
            <Input
              value={settings?.production_frontend_url || ''}
              onChange={(e) => setSettings({ ...settings, production_frontend_url: e.target.value })}
              placeholder="https://daruwallas.com"
              data-testid="production-url"
            />
          </div>
          <div className="space-y-2">
            <Label>Logo URL</Label>
            <Input
              value={settings?.company_logo_url || ''}
              onChange={(e) => setSettings({ ...settings, company_logo_url: e.target.value })}
              placeholder="https://daruwallas.com/logo.png"
              data-testid="company-logo-url"
            />
          </div>
          <div className="space-y-2">
            <Label>Favicon URL</Label>
            <Input
              value={settings?.favicon_url || ''}
              onChange={(e) => setSettings({ ...settings, favicon_url: e.target.value })}
              placeholder="https://daruwallas.com/favicon.ico"
              data-testid="favicon-url"
            />
          </div>
        </CardContent>
      </Card>

      {/* HR Signup Page Settings */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-indigo-600" />
            HR Signup Landing Page
          </CardTitle>
          <CardDescription>Configure the public HR signup page at /hr-signup</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Page Title / Tagline</Label>
              <Input
                value={settings?.hr_signup_tagline || 'Connecting Great Companies with Exceptional Talent'}
                onChange={(e) => setSettings({ ...settings, hr_signup_tagline: e.target.value })}
                placeholder="Connecting Great Companies with Exceptional Talent"
                data-testid="hr-signup-tagline"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Email (shown on page)</Label>
              <Input
                value={settings?.hr_signup_contact_email || ''}
                onChange={(e) => setSettings({ ...settings, hr_signup_contact_email: e.target.value })}
                placeholder="info@daruwallas.com"
                data-testid="hr-signup-contact-email"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Page Description</Label>
            <Textarea
              value={settings?.hr_signup_description || "Thank you for your interest. We'd love to learn more about your hiring needs."}
              onChange={(e) => setSettings({ ...settings, hr_signup_description: e.target.value })}
              placeholder="Description shown on the HR signup page"
              rows={3}
              data-testid="hr-signup-description"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Website URL</Label>
              <Input
                value={settings?.hr_signup_website || ''}
                onChange={(e) => setSettings({ ...settings, hr_signup_website: e.target.value })}
                placeholder="https://daruwallas.com"
                data-testid="hr-signup-website"
              />
            </div>
            <div className="space-y-2">
              <Label>LinkedIn URL</Label>
              <Input
                value={settings?.hr_signup_linkedin || ''}
                onChange={(e) => setSettings({ ...settings, hr_signup_linkedin: e.target.value })}
                placeholder="https://linkedin.com/company/daruwallas"
                data-testid="hr-signup-linkedin"
              />
            </div>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Page URL:</strong>{' '}
              <a 
                href={`${settings?.production_frontend_url || window.location.origin}/hr-signup`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline"
              >
                {settings?.production_frontend_url || window.location.origin}/hr-signup
              </a>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Google Analytics Settings */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-orange-600" />
            Google Analytics
          </CardTitle>
          <CardDescription>Track page views and user behavior across your application</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Google Analytics Measurement ID</Label>
            <div className="flex gap-2">
              <Input
                value={settings?.google_analytics_id || ''}
                onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
                placeholder="G-XXXXXXXXXX"
                className="flex-1"
                data-testid="google-analytics-id"
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://analytics.google.com/', '_blank')}
              >
                Get ID
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Find your Measurement ID in Google Analytics: Admin → Property Settings → Data Streams → Your Stream
            </p>
          </div>
          {settings?.google_analytics_id && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Google Analytics is configured. Tracking will be active on page load.
              </p>
            </div>
          )}
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Note:</strong> After saving, the Google Analytics script will be automatically added to all pages. 
              No manual code changes required.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          data-testid="save-page-settings"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Page Settings
            </>
          )}
        </Button>
        <p className="text-xs text-slate-500">After saving, deploy to production for WhatsApp preview changes to take effect</p>
      </div>
    </div>
    );
  };

  // API Usage Section
  // Page Analytics Section
  const PageAnalyticsSection = () => {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);

    useEffect(() => {
      loadAnalytics();
    }, [days]);

    const loadAnalytics = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/analytics/summary?days=${days}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAnalyticsData(data);
        }
      } catch (e) {
        console.error('Failed to load analytics:', e);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-1">Page Analytics</h3>
            <p className="text-sm text-slate-500">Track page views and visitor activity across your application</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadAnalytics}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : analyticsData ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-3xl font-bold text-slate-800">{analyticsData.total_views?.toLocaleString() || 0}</p>
                  <p className="text-sm text-slate-500">Total Page Views</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <p className="text-3xl font-bold text-green-600">{analyticsData.unique_visitors?.toLocaleString() || 0}</p>
                  <p className="text-sm text-slate-500">Unique Visitors</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="p-4">
                  <p className="text-3xl font-bold text-purple-600">{analyticsData.views_by_page?.length || 0}</p>
                  <p className="text-sm text-slate-500">Pages Tracked</p>
                </CardContent>
              </Card>
            </div>

            {/* Top Pages */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Top Pages by Views</CardTitle>
              </CardHeader>
              <CardContent>
                {analyticsData.views_by_page?.length > 0 ? (
                  <div className="space-y-2">
                    {analyticsData.views_by_page.map((page, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                        <span className="text-sm text-slate-700 truncate max-w-md">{page.page || '/'}</span>
                        <Badge variant="secondary">{page.count?.toLocaleString()} views</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No page view data yet. Views will appear here once visitors start browsing your site.</p>
                )}
              </CardContent>
            </Card>

            {/* Top Referrers */}
            {analyticsData.top_referrers?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Top Referrers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analyticsData.top_referrers.map((ref, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                        <span className="text-sm text-slate-700 truncate max-w-md">{ref.referrer}</span>
                        <Badge variant="outline">{ref.count} visits</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Note:</strong> For more detailed analytics including real-time data, user flows, and conversions, 
                add your Google Analytics ID in the Page Settings section above.
              </p>
            </div>
          </>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <BarChart2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No analytics data available yet.</p>
              <p className="text-sm text-slate-400 mt-2">Page views will be tracked automatically as users visit your application.</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const APIUsageSection = () => {
    const [usageData, setUsageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(30);

    useEffect(() => {
      loadUsage();
    }, [days]);

    const loadUsage = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/api-usage?days=${days}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsageData(data);
        }
      } catch (e) {
        console.error('Failed to load API usage:', e);
      } finally {
        setLoading(false);
      }
    };

    const formatNumber = (n) => n?.toLocaleString() || '0';
    const formatCurrency = (usd, inr) => `$${usd?.toFixed(4) || '0'} (₹${inr?.toFixed(2) || '0'})`;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-1">API Usage & Cost Tracking</h3>
            <p className="text-sm text-slate-500">Monitor token consumption across all AI providers</p>
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : usageData ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50">
                <CardContent className="p-4">
                  <p className="text-xs text-blue-600 font-medium">Total Requests</p>
                  <p className="text-2xl font-bold text-blue-700">{formatNumber(usageData.total_requests)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100/50">
                <CardContent className="p-4">
                  <p className="text-xs text-green-600 font-medium">Total Cost (USD)</p>
                  <p className="text-2xl font-bold text-green-700">${usageData.total_cost_usd?.toFixed(4)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50">
                <CardContent className="p-4">
                  <p className="text-xs text-amber-600 font-medium">Total Cost (INR)</p>
                  <p className="text-2xl font-bold text-amber-700">₹{usageData.total_cost_inr?.toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50">
                <CardContent className="p-4">
                  <p className="text-xs text-purple-600 font-medium">Providers Used</p>
                  <p className="text-2xl font-bold text-purple-700">{Object.keys(usageData.by_provider || {}).length}</p>
                </CardContent>
              </Card>
            </div>

            {/* Usage by Provider */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Usage by Provider</CardTitle>
                <CardDescription>Detailed breakdown of API calls per provider</CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(usageData.by_provider || {}).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">Provider</th>
                          <th className="px-4 py-2 text-right font-medium">Requests</th>
                          <th className="px-4 py-2 text-right font-medium">Input Tokens</th>
                          <th className="px-4 py-2 text-right font-medium">Output Tokens</th>
                          <th className="px-4 py-2 text-right font-medium">Total Tokens</th>
                          <th className="px-4 py-2 text-right font-medium">Cost (USD)</th>
                          <th className="px-4 py-2 text-right font-medium">Cost (INR)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {Object.entries(usageData.by_provider).map(([provider, data]) => (
                          <tr key={provider} className="hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium capitalize">{provider.replace('_', ' ')}</td>
                            <td className="px-4 py-3 text-right">{formatNumber(data.requests)}</td>
                            <td className="px-4 py-3 text-right">{formatNumber(data.input_tokens)}</td>
                            <td className="px-4 py-3 text-right">{formatNumber(data.output_tokens)}</td>
                            <td className="px-4 py-3 text-right font-medium">{formatNumber(data.total_tokens)}</td>
                            <td className="px-4 py-3 text-right text-green-600">${data.cost_usd?.toFixed(4)}</td>
                            <td className="px-4 py-3 text-right text-amber-600">₹{(data.cost_usd * 83)?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No API usage recorded yet</p>
                    <p className="text-xs mt-1">Usage will appear here when you use AI features</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pricing Reference */}
            <Card className="border-slate-200 bg-slate-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Pricing Reference
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div className="p-2 bg-white rounded border">
                    <p className="font-medium text-slate-700">OpenAI GPT-4</p>
                    <p className="text-slate-500">$0.03/$0.06 per 1K tokens</p>
                  </div>
                  <div className="p-2 bg-white rounded border">
                    <p className="font-medium text-slate-700">OpenAI GPT-3.5</p>
                    <p className="text-slate-500">$0.0015/$0.002 per 1K tokens</p>
                  </div>
                  <div className="p-2 bg-white rounded border">
                    <p className="font-medium text-slate-700">Claude</p>
                    <p className="text-slate-500">$0.03/$0.06 per 1K tokens</p>
                  </div>
                  <div className="p-2 bg-white rounded border">
                    <p className="font-medium text-slate-700">Gemini</p>
                    <p className="text-slate-500">$0.001/$0.002 per 1K tokens</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="py-8 text-center">
              <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
              <p className="text-amber-700">Failed to load API usage data</p>
              <Button variant="outline" size="sm" onClick={loadUsage} className="mt-3">
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Danger Zone Section
  const DangerSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-red-600 mb-1">Danger Zone</h3>
        <p className="text-sm text-slate-500">Irreversible actions - proceed with caution</p>
      </div>

      <Card className="border-red-200 bg-red-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-red-700 flex items-center gap-2">
            <Database className="w-4 h-4" />
            Reset Data
          </CardTitle>
          <CardDescription>Permanently delete selected data from the system</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { key: 'reset_candidates', label: 'Candidates' },
              { key: 'reset_jobs', label: 'Jobs' },
              { key: 'reset_matches', label: 'Matches' },
              { key: 'reset_users', label: 'Users (except you)' },
              { key: 'reset_activity', label: 'Activity Logs' },
            ].map((item) => (
              <label key={item.key} className="flex items-center gap-2 p-2 rounded-lg hover:bg-red-100/50 cursor-pointer">
                <Checkbox
                  checked={resetOptions[item.key]}
                  onCheckedChange={(checked) => setResetOptions({ ...resetOptions, [item.key]: checked })}
                />
                <span className="text-sm">{item.label}</span>
              </label>
            ))}
          </div>

          <div className="space-y-2">
            <Label className="text-red-700">Type "RESET" to confirm</Label>
            <Input
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              placeholder="RESET"
              className="border-red-200 max-w-xs"
            />
          </div>

          <Button
            variant="destructive"
            onClick={handleReset}
            disabled={resetConfirm !== 'RESET' || !Object.values(resetOptions).some(v => v)}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Reset Selected Data
          </Button>
        </CardContent>
      </Card>
    </div>
  );


  const EngagementSection = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const [engSettings, setEngSettings] = useState(null);
    const [engLoading, setEngLoading] = useState(true);
    const [engSaving, setEngSaving] = useState(false);

    useEffect(() => {
      const load = async () => {
        try {
          const token = localStorage.getItem('ats_token');
          const res = await fetch(`${API_URL}/api/engagement/settings`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) setEngSettings(await res.json());
        } catch (e) { console.error(e); }
        setEngLoading(false);
      };
      load();
    }, []);

    const saveEngSettings = async () => {
      setEngSaving(true);
      try {
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/engagement/settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(engSettings)
        });
        if (res.ok) toast.success('Engagement settings saved');
        else toast.error('Failed to save');
      } catch (e) { toast.error('Error saving settings'); }
      setEngSaving(false);
    };

    if (engLoading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    if (!engSettings) return <p className="text-red-500">Failed to load engagement settings</p>;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Engagement System</h2>
            <p className="text-sm text-slate-500">Configure unlock gamification, spoof emails, and inactive user alerts</p>
          </div>
          <Button onClick={saveEngSettings} disabled={engSaving} size="sm">
            {engSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>

        {/* LinkedIn Share Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-blue-600" />
              LinkedIn Share-to-Unlock
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Share Interval (days)</Label>
                <Input
                  type="number"
                  value={engSettings.linkedin_share_interval_days || 8}
                  onChange={(e) => setEngSettings({...engSettings, linkedin_share_interval_days: parseInt(e.target.value) || 8})}
                  min={1}
                  max={30}
                />
                <p className="text-xs text-slate-500 mt-1">How often candidates must share on LinkedIn to unlock jobs</p>
              </div>
              <div>
                <Label className="text-sm">Unlock Cooldown (hours)</Label>
                <Input
                  type="number"
                  value={engSettings.unlock_cooldown_hours || 24}
                  onChange={(e) => setEngSettings({...engSettings, unlock_cooldown_hours: parseInt(e.target.value) || 24})}
                  min={1}
                  max={72}
                />
                <p className="text-xs text-slate-500 mt-1">Minimum hours between unlock actions</p>
              </div>
            </div>
            <div>
              <Label className="text-sm">LinkedIn Share Template</Label>
              <Textarea
                value={engSettings.linkedin_share_template || ''}
                onChange={(e) => setEngSettings({...engSettings, linkedin_share_template: e.target.value})}
                rows={4}
                placeholder="Leave empty for auto-generated template with stats and hashtags"
              />
              <p className="text-xs text-slate-500 mt-1">Custom LinkedIn share message. Leave empty for dynamic template.</p>
            </div>
          </CardContent>
        </Card>

        {/* Inactive User Notifications */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-600" />
              Inactive User Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Enable Inactive Notifications</p>
                <p className="text-xs text-slate-500">Send WhatsApp + Email to users who haven't logged in</p>
              </div>
              <Switch
                checked={engSettings.inactive_notification_enabled || false}
                onCheckedChange={(v) => setEngSettings({...engSettings, inactive_notification_enabled: v})}
              />
            </div>
            <div>
              <Label className="text-sm">Inactive Days Threshold</Label>
              <Input
                type="number"
                value={engSettings.inactive_notification_days || 7}
                onChange={(e) => setEngSettings({...engSettings, inactive_notification_days: parseInt(e.target.value) || 7})}
                min={1}
                max={90}
                className="w-32"
              />
              <p className="text-xs text-slate-500 mt-1">WhatsApp template: cooling_period | Email: re-engagement</p>
            </div>
          </CardContent>
        </Card>

        {/* Daily Spoof Profile View Emails */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4 text-purple-600" />
              Daily Profile View Emails
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Enable Spoof Profile View Emails</p>
                <p className="text-xs text-slate-500">REMOVED - these emails were fabricated. This switch no longer does anything.</p>
              </div>
              <Switch
                checked={engSettings.spoof_email_enabled || false}
                onCheckedChange={(v) => setEngSettings({...engSettings, spoof_email_enabled: v})}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-sm">Email Frequency</Label>
                <select
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={engSettings.spoof_email_frequency || 'daily'}
                  onChange={(e) => setEngSettings({...engSettings, spoof_email_frequency: e.target.value})}
                >
                  <option value="daily">Daily</option>
                  <option value="alternate">Every Alternate Day</option>
                  <option value="disabled">Disabled</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">How often to send spoof emails</p>
              </div>
              <div>
                <Label className="text-sm">Gap Between Emails (seconds)</Label>
                <Input
                  type="number"
                  value={engSettings.spoof_email_gap_seconds || 45}
                  onChange={(e) => setEngSettings({...engSettings, spoof_email_gap_seconds: parseInt(e.target.value) || 45})}
                  min={10}
                  max={3600}
                />
                <p className="text-xs text-slate-500 mt-1">Minimum gap between sends</p>
              </div>
              <div>
                <Label className="text-sm">Today's Queue</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-sm">
                    {engSettings.spoof_emails_sent_today || 0} / {engSettings.spoof_emails_queued_today || 0} sent
                  </Badge>
                  {engSettings.spoof_emails_date && (
                    <span className="text-xs text-slate-400">{engSettings.spoof_emails_date}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-xs text-slate-600">
                <strong>How it works:</strong> Every day at 10AM IST, the system picks all candidates with email and sends them a personalized
                "Someone from [Company] has viewed your profile" email. Company names are taken from the ATS companies list plus well-known Indian companies.
                Emails are staggered with the configured gap. Sending stops at 8PM IST.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // JM Import Section Component
  const JMImportSection = () => {
    const [step, setStep] = useState('upload'); // upload, validate, dryrun, import, complete
    const [file, setFile] = useState(null);
    const [validationResult, setValidationResult] = useState(null);
    const [dryRunResult, setDryRunResult] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importStats, setImportStats] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Load current import stats on mount
    useEffect(() => {
      loadImportStats();
    }, []);
    
    const loadImportStats = async () => {
      try {
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/jm/stats?authorization=Bearer%20${encodeURIComponent(token)}`);
        if (response.ok) {
          const data = await response.json();
          setImportStats(data);
        } else {
          console.error('Failed to load import stats:', response.status);
        }
      } catch (err) {
        console.error('Failed to load import stats:', err);
      }
    };
    
    const handleFileSelect = (e) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        if (!selectedFile.name.endsWith('.zip')) {
          toast.error('Please select a ZIP file');
          return;
        }
        // Read file content into memory to avoid stream exhaustion
        const reader = new FileReader();
        reader.onload = (event) => {
          const arrayBuffer = event.target.result;
          // Store both the file metadata and content
          setFile({
            name: selectedFile.name,
            type: selectedFile.type,
            arrayBuffer: arrayBuffer
          });
        };
        reader.onerror = () => {
          toast.error('Failed to read file');
        };
        reader.readAsArrayBuffer(selectedFile);
        
        setValidationResult(null);
        setDryRunResult(null);
        setImportResult(null);
        setError(null);
        setStep('upload');
      }
    };
    
    // Helper to create a fresh File object from stored content
    const createFileFromStored = (storedFile) => {
      if (!storedFile?.arrayBuffer) return null;
      const blob = new Blob([storedFile.arrayBuffer], { type: storedFile.type || 'application/zip' });
      return new File([blob], storedFile.name, { type: storedFile.type || 'application/zip' });
    };
    
    const handleValidate = async () => {
      if (!file?.arrayBuffer) {
        toast.error('Please select a file first');
        return;
      }
      setIsLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('ats_token');
        const formData = new FormData();
        const fileObj = createFileFromStored(file);
        if (!fileObj) {
          throw new Error('Failed to prepare file for upload');
        }
        formData.append('file', fileObj);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/jm/validate?authorization=Bearer ${token}`, {
          method: 'POST',
          body: formData
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Validation failed');
        }
        
        setValidationResult(data);
        setStep(data.is_valid ? 'validate' : 'upload');
        
        if (!data.is_valid) {
          toast.error('Validation failed - see errors below');
        } else {
          toast.success('Validation passed - proceed to dry run');
        }
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    const handleDryRun = async () => {
      if (!file?.arrayBuffer) {
        toast.error('Please select a file first');
        return;
      }
      setIsLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('ats_token');
        const formData = new FormData();
        const fileObj = createFileFromStored(file);
        if (!fileObj) {
          throw new Error('Failed to prepare file for upload');
        }
        formData.append('file', fileObj);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/jm/dry-run?authorization=Bearer ${token}`, {
          method: 'POST',
          body: formData
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Dry run failed');
        }
        
        setDryRunResult(data);
        setStep('dryrun');
        toast.success('Dry run complete - review results before importing');
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    const handleImport = async () => {
      if (!file || !dryRunResult) return;
      
      if (!window.confirm('Are you sure you want to execute the import? This will create records in the database.')) {
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('ats_token');
        const formData = new FormData();
        const fileObj = createFileFromStored(file);
        if (!fileObj) {
          throw new Error('Failed to prepare file for upload');
        }
        formData.append('file', fileObj);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/jm/execute?authorization=Bearer ${token}&dry_run_confirmed=true`, {
          method: 'POST',
          body: formData
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Import failed');
        }
        
        setImportResult(data);
        setStep('complete');
        loadImportStats();
        toast.success('Import completed successfully!');
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    const handleDownloadReport = async () => {
      if (!importResult?.import_id) return;
      
      try {
        const token = localStorage.getItem('ats_token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/jm/report/${importResult.import_id}?authorization=Bearer ${token}`);
        
        if (!response.ok) {
          throw new Error('Failed to download report');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `jm_import_report_${importResult.import_id}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast.error(err.message);
      }
    };
    
    const resetImport = () => {
      setFile(null);
      setValidationResult(null);
      setDryRunResult(null);
      setImportResult(null);
      setError(null);
      setStep('upload');
    };
    
    // Only show for admins
    if (currentUser?.role !== 'admin' && currentUser?.role !== 'superadmin' && currentUser?.role !== 'super_admin') {
      return (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Import from JM
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Shield className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800">Admin access required for data import.</p>
            </div>
          </CardContent>
        </Card>
      );
    }
    
    return (
      <div className="space-y-6">
        {/* Header Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Import from JM (Lovable)
            </CardTitle>
            <CardDescription>
              One-time data migration tool for importing data from the JM Lovable export
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Current Import Stats */}
            {importStats && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Current Imported Data
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div className="bg-white p-2 rounded border">
                    <span className="text-slate-500">Clients:</span>
                    <span className="ml-2 font-medium">{importStats.imported_clients}</span>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <span className="text-slate-500">Requirements:</span>
                    <span className="ml-2 font-medium">{importStats.imported_requirements}</span>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <span className="text-slate-500">Candidates:</span>
                    <span className="ml-2 font-medium">{importStats.imported_candidates}</span>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <span className="text-slate-500">Deals:</span>
                    <span className="ml-2 font-medium">{importStats.imported_deals}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step Indicator */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {['upload', 'validate', 'dryrun', 'import', 'complete'].map((s, i) => (
                <div key={s} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                    step === s ? 'bg-blue-600 text-white' : 
                    ['upload', 'validate', 'dryrun', 'import', 'complete'].indexOf(step) > i ? 'bg-green-500 text-white' : 
                    'bg-slate-200 text-slate-500'
                  }`}>
                    {['upload', 'validate', 'dryrun', 'import', 'complete'].indexOf(step) > i ? '✓' : i + 1}
                  </div>
                  <span className={`ml-2 text-sm whitespace-nowrap ${step === s ? 'font-medium' : 'text-slate-500'}`}>
                    {s === 'upload' ? 'Upload' : s === 'validate' ? 'Validate' : s === 'dryrun' ? 'Dry Run' : s === 'import' ? 'Import' : 'Complete'}
                  </span>
                  {i < 4 && <ChevronRight className="h-4 w-4 mx-2 text-slate-300" />}
                </div>
              ))}
            </div>
            
            {/* Error Display */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div className="text-red-700 text-sm">{error}</div>
              </div>
            )}
            
            {/* Step 1: Upload */}
            {(step === 'upload' || !validationResult) && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept=".zip"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="jm-import-file"
                  />
                  <label htmlFor="jm-import-file" className="cursor-pointer">
                    <Download className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                    <p className="text-lg font-medium text-slate-700">
                      {file ? file.name : 'Drop JM export ZIP here or click to browse'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Expected: jm-export-YYYYMMDD.zip
                    </p>
                  </label>
                </div>
                
                {file && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">{file.name}</span>
                      <span className="text-slate-500 text-sm">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <Button onClick={handleValidate} disabled={isLoading}>
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Validate ZIP
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 2: Validation Results */}
            {validationResult && step === 'validate' && (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${validationResult.is_valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {validationResult.is_valid ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="font-medium">{validationResult.is_valid ? 'Validation Passed' : 'Validation Failed'}</span>
                  </div>
                  
                  {validationResult.errors?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
                      <ul className="list-disc list-inside text-sm text-red-600">
                        {validationResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
                
                {/* Entity Counts */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(validationResult.entity_counts || {}).map(([entity, count]) => (
                    <div key={entity} className="bg-slate-50 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-slate-700">{count}</div>
                      <div className="text-sm text-slate-500 capitalize">{entity.replace('_', ' ')}</div>
                    </div>
                  ))}
                </div>
                
                {/* File Counts */}
                <div className="grid grid-cols-4 gap-3">
                  {Object.entries(validationResult.file_counts || {}).map(([folder, count]) => (
                    <div key={folder} className="bg-slate-50 p-2 rounded text-center text-sm">
                      <span className="font-medium">{count}</span>
                      <span className="text-slate-500 ml-1">{folder}</span>
                    </div>
                  ))}
                </div>
                
                {/* Skip Reasons */}
                {Object.keys(validationResult.skip_reasons || {}).some(k => validationResult.skip_reasons[k]?.length > 0) && (
                  <div className="mt-4">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Records that will be skipped
                    </h4>
                    {Object.entries(validationResult.skip_reasons).map(([entity, reasons]) => (
                      reasons?.length > 0 && (
                        <div key={entity} className="mb-2">
                          <p className="text-sm font-medium capitalize">{entity}:</p>
                          <ul className="text-sm text-slate-600 ml-4">
                            {reasons.slice(0, 5).map((r, i) => (
                              <li key={i}>{r.name || r.id}: {r.reasons?.join(', ')}</li>
                            ))}
                            {reasons.length > 5 && <li className="text-slate-400">...and {reasons.length - 5} more</li>}
                          </ul>
                        </div>
                      )
                    ))}
                  </div>
                )}
                
                <div className="flex gap-3 mt-4">
                  <Button variant="outline" onClick={resetImport}>Start Over</Button>
                  <Button onClick={handleDryRun} disabled={isLoading || !validationResult.is_valid}>
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                    Run Dry Run
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 3: Dry Run Results */}
            {dryRunResult && step === 'dryrun' && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Eye className="h-5 w-5 text-blue-600" />
                    Dry Run Results (No data written)
                  </h4>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-green-100 rounded">
                      <div className="text-2xl font-bold text-green-700">
                        {Object.values(dryRunResult.would_create || {}).reduce((a, b) => a + b, 0)}
                      </div>
                      <div className="text-sm text-green-600">Would Create</div>
                    </div>
                    <div className="text-center p-3 bg-amber-100 rounded">
                      <div className="text-2xl font-bold text-amber-700">
                        {Object.values(dryRunResult.would_merge || {}).reduce((a, b) => a + b, 0)}
                      </div>
                      <div className="text-sm text-amber-600">Would Merge</div>
                    </div>
                    <div className="text-center p-3 bg-slate-100 rounded">
                      <div className="text-2xl font-bold text-slate-700">
                        {Object.values(dryRunResult.would_skip || {}).reduce((a, b) => a + b, 0)}
                      </div>
                      <div className="text-sm text-slate-600">Would Skip</div>
                    </div>
                  </div>
                  
                  {/* Breakdown by entity */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                    {Object.keys(dryRunResult.would_create || {}).map(entity => (
                      <div key={entity} className="flex justify-between bg-white p-2 rounded">
                        <span className="capitalize">{entity.replace('_', ' ')}</span>
                        <span>
                          <span className="text-green-600">+{dryRunResult.would_create[entity] || 0}</span>
                          {' / '}
                          <span className="text-amber-600">~{dryRunResult.would_merge?.[entity] || 0}</span>
                          {' / '}
                          <span className="text-slate-400">-{dryRunResult.would_skip?.[entity] || 0}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* User Mapping */}
                {dryRunResult.user_mapping?.length > 0 && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      User Mapping
                    </h4>
                    <div className="max-h-40 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-slate-500">
                            <th className="p-1">JM User</th>
                            <th className="p-1">ATS Match</th>
                            <th className="p-1">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dryRunResult.user_mapping.map((u, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-1">{u.jm_email}</td>
                              <td className="p-1">{u.ats_match || '-'}</td>
                              <td className="p-1">
                                <Badge variant={u.action === 'mapped' ? 'default' : 'secondary'}>
                                  {u.action}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Awaiting Response Submissions */}
                {dryRunResult.awaiting_response_submissions?.length > 0 && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-600" />
                      Submissions Awaiting Client Response ({dryRunResult.awaiting_response_submissions.length})
                    </h4>
                    <p className="text-sm text-amber-700 mb-2">
                      These were still awaiting client response in JM. Review and manually recreate as needed.
                    </p>
                    <div className="max-h-32 overflow-y-auto text-sm">
                      {dryRunResult.awaiting_response_submissions.slice(0, 5).map((sub, i) => (
                        <div key={i} className="py-1 border-b border-amber-100 last:border-0">
                          {sub.subject} → {sub.client_email}
                        </div>
                      ))}
                      {dryRunResult.awaiting_response_submissions.length > 5 && (
                        <div className="text-amber-500 mt-1">
                          ...and {dryRunResult.awaiting_response_submissions.length - 5} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3 mt-4">
                  <Button variant="outline" onClick={resetImport}>Start Over</Button>
                  <Button onClick={handleImport} disabled={isLoading} className="bg-green-600 hover:bg-green-700">
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Execute Import
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 4: Import Complete */}
            {importResult && step === 'complete' && (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {importResult.success ? (
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-6 w-6 text-amber-600" />
                    )}
                    <span className="text-lg font-medium">
                      {importResult.success ? 'Import Completed Successfully!' : 'Import Completed with Errors'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-white rounded">
                      <div className="text-2xl font-bold text-green-600">
                        {Object.values(importResult.created || {}).reduce((a, b) => a + b, 0)}
                      </div>
                      <div className="text-sm text-slate-600">Created</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded">
                      <div className="text-2xl font-bold text-amber-600">
                        {Object.values(importResult.merged || {}).reduce((a, b) => a + b, 0)}
                      </div>
                      <div className="text-sm text-slate-600">Merged</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded">
                      <div className="text-2xl font-bold text-slate-500">
                        {Object.values(importResult.skipped || {}).reduce((a, b) => a + b, 0)}
                      </div>
                      <div className="text-sm text-slate-600">Skipped</div>
                    </div>
                  </div>
                  
                  {/* Detailed breakdown */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm mb-4">
                    {Object.keys(importResult.created || {}).map(entity => (
                      <div key={entity} className="flex justify-between bg-white p-2 rounded">
                        <span className="capitalize">{entity.replace('_', ' ')}</span>
                        <span className="text-green-600">+{importResult.created[entity]}</span>
                      </div>
                    ))}
                  </div>
                  
                  {importResult.errors?.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 rounded">
                      <p className="text-sm font-medium text-red-700 mb-1">Errors ({importResult.errors.length}):</p>
                      <ul className="list-disc list-inside text-sm text-red-600 max-h-32 overflow-y-auto">
                        {importResult.errors.slice(0, 10).map((err, i) => <li key={i}>{err}</li>)}
                        {importResult.errors.length > 10 && <li>...and {importResult.errors.length - 10} more</li>}
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <Button variant="outline" onClick={resetImport}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Import Another
                  </Button>
                  <Button onClick={handleDownloadReport}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Report CSV
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Safety Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-4 w-4" />
              Safety & Idempotency
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-600 space-y-2">
            <p>• <strong>Idempotent:</strong> Re-importing the same ZIP creates zero duplicates (matched by legacy_id)</p>
            <p>• <strong>Merge rules:</strong> Existing clients matched by GSTIN or name get empty fields filled only - never overwritten</p>
            <p>• <strong>No emails:</strong> Import does NOT trigger any outbound emails, reminders, or notifications</p>
            <p>• <strong>Open deals:</strong> Imported open deals have reminders PAUSED until manually resumed</p>
            <p>• <strong>Audit trail:</strong> Every import run is logged with user, timestamp, and results</p>
          </CardContent>
        </Card>
      </div>
    );
  };


  const renderSection = () => {
    switch (activeSection) {
      // Sections WITHOUT hooks - call as functions to prevent scroll-to-top
      case 'ai': return AISection();
      case 'ai_job_search': return AIJobSearchSection();
      case 'email': return EmailSection();
      case 'mailboxes': return <MailboxesPanel />;
      case 'signature': return <SignatureSettings />;
      case 'authentication': return AuthenticationSection();
      case 'system_users': return SystemUsersSection();
      case 'permissions': return PermissionsSection();
      case 'integrations': return IntegrationsSection();
      case 'page_settings': return PageSettingsSection();
      case 'page_analytics': return <PageAnalyticsSection />;
      case 'danger': return DangerSection();
      case 'logs': return LogsSection();
      case 'hr_retargeting': return HRRetargetingSection();
      case 'deal_reminders': return <DealRemindersSection />;
      case 'profile_signature': return <ProfileSignatureSection />;
      case 'recruiting_discipline': return <RecruitingDisciplineSection />;
      case 'documentation': return DocumentationSection();
      // Sections WITH hooks - render as components to isolate hook scope
      case 'api_usage': return <APIUsageSection />;
      case 'system': return <SystemSection />;
      case 'scheduled_tasks': return <ScheduledTasksSection />;
      case 'ai_parsing': return <AIParsingSection />;
      case 'email_templates': return <EmailTemplatesSection />;
      case 'notification_center': return <NotificationCenterSection />;
      case 'webhook': return <WebhookSection />;
      case 'verification': return <VerificationSection />;
      case 'productivity': return <ProductivitySection />;
      case 'engagement': return <EngagementSection />;
      case 'jm_import': return <JMImportSection />;
      default: return AISection();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500">Manage your application configuration</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - Desktop with Categories */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <nav className="sticky top-8 space-y-2 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
              {SETTINGS_CATEGORIES.map((category) => {
                const CategoryIcon = category.icon;
                const hasSectionInCategory = category.sections.some(s => s.id === activeSection);
                return (
                  <div key={category.id} className="mb-2">
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-1 ${
                      hasSectionInCategory ? 'bg-blue-50' : 'bg-slate-100'
                    }`}>
                      <CategoryIcon className={`w-4 h-4 ${hasSectionInCategory ? 'text-blue-600' : 'text-slate-500'}`} />
                      <span className={`text-sm font-semibold ${hasSectionInCategory ? 'text-blue-700' : 'text-slate-600'}`}>
                        {category.label}
                      </span>
                    </div>
                    <div className="ml-2 space-y-0.5">
                      {category.sections.map((section) => {
                        const Icon = section.icon;
                        const isActive = activeSection === section.id;
                        return (
                          <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all text-sm ${
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'hover:bg-slate-100 text-slate-600'
                            }`}
                            data-testid={`settings-nav-${section.id}`}
                          >
                            <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                            <span className="truncate">{section.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </nav>
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden">
            <Select value={activeSection} onValueChange={setActiveSection}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SETTINGS_CATEGORIES.map((category) => (
                  <div key={category.id}>
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">
                      {category.label}
                    </div>
                    {category.sections.map((section) => (
                      <SelectItem key={section.id} value={section.id}>
                        {section.label}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Card>
              <CardContent className="p-6">
                {renderSection()}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
